import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import { useAuth } from "../contexts/AuthContext";
import LivretExplication from "./LivretExplication";

// ─── STYLES ───────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700&family=DM+Serif+Display:ital@0;1&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

  html, body, #root {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    background: #08090c;
    overflow-y: auto;        /* ← Changé de hidden à auto */
    overflow-x: hidden;      /* ← Évite le scroll horizontal */
    color: #e0e0e3;
}

  *, *::before, *::after {
    box-sizing: border-box;
  }

  /* ====================== RENDU POLICES ====================== */
  body {
    font-family: 'Syne', Apple Color Emoji, Segoe UI Emoji, Twemoji Mozilla, sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 1.68;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    font-feature-settings: "kern" 1, "liga" 1;
  }

  /* ====================== TYPOGRAPHIE ====================== */
  strong, b, h1, h2, h3, h4, .uppercase, .bold {
    font-weight: 700;
    color: #f0f2f8;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    line-height: 1.3;
    text-shadow: 0 2px 6px rgba(0,0,0,0.6);
  }

  .narrative-text, .serif-text {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: 15.8px;
    line-height: 1.82;
    letter-spacing: 0.02em;
  }

  .mono-text {
    font-family: 'JetBrains Mono', monospace;
  }

  /* ====================== CONTRASTE & NETTETÉ ====================== */
  p, span, div {
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
  }

  @keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

  /* ====================== MOBILE & TOUCH ====================== */

  /* iOS Safari - évite le débordement et le zoom */
  input, button, select, textarea {
    font-size: 16px;                    /* Empêche le zoom auto sur iOS */
  }

  * {
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  }

  /* Scroll fluide sur mobile */
  .hand-scroll-zone,
  .sidebar-scroll-zone {
    -webkit-overflow-scrolling: touch;
  }

  /* Taille minimale des cibles tactiles */
  .touch-target {
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ====================== MEDIA QUERIES MOBILE ====================== */
  @media (max-width: 600px) {
    .arena-grid {
      grid-template-columns: 1fr !important;
    }

    .cinematic-grid {
      grid-template-columns: 1fr !important;
      grid-template-rows: auto 1fr;
    }

    .cinematic-right {
      padding: 16px !important;
    }

    .cinematic-left {
      padding: 20px 16px 12px !important;
      border-right: none !important;
      border-bottom: 1px solid rgba(255,255,255,0.06) !important;
      flex-direction: row !important;
      gap: 14px;
      align-items: center;
      justify-content: flex-start;
    }

    @media (max-width: 600px) {
    /* Permettre le scroll dans les zones d'information */
    .game-info-panel,
    .heritage-panel,
    .stats-panel {
        max-height: 40vh;           /* Limite la hauteur */
        overflow-y: auto;           /* Active le scroll */
        -webkit-overflow-scrolling: touch;
    }

    /* Réduire la taille des éléments */
    .game-arena {
        gap: 8px;                   /* Moins d'espace */
    }

    .card, .panel {
        padding: 10px;              /* Padding réduit */
        font-size: 14px;            /* Texte plus petit */
    }
  }

  /* ====================== SCROLLBAR ====================== */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255,255,255,0.04);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.18);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255,255,255,0.28);
  }
`;

// ─── BEHAVIOR COLORS ──────────────────────────────────────────
const BC = {
  confiance:"#4ade80",solidarite:"#60a5fa",memoire:"#a78bfa",
  innovation:"#22d3ee",valeur:"#fbbf24",production:"#86efac",
  monnaie:"#fb923c",cupidite:"#f87171",pouvoir:"#e879f9",
  echange:"#2dd4bf",travail:"#94a3b8",loyaute:"#4ade80",
  inegalite:"#f97316",tension:"#ef4444",decision:"#818cf8",
  prudence:"#6ee7b7",compromis:"#d4a017",justice:"#38bdf8",
  risque:"#f59e0b",regulation:"#60a5fa",injustice:"#fc8181",
  cooperation:"#22d3ee",domination:"#dc2626",hierarchie:"#9ca3af",
  extraction:"#b45309",representation:"#7dd3fc",culture:"#e879f9",
};
const DARK_BEHAVIORS = new Set(["cupidite","pouvoir","inegalite","tension","injustice","risque","domination","hierarchie","extraction"]);
const RARITY_COLORS = { commun:"#94a3b8", rare:"#60a5fa", légendaire:"#fbbf24" };

const BOSS_BASE_ATK = 4;
const PLAYER_HP_CAP = 40;
const INITIAL_MAX_MANA = 4;

// ─── HERITAGE (DOMINO SYSTEM) ─────────────────────────────────
const HERITAGE_RULES = [
  { from:1,to:2,condition:s=>s.confiance>=2,
    light:{id:"reseau_confiance",name:"Réseau de confiance",icon:"🌱",color:"#4ade80",type:"bonus",desc:"Le clan a tissé des liens. La Famille Abeille hérite d'un réseau commercial naissant.",effect:"+1 Mana de base · Cartes 'échange' coûtent 1 de moins",manaBonus:1},
    dark:{id:"mefiance_heritee",name:"Méfiance héritée",icon:"🔒",color:"#f87171",type:"malus",desc:"Le clan s'est replié. La Famille Abeille commence méfiante.",effect:"-1 DEF à tous les alliés au départ",defPenalty:1}},
  { from:1,to:2,condition:s=>s.darkPlayed>=2,
    always_dark:{id:"violence_fondatrice",name:"Violence fondatrice",icon:"⚔️",color:"#dc2626",type:"systemic",forced:true,desc:"La domination dès l'origine sera reproduite. Le boss gagne +6 HP.",effect:"Boss +6 HP · Carte 'Domination héritée' dans votre main",bossHPExtra:6,imposedCard:"domination_imposee"}},
  { from:2,to:3,condition:s=>s.combosUsed>=2,
    light:{id:"economie_vivante",name:"Économie vivante",icon:"🏛️",color:"#fbbf24",type:"bonus",desc:"Les échanges ont prospéré. Le Moyen Âge hérite d'un marché développé.",effect:"+2 cartes en main au départ",extraStartCards:2},
    dark:{id:"concentration_richesse",name:"Concentration précoce",icon:"💰",color:"#f97316",type:"malus",desc:"La richesse s'est concentrée. Le Seigneur commence avec +4 HP.",effect:"Boss +4 HP",bossHPExtra:4}},
  { from:3,to:4,condition:s=>s.justice>=2,
    light:{id:"droit_embryonnaire",name:"Droit embryonnaire",icon:"⚖️",color:"#38bdf8",type:"bonus",desc:"Des règles collectives ont été posées. Le mouvement ouvrier a un cadre légal minimal.",effect:"Immunité 2 tours au début · Cartes 'justice' coûtent 1 de moins",startImmunityTurns:2},
    dark:{id:"servitude_normalisee",name:"Servitude normalisée",icon:"🔗",color:"#6b7280",type:"malus",desc:"L'exploitation est devenue invisible, donc acceptée.",effect:"Cartes 'travail' ont -2 ATK",behaviorATKPenalty:{travail:-2}}},
  { from:4,to:5,condition:s=>s.syndicatPlayed,
    light:{id:"culture_syndicale",name:"Culture syndicale",icon:"✊",color:"#60a5fa",type:"bonus",desc:"La solidarité ouvrière a laissé une trace durable.",effect:"+3 DEF collective · Combos 'solidarité' donnent +2 ATK",collectiveDEFBonus:3},
    dark:{id:"atomisation_sociale",name:"Atomisation sociale",icon:"🔇",color:"#374151",type:"malus",desc:"Le collectif n'a jamais émergé. Chacun pour soi.",effect:"Combos 'solidarité' bloqués 3 tours · Boss +2 ATK de départ",bossStartATKBonus:2}},
  { from:5,to:6,condition:s=>s.regulationPlayed>=1,
    light:{id:"cadre_regulatoire",name:"Cadre régulatoire",icon:"📋",color:"#38bdf8",type:"bonus",desc:"Des lois ont été posées. Le Milliardaire ne peut pas tout se permettre.",effect:"Immunité boss réduite de 2 · Carte 'justice' offerte",immunityReduction:2},
    dark:{id:"deregulation_totale",name:"Dérégulation totale",icon:"🏦",color:"#f97316",type:"malus",desc:"Les marchés ont été laissés libres. Le capital a capturé l'État.",effect:"Boss double immunité · Cartes coûtent +1 pendant 4 tours",cardCostExtra:4}},
  { from:6,to:7,condition:s=>s.coopPlayed>=1,
    light:{id:"modele_alternatif",name:"Modèle alternatif prouvé",icon:"🌿",color:"#4ade80",type:"bonus",desc:"Des alternatives économiques existent. La Famille Cigogne n'est pas seule.",effect:"+4 ATK/DEF combo coopération · Tous combos -1 mana",coopComboBonus:4},
    dark:{id:"fatigue_systemique",name:"Fatigue systémique",icon:"💫",color:"#6b7280",type:"malus",desc:"Trop de défaites accumulées. L'Effondrement commence renforcé.",effect:"Boss +10 HP · Cartes 'confiance' -1 ATK",bossHPExtra:10,behaviorATKPenalty:{confiance:-1}}},
];

function computeHeritage(phaseId, stats) {
  const rules = HERITAGE_RULES.filter(r => r.from === phaseId);
  const result = [];
  for (const rule of rules) {
    if (rule.always_dark) {
      if (rule.condition(stats)) result.push({ ...rule.always_dark });
    } else if (rule.condition(stats)) {
      const dark = (stats.darkPlayed || 0) > (stats.lightPlayed || 0);
      result.push(dark ? { ...rule.dark } : { ...rule.light });
    }
  }
  return result;
}

// ─── IMPOSED CARDS ────────────────────────────────────────────
const IMPOSED_CARDS = {
  domination_imposee:{id:"domination_imposee",name:"Domination héritée",role:"Héritage involontaire",emoji:"⛓️",color:"#dc2626",behavior:"domination",atk:4,def:2,cost:0,effectDesc:"Tu ne l'as pas choisie. Elle est là quand même.",lesson:"80% de tes comportements économiques sont appris avant 12 ans. Ce n'est pas une fatalité — c'est un point de départ.",imposed:true,imposedNote:"Cette carte t'a été imposée par tes choix précédents. Tu peux la jouer ou résister (coût +2 mana)."},
  hierarchie_naturelle:{id:"hierarchie_naturelle",name:"Hiérarchie naturelle",role:"Idéologie reçue",emoji:"🏛️",color:"#9ca3af",behavior:"hierarchie",atk:3,def:4,cost:0,effectDesc:"Certains commandent. C'est 'naturel'. (C'est une construction.)",lesson:"L'idée que la hiérarchie est naturelle est une idée — pas un fait. Les fourmis ont des hiérarchies, les bonobos des structures égalitaires.",imposed:true,imposedNote:"Cette carte est dans ta main parce que le système te la met."},
  flexibilite_subie:{id:"flexibilite_subie",name:"La flexibilité c'est la liberté",role:"Novlangue",emoji:"🛵",color:"#f59e0b",behavior:"inegalite",atk:3,def:1,cost:0,effectDesc:"Libre de travailler 60h sans garantie.",lesson:"'Flexibilité du marché du travail' signifie que l'employeur peut licencier plus facilement. Quand on te demande d'être flexible, demande : dans quel sens ?",imposed:true,imposedNote:"Cette rhétorique est répétée jusqu'à paraître naturelle."},
  dette_normalisee:{id:"dette_normalisee",name:"La dette c'est normal",role:"Croyance systémique",emoji:"📑",color:"#6060a0",behavior:"inegalite",atk:2,def:3,cost:0,effectDesc:"S'endetter pour vivre. Tout le monde fait ça.",lesson:"En France, 48% des ménages ont un crédit conso. En Allemagne, 24%. La différence n'est pas dans la nature humaine.",imposed:true,imposedNote:"Ressources immédiates vs coût long terme."},
};

// ─── PARALLEL WORLDS ──────────────────────────────────────────
const PARALLEL_WORLDS = {
  1:{
    id:"clan_dominant",name:"Le Clan Dominant",subtitle:"Et si la Famille Ours avait choisi la domination ?",
    color:"#dc2626",bossIcon:"😔",
    revelation:"Dans ce monde, le clan Ours a imposé sa loi aux autres. Ils ont survécu. Mais quelque chose s'est brisé — la réciprocité. 10 000 ans plus tard, vous en payez encore le prix.",
    mirror:"Dans ce monde, VOUS êtes le problème. La violence symbolique vient de vous.",
    enigma:{
      situation:"Le clan dominant a tout le gibier. Les autres clans ont faim. Le chef Ours propose un échange : nourriture contre soumission. Un vieux membre du clan refuse et propose autre chose.",
      question:"Qu'est-ce que le vieux membre propose pour sortir de ce piège sans violence ni soumission ?",
      options:[
        {text:"Fuir vers un autre territoire — l'éloignement brise la dépendance.",correct:false,feedback:"Fuir résout le problème immédiat, mais laisse le système de domination intact pour les autres."},
        {text:"Créer un surplus partagé entre plusieurs clans — personne ne dépend d'un seul.",correct:true,feedback:"Exact. La mutualisation des ressources est le premier mécanisme anti-monopole de l'histoire. La solidarité inter-clans brise la dépendance unilatérale."},
        {text:"Voler du gibier la nuit — la survie justifie tout.",correct:false,feedback:"Le vol crée une spirale de méfiance. Il reproduit la logique du plus fort, pas une alternative."},
      ],
      unlock:{
        id:"memoire_collective",name:"Mémoire Collective",emoji:"🧠",color:"#a78bfa",
        role:"Carte Parallèle",behavior:"memoire",atk:2,def:4,cost:0,
        effectDesc:"+6 PV · Annule 1 carte ennemie",rarity:"parallèle",
        lesson:"La mémoire partagée des injustices passées est le fondement de toute résistance durable.",
        lore:"Ce clan a failli disparaître. Il n'a pas oublié. Et cette mémoire est devenue leur armure.",
        quote:"« Ce dont on se souvient ensemble, on ne peut pas le faire subir à nouveau. »",
        isParallel:true,
      }
    }
  },
  2:{
    id:"monopole_or",name:"Le Monopole de l'Or",subtitle:"Et si l'Orfèvre avait gagné dès le début ?",
    color:"#b45309",bossIcon:"👥",
    revelation:"L'Orfèvre contrôle la monnaie depuis 3000 ans. Ce n'est pas un hasard — c'est un système. Chaque crise financière depuis lors reproduit ce moment fondateur.",
    mirror:"Tu joues le banquier central. Reconnais-tu quelqu'un ?",
    enigma:{
      situation:"L'Orfèvre détient tout l'or. Il prête avec intérêts. Tout le monde s'endette, personne ne peut rembourser — car il n'y a pas assez d'or en circulation pour payer les intérêts. Les marchands réalisent le problème.",
      question:"Pourquoi est-il mathématiquement impossible de rembourser les intérêts dans ce système ?",
      options:[
        {text:"Parce que les marchands dépensent trop et ne font pas d'économies.",correct:false,feedback:"Non. C'est ce que l'Orfèvre leur dit pour masquer le vrai problème structurel."},
        {text:"Parce que l'or se dégrade avec le temps et perd de la valeur.",correct:false,feedback:"L'or est justement choisi parce qu'il ne se dégrade pas. C'est un faux problème."},
        {text:"Parce que les intérêts exigent plus d'or qu'il n'en a été créé — la dette totale dépasse toujours la monnaie existante.",correct:true,feedback:"Exactement. Si 100 pièces existent et que l'Orfèvre prête à 10% d'intérêt, il exige 110 pièces au remboursement. Les 10 pièces supplémentaires n'existent nulle part. C'est la logique de toute dette à intérêt."},
      ],
      unlock:{
        id:"denier_libre",name:"Le Denier Libre",emoji:"🪙",color:"#f0a030",
        role:"Carte Parallèle",behavior:"monnaie",atk:3,def:3,cost:0,
        effectDesc:"-2 Mana ennemi · +2 ATK alliés monnaie",rarity:"parallèle",
        lesson:"Une monnaie émise collectivement, sans intérêt, change radicalement les rapports de pouvoir.",
        lore:"Dans ce monde alternatif, les marchands ont créé leur propre étalon. L'Orfèvre a perdu son monopole.",
        quote:"« Celui qui crée la monnaie ne devrait pas être le seul à en bénéficier. »",
        isParallel:true,
      }
    }
  },
  3:{
    id:"servitude_eternelle",name:"La Servitude éternelle",subtitle:"Et si le Seigneur n'avait jamais été vaincu ?",
    color:"#78350f",bossIcon:"😶",
    revelation:"Dans certains pays, le système féodal n'a jamais vraiment disparu. Il s'est rebrandé — propriétaire foncier, actionnaire majoritaire, PDG héréditaire.",
    mirror:"La résignation est le mécanisme le plus efficace du pouvoir.",
    enigma:{
      situation:"Dans ce monde parallèle, les serfs paient la dîme depuis 800 ans. Une génération entière n'a jamais vu ce que ça fait de garder le fruit de son travail. Un jeune marchand arrive avec une idée pour contourner le système.",
      question:"Quel mécanisme le marchand propose-t-il pour sortir les serfs de la dépendance sans affronter directement le Seigneur ?",
      options:[
        {text:"Une pétition au roi — le pouvoir supérieur peut contraindre le Seigneur.",correct:false,feedback:"Le roi et le Seigneur font partie du même système. Demander protection à l'un contre l'autre ne brise pas la structure féodale."},
        {text:"Créer des guildes de métier indépendantes — la spécialisation crée une valeur que le Seigneur ne peut pas taxer facilement.",correct:true,feedback:"Précis. Les guildes médiévales ont historiquement constitué le premier espace économique autonome hors de la logique féodale. La compétence technique échappe à la rente foncière."},
        {text:"Incendier les registres de dette — sans preuve, la dîme disparaît.",correct:false,feedback:"La violence directe sans alternative économique mène à une répression puis à un retour du même système, souvent en pire."},
      ],
      unlock:{
        id:"sceau_des_guildes",name:"Sceau des Guildes",emoji:"⚙️",color:"#d4b483",
        role:"Carte Parallèle",behavior:"production",atk:3,def:5,cost:0,
        effectDesc:"+4 DEF · Cartes production coûtent -1 Mana ce tour",rarity:"parallèle",
        lesson:"Les guildes médiévales sont le premier exemple d'organisation économique collective autonome.",
        lore:"Ce sceau représente l'accord entre sept guildes. Aucun Seigneur ne put les taxer séparément.",
        quote:"« La compétence partagée vaut mieux que la terre possédée. »",
        isParallel:true,
      }
    }
  },
  4:{
    id:"capital_sans_travail",name:"Le Capital sans Travail",subtitle:"Et si les travailleurs n'avaient jamais résisté ?",
    color:"#1e3a5f",bossIcon:"⛓️",
    revelation:"Aucun syndicat, aucun code du travail, aucune retraite. C'est le monde que certains veulent restaurer en l'appelant 'liberté'.",
    mirror:"Quand on dit 'flexibilité du marché du travail', qui est flexible ?",
    enigma:{
      situation:"Dans cette usine parallèle, les ouvriers travaillent 14h/jour, 7j/7. Un enfant de 9 ans vient de mourir d'épuisement. Le patron dit : 'Personne ne vous oblige à travailler ici.' Un délégué veut répondre.",
      question:"Quel argument économique — pas moral — démontre que 'personne ne vous oblige' est faux ?",
      options:[
        {text:"Parce que les ouvriers aiment leur travail et n'auraient pas choisi de partir de toute façon.",correct:false,feedback:"Ce n'est pas un argument économique, et il valide implicitement la position du patron."},
        {text:"Parce que quand toutes les alternatives ont été détruites (terres clôturées, artisanat écrasé), le 'choix' de travailler n'est plus un choix — c'est une contrainte structurelle.",correct:true,feedback:"Exactement. L'économiste Karl Polanyi appelle ça la 'grande transformation' : transformer des êtres humains en 'main d'œuvre' nécessite de détruire préalablement leurs moyens d'existence autonomes. Le marché du travail n'est libre que si on peut ne pas y entrer."},
        {text:"Parce que le patron gagne plus que les ouvriers, ce qui prouve l'exploitation.",correct:false,feedback:"L'inégalité salariale est réelle, mais elle ne prouve pas l'absence de choix en tant que telle. L'argument structurel est plus solide."},
      ],
      unlock:{
        id:"charte_ouvriere",name:"Charte Ouvrière",emoji:"✊",color:"#60a5fa",
        role:"Carte Parallèle",behavior:"solidarite",atk:4,def:4,cost:0,
        effectDesc:"+3 ATK à tous les alliés · Boss -2 ATK ce tour",rarity:"parallèle",
        lesson:"Les droits du travail ne sont pas des cadeaux — ce sont des conquêtes arrachées par des gens qui ont payé de leur vie.",
        lore:"Cette charte n'existe pas dans notre monde. Elle aurait pu éviter 50 ans de misère industrielle.",
        quote:"« La liberté sans filet n'est de la liberté que pour ceux qui ne tombent jamais. »",
        isParallel:true,
      }
    }
  },
  5:{
    id:"surveillance_totale",name:"La Surveillance Totale",subtitle:"Et si l'algorithme avait tout optimisé ?",
    color:"#0f3460",bossIcon:"🤖",
    revelation:"Le score social existe. La notation comportementale existe. L'optimisation algorithmique des vies humaines est un projet en cours, pas une dystopie future.",
    mirror:"Ta vie numérique produit 400 décisions par jour que tu ne prends pas toi-même.",
    enigma:{
      situation:"Dans ce monde, chaque citoyen a un score de 0 à 1000 basé sur ses achats, déplacements et fréquentations. En dessous de 600 : pas de prêt, pas de transport, pas d'emploi public. Un ingénieur du système réalise une faille fondamentale.",
      question:"Quelle est la faille économique centrale d'un système de score comportemental généralisé ?",
      options:[
        {text:"Il est trop coûteux à maintenir — les serveurs consomment trop d'énergie.",correct:false,feedback:"Le coût technique est réel mais secondaire. Ce n'est pas la faille qui remet en cause le système lui-même."},
        {text:"Il optimise les comportements passés, pas futurs — il détruit l'innovation en pénalisant tout ce qui sort de la norme mesurée.",correct:true,feedback:"Exactement. Goodhart's Law : quand une mesure devient un objectif, elle cesse d'être une bonne mesure. Un système qui récompense la conformité tue la créativité, l'entrepreneuriat et la déviance positive — qui sont précisément les moteurs de l'économie."},
        {text:"Il favorise les riches qui peuvent se payer de bons scores.",correct:false,feedback:"C'est une injustice réelle, mais pas la faille systémique centrale. Un système égalitaire de score aurait les mêmes problèmes d'optimisation."},
      ],
      unlock:{
        id:"code_ouvert",name:"Code Ouvert",emoji:"💻",color:"#38bdf8",
        role:"Carte Parallèle",behavior:"innovation",atk:5,def:2,cost:0,
        effectDesc:"Révèle la prochaine carte ennemie · +3 ATK si innovation en jeu",rarity:"parallèle",
        lesson:"La transparence algorithmique est la condition minimale d'une démocratie numérique.",
        lore:"Dans ce monde alternatif, le code source de chaque algorithme public est accessible à tous.",
        quote:"« Ce qu'on ne peut pas auditer, on ne peut pas lui faire confiance. »",
        isParallel:true,
      }
    }
  },
  6:{
    id:"philanthropy_sauvage",name:"La Philanthropie Sauvage",subtitle:"Et si les milliardaires décidaient de tout ?",
    color:"#7c2d12",bossIcon:"🙏",
    revelation:"Bill Gates décide des politiques de santé mondiales. Ce n'est pas de la philanthropie — c'est de la gouvernance privée.",
    mirror:"Qui a choisi ces priorités ? Toi ? Ou celui qui a l'argent ?",
    enigma:{
      situation:"Dans ce monde, la fondation du Milliardaire finance 40% de la recherche médicale mondiale. Il décide de prioriser les maladies rentables. Une épidémie frappe un pays pauvre — aucun vaccin rentable n'existe. Une économiste de l'OMS demande la parole.",
      question:"Quel mécanisme économique explique pourquoi la philanthropie privée ne peut structurellement pas remplacer la santé publique ?",
      options:[
        {text:"Les milliardaires sont corrompus et ne donnent que pour leur image.",correct:false,feedback:"La critique morale peut être vraie, mais elle n'explique pas la défaillance structurelle — même un philanthrope de bonne foi reproduirait le même problème."},
        {text:"La philanthropie ne peut financer que ce qui correspond aux préférences d'une personne — or la santé publique exige de financer les besoins collectifs indépendamment de leur rentabilité ou de l'intérêt d'un donateur.",correct:true,feedback:"Précis. C'est ce que les économistes appellent un 'bien public' : non-exclusif, non-rival, impossible à financer efficacement par le marché car personne ne peut en être exclu. La vaccination de masse en est l'exemple parfait — son bénéfice dépasse toujours son coût privé, donc le marché sous-investit structurellement."},
        {text:"Parce que les fondations paient trop de frais de gestion.",correct:false,feedback:"Les frais sont un problème réel de gouvernance, mais pas la défaillance fondamentale du modèle."},
      ],
      unlock:{
        id:"bien_commun",name:"Le Bien Commun",emoji:"🌐",color:"#67e8f9",
        role:"Carte Parallèle",behavior:"justice",atk:3,def:5,cost:0,
        effectDesc:"+5 PV · Boss perd 2 HP · Immunité 1 tour",rarity:"parallèle",
        lesson:"Certains biens ne peuvent être gérés que collectivement — leur valeur réside précisément dans leur universalité.",
        lore:"Cette carte représente ce qui n'a jamais eu de prix et ne devrait jamais en avoir.",
        quote:"« Ce qui appartient à tous ne peut appartenir à personne. »",
        isParallel:true,
      }
    }
  },
  7:{
    id:"effondrement_accepte",name:"L'Effondrement Accepté",subtitle:"Et si on avait décidé que c'était inévitable ?",
    color:"#4b5563",bossIcon:"🕳️",
    revelation:"La résignation est une décision. Choisir de ne pas changer le système est un choix politique. L'inaction a des auteurs.",
    mirror:"Tu peux encore choisir de ne pas jouer cette version.",
    enigma:{
      situation:"Dans ce monde, les économistes ont prouvé en 2031 que la transition écologique coûterait 4% du PIB mondial par an pendant 20 ans. Les gouvernements ont choisi de ne pas agir. Un rapport confidentiel circule maintenant : le coût de l'inaction est estimé à 23% du PIB par an à partir de 2045.",
      question:"Quel concept économique fondamental les décideurs ont-ils ignoré en choisissant l'inaction ?",
      options:[
        {text:"L'externalité négative — les coûts réels de l'inaction ne sont pas supportés par ceux qui décident de ne pas agir.",correct:false,feedback:"L'externalité est réelle et importante, mais ce n'est pas le concept le plus fondamental ici — il y en a un qui explique précisément pourquoi 4% maintenant vaut bien plus que 23% plus tard."},
        {text:"La valeur temporelle de l'argent inversée — investir 4% maintenant pour éviter 23% plus tard est mathématiquement rationnel, mais les cycles politiques courts rendent cet horizon invisible aux décideurs.",correct:true,feedback:"Exactement. C'est ce que l'économiste Nicholas Stern appelle 'l'échec du marché le plus grave de l'histoire' : le taux d'actualisation utilisé par les marchés rend le futur lointain presque sans valeur dans les décisions présentes. Une perte dans 20 ans 'vaut' beaucoup moins qu'une dépense aujourd'hui — même si elle est 6 fois plus grande."},
        {text:"La loi des rendements décroissants — investir plus n'aurait pas changé le résultat.",correct:false,feedback:"Les rendements décroissants s'appliquent à la production marginale, pas aux investissements de prévention d'une catastrophe systémique."},
      ],
      unlock:{
        id:"horizon_long",name:"Horizon Long",emoji:"🌅",color:"#a78bfa",
        role:"Carte Parallèle",behavior:"memoire",atk:4,def:4,cost:0,
        effectDesc:"+2 ATK · +2 DEF · Héritage +1 pour phase suivante",rarity:"parallèle",
        lesson:"Penser à long terme n'est pas naturel — c'est une compétence économique et politique qui s'apprend.",
        lore:"Cette carte n'existe que si quelqu'un a eu le courage de regarder au-delà du prochain trimestre.",
        quote:"« Les arbres sous l'ombre desquels nous nous asseyons ont été plantés par des gens qui n'ont jamais vu leur ombre. »",
        isParallel:true,
      }
    }
  },
};

// ─── STRATEGIES ───────────────────────────────────────────────
const STRATEGIES = {
  1:[
    {id:"cooperation",name:"Tisser les liens",color:"#4ade80",icon:"🌱",desc:"Construire la confiance avant tout. Lent mais durable.",effect:"Cartes confiance/solidarité gratuites 3 tours.",profile:"harmonie",bonus:{type:"freeCoop",turns:3}},
    {id:"innovation",name:"Inventer d'abord",color:"#22d3ee",icon:"⚙️",desc:"Innover en premier, les autres suivront.",effect:"+1 ATK permanent aux cartes 'innovation'.",profile:"equilibre",bonus:{type:"atkBehavior",behavior:"innovation",value:1}},
    {id:"survie",name:"Survivre d'abord",color:"#f97316",icon:"🔥",desc:"Le contexte est hostile. Protège-toi.",effect:"+5 HP de départ. Une carte 'Domination héritée' s'impose.",profile:"domination",bonus:{type:"survival",hp:5,imposedCard:"domination_imposee"}},
  ],
  2:[
    {id:"redistribution",name:"Répartir la richesse",color:"#4ade80",icon:"🤝",desc:"L'abondance partagée dure plus longtemps.",effect:"Chaque carte jouée soigne +1 PV à un allié.",profile:"harmonie",bonus:{type:"healOnPlay",value:1}},
    {id:"accumulation",name:"Accumuler d'abord",color:"#fbbf24",icon:"💰",desc:"Le capital permet tout.",effect:"+2 Mana de base. Carte 'dette normalisée' imposée.",profile:"equilibre",bonus:{type:"manaPlus",value:2,imposedCard:"dette_normalisee",mana:2}},
    {id:"controle",name:"Contrôler l'étalon",color:"#e879f9",icon:"⚖️",desc:"Qui frappe la monnaie contrôle tout.",effect:"Cartes 'monnaie' +3 ATK.",profile:"domination",bonus:{type:"monnaieBoost",value:3}},
  ],
  3:[
    {id:"commerce",name:"Contourner le Seigneur",color:"#2dd4bf",icon:"🧳",desc:"Les routes libres créent les villes libres.",effect:"+1 carte échange offerte.",profile:"harmonie",bonus:{type:"commerceFree",turns:2}},
    {id:"alliance",name:"Négocier avec le pouvoir",color:"#d4a017",icon:"🤝",desc:"Pas toujours juste, mais souvent efficace.",effect:"Boss -30% HP initial.",profile:"equilibre",bonus:{type:"bossReduce",pct:0.3}},
    {id:"revolte",name:"Renverser la dîme",color:"#ef4444",icon:"✊",desc:"Justice ou rien.",effect:"Boss -5 HP. Carte 'hiérarchie naturelle' imposée.",profile:"domination",bonus:{type:"revolt",bossHPMinus:5,imposedCard:"hierarchie_naturelle"}},
  ],
  4:[
    {id:"collectif",name:"Organiser la résistance",color:"#60a5fa",icon:"✊",desc:"La force du nombre contre la force du capital.",effect:"Chaque allié travail/solidarité donne +1 ATK à tous.",profile:"harmonie",bonus:{type:"collectiveATK"}},
    {id:"reforme",name:"Changer de l'intérieur",color:"#38bdf8",icon:"📋",desc:"Lent, frustrant, mais les réformes tiennent.",effect:"Cartes justice/regulation +2 DEF. Boss -2 ATK.",profile:"equilibre",bonus:{type:"reformBonus",bossATKMinus:2}},
    {id:"exil",name:"Partir pour comprendre",color:"#a78bfa",icon:"🧳",desc:"Fuir le système pour le voir de l'extérieur.",effect:"+3 cartes en main. Carte 'flexibilité subie' imposée.",profile:"lucidite",bonus:{type:"exile",extraCards:3,imposedCard:"flexibilite_subie"}},
  ],
  5:[
    {id:"transparence",name:"Rendre l'invisible visible",color:"#a78bfa",icon:"💻",desc:"Les algorithmes cachés doivent être exposés.",effect:"Cartes 'innovation' révèlent le prochain effet du boss.",profile:"lucidite",bonus:{type:"reveal"}},
    {id:"regulation",name:"Réguler la tech",color:"#38bdf8",icon:"⚖️",desc:"La loi peut rattraper le code.",effect:"+3 HP à chaque carte 'justice' jouée.",profile:"equilibre",bonus:{type:"justiceHeal",value:3}},
    {id:"alternative",name:"Construire en dehors",color:"#4ade80",icon:"🌱",desc:"Pas réformer — remplacer.",effect:"+10 HP de départ.",profile:"harmonie",bonus:{type:"outsideSystem",hp:10}},
  ],
  6:[
    {id:"presse",name:"Exposer les Pandora Papers",color:"#67e8f9",icon:"🎤",desc:"La transparence est la première arme du peuple.",effect:"Cartes 'justice' font -2 HP direct boss.",profile:"lucidite",bonus:{type:"pressBonus",value:2}},
    {id:"fisc",name:"Faire payer les impôts",color:"#d4a017",icon:"📊",desc:"La justice fiscale change les rapports de force.",effect:"Boss perd 1 HP/tour.",profile:"equilibre",bonus:{type:"taxBonus",bossHPPerTurn:1,mana:2}},
    {id:"coop",name:"Construire l'alternative",color:"#4ade80",icon:"🤝",desc:"Le Milliardaire ne peut pas acheter ce qu'il ne contrôle pas.",effect:"Combos 'coopération' +5 ATK.",profile:"harmonie",bonus:{type:"coopMaster",value:5}},
  ],
  7:[
    {id:"civic",name:"Démocratie économique",color:"#38bdf8",icon:"🗳️",desc:"Les citoyens décident des priorités économiques.",effect:"+1 ATK par carte confiance/representation jouée.",profile:"harmonie",bonus:{type:"civicBonus"}},
    {id:"tech",name:"Tech au service du collectif",color:"#22d3ee",icon:"🚀",desc:"L'innovation peut résoudre ce que la politique évite.",effect:"+2 mana de départ.",profile:"equilibre",bonus:{type:"techBonus",mana:2}},
    {id:"rupture",name:"Recommencer depuis la confiance",color:"#a78bfa",icon:"🌍",desc:"Revenir au commencement n'est pas une régression.",effect:"Toutes cartes 'confiance' coûtent 0.",profile:"lucidite",bonus:{type:"trustLoop"}},
  ],
};

// ─── PHASES ───────────────────────────────────────────────────
const PHASES = [
  {id:1,familia:"Famille Ours",era:"Préhistoire",color:"#c97c3a",accent:"#f5c07a",portrait:"🐻",
    bossHp:28,bossName:"L'Isolement",bossIcon:"🌑",bossDesc:"Avant le lien, chacun survit seul.",
    hook:"Tu as 15 ans et tu viens de naître dans la mauvaise famille. Ou dans la bonne. La différence, c'est ça — la préhistoire économique.",
    lines:["On est en 35 000 av. J.-C. Mais l'économie a déjà commencé.","Pas de pièces, pas de billets. Juste : qui partage avec qui.","Le clan Ours peut tisser des liens ou imposer sa loi.","Ce que tu choisis ici se répercutera sur toute la suite."],
    playerRole:"Décide comment ton clan va fonctionner.",
    bossDialogues:[{at:1.0,side:"boss",text:"L'isolement protège. Le lien expose. Pourquoi se risquer à faire confiance ?"},{at:0.75,side:"clan",text:"Le Père Ours partage sa chasse. Pas par altruisme — par calcul intelligent."},{at:0.50,side:"boss",text:"Ta confiance peut être trahie. L'isolement, jamais."},{at:0.25,side:"clan",text:"La Grand-Mère chante. La mémoire collective se constitue. C'est le premier algorithme."}],
    turnNarrative:["Le feu crépite. Qui mange avec qui ce soir ?","Un autre clan approche. Peur ou curiosité ?","La récolte est abondante. Partager ou stocker ?","Un membre du clan est blessé. Soigner ou avancer ?"],
    reactionLabel:"Ce choix façonnera l'Antiquité.",
    strategyPrompt:"Comment va fonctionner ton clan ?",
    guide:{
      objective:"Vaincre L'Isolement (28 HP) avant de manquer de PV.",
      tips:["Joue confiance + solidarité ensemble → Combo Réciprocité (+4 ATK à tous)","La Grand-Mère (mémoire) pioche 2 cartes — joue-la tôt","Les cartes sombres (domination) infligent des pénalités dès la Phase 4","Fin de tour : le boss attaque. Ta DEF totale absorbe la moitié des dégâts"],
      watchOut:"Évite de jouer trop de cartes domination : héritage négatif pour la phase suivante.",
      comboPriority:"Réciprocité (confiance+solidarité) → Clan Solidaire (confiance+solidarité+mémoire)"
    },
    playerCards:[
      {id:"pere_ours",name:"Père Ours",role:"Chasseur",emoji:"🐻",color:"#c97c3a",behavior:"confiance",atk:3,def:3,cost:0,effectDesc:"+4 PV au clan",rarity:"commun",lesson:"Le partage de nourriture est le premier contrat social. Il précède la monnaie de 30 000 ans.",lore:"Grand et imposant, le Père Ours parcourt les forêts immenses pour nourrir son clan.",quote:"« Celui qui donne avant d'avoir besoin ne manquera jamais. »"},
      {id:"mere_ours",name:"Mère Ours",role:"Cueilleuse",emoji:"🌿",color:"#60a878",behavior:"solidarite",atk:2,def:4,cost:0,effectDesc:"+1 DEF à tout le champ",rarity:"commun",lesson:"La réciprocité différée — donner sans attendre de retour immédiat — est le fondement de toute société durable.",lore:"Douce et méthodique, elle cartographie les ressources du territoire.",quote:"« Ce que la forêt donne, on le renvoie. Autrement, elle se ferme. »"},
      {id:"gp_ours",name:"Grand-Père",role:"Conteur",emoji:"📖",color:"#a78bfa",behavior:"memoire",atk:0,def:3,cost:0,effectDesc:"Piochez 2 cartes",rarity:"rare",lesson:"La mémoire collective, c'est ce qui permet de ne pas répéter les erreurs.",lore:"Sa mémoire est le disque dur du clan.",quote:"« On n'hérite pas de la terre de ses pères. On l'emprunte à ses enfants. »"},
      {id:"fils_ours",name:"Fils Ours",role:"Tailleur",emoji:"👦",color:"#fbbf24",behavior:"innovation",atk:4,def:2,cost:0,effectDesc:"+1 ATK permanent",rarity:"commun",lesson:"Partager une technique la multiplie. Un secret technique devient obsolète.",lore:"Curieux, toujours en train de gratter une pierre ou d'observer comment les autres font.",quote:"« Le meilleur outil est celui qu'on peut copier. »"},
      {id:"gm_ours",name:"Grand-Mère",role:"Soigneuse",emoji:"🌱",color:"#34d399",behavior:"solidarite",atk:1,def:5,cost:0,effectDesc:"+8 PV",rarity:"rare",lesson:"Les civilisations se mesurent à ce qu'elles font de leurs membres vulnérables.",lore:"Elle soigne sans compter. Elle ne cherche pas la contrepartie — mais elle sait qu'un clan affaibli est un clan vulnérable.",quote:"« La santé des autres est ma sécurité. »"},
      {id:"dominant_ours",name:"Le Dominant",role:"Alpha",emoji:"💪",color:"#dc2626",behavior:"domination",atk:5,def:3,cost:0,effectDesc:"+5 ATK, -2 PV allié",rarity:"commun",lesson:"La domination crée de la hiérarchie. La hiérarchie réduit les coûts de coordination mais augmente les coûts de résistance.",lore:"Fort et solitaire, il a appris à imposer sa volonté.",quote:"« Le plus fort survit. C'est la loi. (Jusqu'à ce qu'elle change.) »"},
    ],
    enemyCards:[{id:"isole",name:"L'Isolé",emoji:"🌑",color:"#6b7280",behavior:"tension",atk:3,def:2,effectDesc:"-2 PV/tour"},{id:"mefiance",name:"Méfiance",emoji:"💀",color:"#ef4444",behavior:"cupidite",atk:4,def:3,effectDesc:"Annule 1 allié"},{id:"froid",name:"L'Hiver",emoji:"❄️",color:"#93c5fd",behavior:"tension",atk:2,def:5,effectDesc:"+1 DEF ennemi/tour"},{id:"peur_primale",name:"Peur Primale",emoji:"😨",color:"#7c3aed",behavior:"tension",atk:3,def:3,effectDesc:"-1 ATK tes cartes"}],
  },
  {id:2,familia:"Famille Abeille",era:"Antiquité",color:"#f0a030",accent:"#fde68a",portrait:"🐝",
    bossHp:36,bossName:"L'Orfèvre Cupide",bossIcon:"⚖️",bossDesc:"Qui contrôle la monnaie contrôle tout le reste.",
    hook:"L'Antiquité a inventé quelque chose d'extraordinaire et de dangereux : la valeur abstraite.",
    lines:["La Famille Abeille vient de découvrir que certaines choses valent plus que d'autres.","Ce n'est pas l'utilité qui est remise en cause. 'On' a décidé simplement qu'elles valaient plus.","L'Orfèvre a compris ça avant tout le monde. Et il l'utilise.","Ton héritage de la Préhistoire arrive maintenant."],
    playerRole:"Construis la chaîne de valeur avant qu'elle ne soit capturée.",
    bossDialogues:[{at:1.0,side:"boss",text:"La monnaie n'est qu'un outil. Celui qui la contrôle contrôle tout."},{at:0.75,side:"clan",text:"Henriz distribue sa récolte sans passer par l'Orfèvre."},{at:0.50,side:"boss",text:"Tu crois que l'échange te libère ? Il te lie à moi tant que je fixe les prix."},{at:0.25,side:"clan",text:"Denis frappe un denier que tous reconnaissent."}],
    turnNarrative:["Les caravanes arrivent. La valeur se négocie. Qui fixe le prix ?","L'Orfèvre pèse l'or. Il sait quelque chose que tu ne sais pas encore.","La première dette vient d'être signée. C'était avant les pièces.","Qui contrôle l'étalon, contrôle tout ce qui se mesure."],
    reactionLabel:"Ton rapport à la monnaie conditionne les Temps Modernes.",
    strategyPrompt:"Ton rapport à la valeur détermine tout ce qui suit.",
    guide:{
      objective:"Vaincre l'Orfèvre (36 HP) en contrôlant la valeur avant lui.",
      tips:["Denis (monnaie) double le mana suivant — joue-le avant une carte chère","Combo Marché Vivant (échange+production) = +3 ATK +5 PV","Laure (valeur) est légendaire : garde-la pour activer un combo","L'Orfèvre Allié (+5ATK) risque d'augmenter la Corruption — surveille le compteur"],
      watchOut:"Si tu accumules 3 points de Corruption, un Monde Parallèle s'active. Ce n'est pas game over — c'est pédagogique.",
      comboPriority:"Marché Vivant (échange+production) → Essor (échange+innovation+production)"
    },
    playerCards:[
      {id:"henriz",name:"Henriz",role:"Cultivateur",emoji:"🌾",color:"#86efac",behavior:"production",atk:2,def:3,cost:2,effectDesc:"+1 Mana",rarity:"commun",lesson:"La production crée le surplus. Le surplus crée le marché. Le marché crée le besoin d'un étalon.",lore:"Henriz a compris que produire plus que ce dont on a besoin crée quelque chose de nouveau : le surplus.",quote:"« Je ne vends pas ma récolte. Je vends du temps transformé en blé. »"},
      {id:"seline",name:"Séline",role:"Conservatrice",emoji:"🧂",color:"#d4a017",behavior:"innovation",atk:2,def:3,cost:2,effectDesc:"Double ATK d'un allié",rarity:"rare",lesson:"Le sel était la première monnaie universelle. 'Salaire' vient de 'sal'.",lore:"Le sel est la première monnaie universelle. Séline l'a compris avant tout le monde.",quote:"« Ce qui se conserve se valorise. Ce qui se dépense disparaît. »"},
      {id:"denis",name:"Denis",role:"Le Denier",emoji:"💰",color:"#fb923c",behavior:"monnaie",atk:3,def:2,cost:2,effectDesc:"-2 DEF ennemi",rarity:"commun",lesson:"La monnaie n'a pas de valeur intrinsèque. Sa valeur vient de la confiance collective.",lore:"Il a frappé la première pièce de la région. Pas pour s'enrichir — pour faciliter les échanges.",quote:"« La pièce ne vaut rien. C'est la confiance qu'elle porte qui vaut tout. »"},
      {id:"laure",name:"Laure",role:"L'Or",emoji:"💛",color:"#fbbf24",behavior:"valeur",atk:4,def:2,cost:3,effectDesc:"+3 ATK si combo actif",rarity:"rare",lesson:"L'or ne nourrit pas, ne protège pas. Et pourtant depuis 5000 ans, il convainc.",lore:"L'or ne nourrit pas. Et pourtant depuis 5000 ans, il convainc.",quote:"« L'or brille parce que nous décidons qu'il brille. »"},
      {id:"marchand_ab",name:"Le Marchand",role:"Commerçant",emoji:"🛒",color:"#2dd4bf",behavior:"echange",atk:2,def:3,cost:2,effectDesc:"+3 PV + pioche 1",rarity:"commun",lesson:"L'échange crée de la valeur pour les deux parties.",lore:"Il fait le lien entre ceux qui produisent et ceux qui consomment.",quote:"« Le commerce ne crée pas la richesse — il la distribue. »"},
      {id:"orfevre_allie",name:"L'Orfèvre Allié",role:"Double Jeu",emoji:"🔱",color:"#9333ea",behavior:"cupidite",atk:5,def:4,cost:3,effectDesc:"+5 ATK mais +1 Corruption",rarity:"légendaire",lesson:"Parfois le seul moyen de changer un système est d'en faire partie.",lore:"Il a vu ce que l'Orfèvre fait. Et il a décidé d'utiliser les mêmes armes.",quote:"« Pour changer le système de l'intérieur, il faut d'abord y entrer. »"},
    ],
    enemyCards:[{id:"orfevre_b",name:"L'Orfèvre",emoji:"⚖️",color:"#c08040",behavior:"cupidite",atk:4,def:4,effectDesc:"-2 PV/tour"},{id:"accapareur",name:"L'Accapareur",emoji:"💎",color:"#f87171",behavior:"pouvoir",atk:5,def:3,effectDesc:"Vole 2 cartes"},{id:"speculateur",name:"Spéculateur",emoji:"📈",color:"#f59e0b",behavior:"cupidite",atk:3,def:3,effectDesc:"+1 ATK/tour"},{id:"devaluation",name:"Dévaluation",emoji:"📉",color:"#dc2626",behavior:"inegalite",atk:2,def:4,effectDesc:"-1 Mana/tour"}],
  },
  {id:3,familia:"Famille Hibou",era:"Moyen Âge",color:"#8b6940",accent:"#d4b483",portrait:"🦉",
    bossHp:44,bossName:"Le Seigneur Féodal",bossIcon:"👑",bossDesc:"La rente foncière — premier revenu passif de l'histoire.",
    hook:"Le Moyen Âge, c'est le premier 'business model' à grande échelle : possède la terre, encaisse les loyers.",
    lines:["La Famille Hibou vit dans un monde où tout appartient à quelqu'un.","Produire ne suffit pas — il faut produire pour quelqu'un d'autre.","L'héritage de l'Antiquité arrive. Ton rapport à la monnaie se concrétise.","La vraie question : comment sortir du système, pas juste comment survivre dedans."],
    playerRole:"Construis le commerce avant que la rente l'étouffe.",
    bossDialogues:[{at:1.0,side:"boss",text:"Ma terre nourrit vos familles. En échange, vous me nourrissez."},{at:0.75,side:"clan",text:"Le Marchand trouve une route qui contourne les terres du Seigneur."},{at:0.50,side:"boss",text:"Toutes les routes passent par quelqu'un. Tu changes de Seigneur, pas de système."},{at:0.25,side:"clan",text:"Les guildes s'organisent. La solidarité professionnelle précède le syndicat de 500 ans."}],
    turnNarrative:["Les routes de commerce s'ouvrent. Chaque péage est une taxe privée.","Le Bailli passe. La dîme attend. Elle a toujours existé sous un autre nom.","La halle du marché bourdonne. L'échange libre résiste à la rente.","Le Moine écrit. La première trace contractuelle vient d'être posée."],
    reactionLabel:"Ton rapport à la propriété forge le capitalisme industriel.",
    strategyPrompt:"Comment résistes-tu à la rente ?",
    guide:{
      objective:"Vaincre le Seigneur (44 HP). Règle de dîme : sans carte production sur le champ, -1 Mana par tour.",
      tips:["GARDE toujours une carte production (Paysan) sur le champ pour éviter la dîme","La Jacquerie (-8 HP boss) te coûte -5 PV — utilise-la en dernier recours","Artisan + Chevalier = combo État de Droit si tu y ajoutes justice ou regulation","Le Moine pioche 2 cartes gratis (cost 1)"],
      watchOut:"La Dîme est impitoyable : sans production sur le champ tu perds du mana chaque tour.",
      comboPriority:"Marché Vivant → État de Droit (justice+regulation)"
    },
    playerCards:[
      {id:"commercant",name:"Commerçant",role:"Marchand",emoji:"🧳",color:"#2dd4bf",behavior:"echange",atk:3,def:3,cost:2,effectDesc:"+5 PV + pioche 1",rarity:"commun",lesson:"Les grandes villes médiévales sont nées autour d'un marché, pas d'un château.",lore:"Il connaît dix routes que le Seigneur ne soupçonne pas.",quote:"« La route qui évite le château est toujours la plus profitable. »"},
      {id:"paysan",name:"Le Paysan",role:"Producteur",emoji:"🌻",color:"#86efac",behavior:"production",atk:2,def:5,cost:2,effectDesc:"+1 DEF par allié",rarity:"commun",lesson:"Pendant 1000 ans, 90% de l'humanité a produit la richesse que 10% accumulait.",lore:"Pendant 1000 ans, 90% de l'humanité a produit la richesse que 10% accumulait.",quote:"« Mes mains nourrissent le château. Mes mains peuvent aussi l'assiéger. »"},
      {id:"artisan",name:"L'Artisan",role:"Fabricant",emoji:"🛠️",color:"#c06040",behavior:"confiance",atk:4,def:3,cost:3,effectDesc:"+1 ATK permanent",rarity:"rare",lesson:"Les guildes médiévales ont inventé la certification, l'apprentissage et la protection sociale sectorielle.",lore:"Les guildes médiévales ont inventé la certification professionnelle.",quote:"« Ma guilde est ma loi. Et ma loi protège mieux que le Seigneur. »"},
      {id:"chevalier",name:"Le Chevalier",role:"Mercenaire",emoji:"⚔️",color:"#818cf8",behavior:"loyaute",atk:5,def:4,cost:3,effectDesc:"Bloque 1 attaque",rarity:"rare",lesson:"La loyauté féodale était un contrat bilatéral.",lore:"La loyauté féodale était un contrat bilatéral — pas une déférence aveugle.",quote:"« Je sers celui qui mérite d'être servi. »"},
      {id:"moine",name:"Le Moine",role:"Érudit",emoji:"📜",color:"#a78bfa",behavior:"memoire",atk:1,def:3,cost:1,effectDesc:"Piochez 2 cartes",rarity:"commun",lesson:"Les monastères ont préservé le savoir antique.",lore:"Les monastères ont préservé le savoir antique pendant les siècles obscurs.",quote:"« Ce que j'écris survivra aux pierres du château. »"},
      {id:"jacquerie",name:"La Jacquerie",role:"Révolte",emoji:"🔥",color:"#ef4444",behavior:"tension",atk:6,def:1,cost:2,effectDesc:"-8 HP boss mais -5 PV soi",rarity:"légendaire",lesson:"La Jacquerie de 1358 a été la première révolte fiscale de masse. Elle a échoué militairement. Elle a planté une graine qui a germé 431 ans plus tard.",lore:"Elle a échoué militairement. Elle a planté une graine qui a germé 431 ans plus tard.",quote:"« Nous mourrons peut-être. Mais ils se souviendront. »"},
    ],
    enemyCards:[{id:"seigneur_b",name:"Le Seigneur",emoji:"👑",color:"#d4a017",behavior:"pouvoir",atk:4,def:5,effectDesc:"Draine 1 Mana/tour"},{id:"bailli",name:"Le Bailli",emoji:"⚖️",color:"#f97316",behavior:"inegalite",atk:3,def:4,effectDesc:"-1 ATK tes cartes"},{id:"guerre_privee",name:"Guerre Privée",emoji:"⚔️",color:"#dc2626",behavior:"tension",atk:5,def:3,effectDesc:"Détruit 1 allié"},{id:"famine",name:"La Famine",emoji:"💀",color:"#374151",behavior:"tension",atk:2,def:6,effectDesc:"-3 PV/tour"}],
  },
  {id:4,familia:"Famille Loup",era:"Temps Modernes",color:"#6060a0",accent:"#a5b4fc",portrait:"🐺",
    bossHp:52,bossName:"L'Usurier",bossIcon:"🐍",bossDesc:"Le crédit devient piège. La dette se vend.",
    hook:"Les Temps Modernes ont inventé quelque chose de génial et de terrifiant : vendre du futur.",
    lines:["La Famille Loup entre dans l'ère où tout peut s'acheter à crédit.","Y compris l'État. Y compris la guerre. Y compris toi.","La Banque d'Angleterre vient d'être créée. L'État emprunte aux marchés.","Ton héritage des siècles précédents arrive. As-tu construit des protections ?"],
    playerRole:"Naviguez entre crédit et dette.",
    bossDialogues:[{at:1.0,side:"boss",text:"Tout le monde emprunte tôt ou tard. Je suis patient. Les intérêts composés travaillent 24h/24."},{at:0.75,side:"clan",text:"Le Syndicat s'organise. La force collective peut ce que l'individu ne peut pas seul."},{at:0.50,side:"boss",text:"Vos luttes collectives sont émouvantes. Mais la dette ne fait pas grève."},{at:0.25,side:"clan",text:"Le Réformateur présente les premières lois anti-usure."}],
    turnNarrative:["Les cheminées fument. Une classe sociale naît dans la sueur et la dette.","L'Usurier consulte son registre. Les intérêts s'accumulent pendant que tu dors.","Le Syndicat se réunit en secret. Organiser prend du temps. La dette, non.","Capital vs Travail. L'issue de ce combat définit encore ton monde aujourd'hui."],
    reactionLabel:"Ta résistance ici détermine si le contemporain a des protections.",
    strategyPrompt:"Comment résistes-tu à la dette ?",
    guide:{
      objective:"Vaincre l'Usurier (52 HP). Règle dette : les cartes sombres sur le champ infligent -2 PV par carte en fin de tour.",
      tips:["Le Syndicat (légendaire) donne +1 ATK à TOUS tes alliés — priorité absolue","Évite d'accumuler des cartes sombres sur le champ (règle dette)","Réformateur neutralise un ennemi ET soigne 6 PV","Banquier Progressiste réduit les effets dark ennemis"],
      watchOut:"Règle dette : chaque carte sombre sur ton champ te coûte 2 PV à chaque fin de tour.",
      comboPriority:"Coopération (cooperation+solidarite) → État de Droit (justice+regulation)"
    },
    playerCards:[
      {id:"travailleur",name:"Travailleur",role:"Salarié",emoji:"⚙️",color:"#94a3b8",behavior:"travail",atk:3,def:3,cost:2,effectDesc:"+2 ATK si allié travail",rarity:"commun",lesson:"La valeur du travail est toujours une négociation.",lore:"La valeur du travail est toujours une négociation. Sans rapport de force collectif, c'est perdu d'avance.",quote:"« Mon heure de travail vaut ce que je réussis à en obtenir. »"},
      {id:"syndicat",name:"Le Syndicat",role:"Collectif",emoji:"✊",color:"#60b4ff",behavior:"solidarite",atk:3,def:4,cost:3,effectDesc:"+1 ATK à tous les alliés",rarity:"légendaire",lesson:"Les congés payés, les 35h, la retraite, la sécu — tout ça vient de la résistance organisée.",lore:"Les congés payés, la semaine de 40h, la retraite, la sécurité sociale — tout cela vient de la résistance organisée.",quote:"« Seul on va plus vite. Ensemble on va plus loin. »"},
      {id:"comptable",name:"Le Comptable",role:"Gestionnaire",emoji:"📊",color:"#22d3ee",behavior:"prudence",atk:2,def:4,cost:2,effectDesc:"+2 Mana ce tour",rarity:"commun",lesson:"La comptabilité en partie double (Pacioli, 1494) a rendu le capitalisme possible.",lore:"La comptabilité en partie double, inventée par Pacioli en 1494, a rendu le capitalisme possible.",quote:"« Ce qui ne se mesure pas ne s'améliore pas. »"},
      {id:"imprimeur",name:"L'Imprimeur",role:"Diffuseur",emoji:"🗞️",color:"#a78bfa",behavior:"innovation",atk:2,def:3,cost:2,effectDesc:"Piochez 2 cartes",rarity:"rare",lesson:"Gutenberg et Internet ont fait la même chose à 550 ans d'écart.",lore:"La presse de Gutenberg a démocratisé l'information économique.",quote:"« L'information libre est la seule monnaie que les puissants ne peuvent pas dévaluer. »"},
      {id:"reformateur",name:"Réformateur",role:"Législateur",emoji:"📋",color:"#38bdf8",behavior:"justice",atk:2,def:5,cost:3,effectDesc:"Neutralise 1 ennemi + +6 PV",rarity:"rare",lesson:"La régulation naît toujours après la crise qu'elle aurait pu prévenir.",lore:"La régulation naît toujours après la crise qu'elle aurait pu prévenir.",quote:"« Je ne change pas le passé. Je protège l'avenir. »"},
      {id:"banquier_prog",name:"Banquier Progressiste",role:"Finance Éthique",emoji:"🏦",color:"#4ade80",behavior:"regulation",atk:3,def:4,cost:3,effectDesc:"Réduit effets dark ennemis",rarity:"rare",lesson:"Il existe des banques coopératives, des fonds éthiques. Ils représentent moins de 3% du marché.",lore:"Il existe des banques coopératives, des fonds éthiques. Ils représentent moins de 3% du marché.",quote:"« Le profit n'est pas une fin. C'est un outil. »"},
    ],
    enemyCards:[{id:"usurier_b",name:"L'Usurier",emoji:"🐍",color:"#f87171",behavior:"cupidite",atk:5,def:3,effectDesc:"Annule les soins"},{id:"rentier_b",name:"Le Rentier",emoji:"🏠",color:"#f97316",behavior:"inegalite",atk:3,def:5,effectDesc:"+1 ATK/tour"},{id:"banquier_b",name:"Le Banquier",emoji:"🏦",color:"#818cf8",behavior:"pouvoir",atk:4,def:4,effectDesc:"Vole 1 Mana/tour"},{id:"depossession",name:"Dépossession",emoji:"📑",color:"#dc2626",behavior:"inegalite",atk:4,def:3,effectDesc:"-2 cartes max en main"}],
  },
  {id:5,familia:"Famille Écureuil",era:"Contemporain",color:"#20a0a0",accent:"#67e8f9",portrait:"🐿️",
    bossHp:60,bossName:"L'Inégalité Systémique",bossIcon:"📊",bossDesc:"L'algorithme discrimine à la vitesse de la lumière.",
    hook:"L'inégalité contemporaine n'a plus besoin de Seigneur ni d'Orfèvre. Elle fonctionne seule.",
    lines:["La Famille Écureuil vit dans un monde optimisé pour quelques-uns.","L'inégalité n'est plus une décision — c'est une architecture.","Les héritages de 4 phases arrivent tous en même temps.","C'est là que tu réalises que le jeu était truqué depuis le début. Ou pas."],
    playerRole:"Réglez avant que le système ne se grippe définitivement.",
    bossDialogues:[{at:1.0,side:"boss",text:"L'inégalité n'est pas un accident. C'est une architecture optimisée."},{at:0.75,side:"clan",text:"L'Hacktiviste expose les biais. La transparence est une arme."},{at:0.50,side:"boss",text:"Tes lois ont 10 ans de retard sur mes algorithmes."},{at:0.25,side:"clan",text:"Le Législateur passe la loi. Lente, imparfaite — mais contraignante."}],
    turnNarrative:["L'algorithme optimise. Personne ne sait pour qui.","Ton poste vient d'être automatisé. Notification à 14h37.","Le Hacktiviste a trouvé la faille. La vérité commence à fuir.","La loi finit toujours par rattraper le réel. Mais combien de gens entre les deux ?"],
    reactionLabel:"Ta lucidité ici détermine si la Mondialisation a un contre-pouvoir.",
    strategyPrompt:"Comment tu agis dans un système conçu sans toi ?",
    guide:{
      objective:"Vaincre l'Inégalité (60 HP). Règle algo : le boss gagne +1 ATK cumulatif chaque tour.",
      tips:["URGENT : le boss grossit chaque tour → attaque vite, ne défense pas trop","Hacktiviste (légendaire) fait -4 HP direct sans résistance","Législateur neutralise TOUS les boss pendant 1 tour — garde-le pour un moment critique","Collectif Numérique +2 ATK/DEF si alliance active"],
      watchOut:"L'algorithme ne s'arrête pas : +1 ATK par tour. À tour 8, le boss attaque avec 12 ATK. Sois agressif.",
      comboPriority:"Lucidité Critique (mémoire+justice+innovation) → État de Droit"
    },
    playerCards:[
      {id:"ouvrier",name:"L'Ouvrier",role:"Travailleur",emoji:"🔧",color:"#94a3b8",behavior:"travail",atk:3,def:4,cost:2,effectDesc:"Résiste à 1 effet négatif",rarity:"commun",lesson:"L'automatisation détruit et crée des emplois — mais pas au même endroit, pas les mêmes salaires.",lore:"L'automatisation détruit des emplois et en crée d'autres — mais pas au même endroit.",quote:"« Ma qualification m'appartient. Mon poste, non. »"},
      {id:"patron",name:"Le Patron",role:"Dirigeant",emoji:"📊",color:"#818cf8",behavior:"decision",atk:4,def:3,cost:3,effectDesc:"Choisissez 1 effet en jeu",rarity:"rare",lesson:"La pression des actionnaires crée une myopie systémique.",lore:"La pression des actionnaires crée une myopie systémique — le trimestre prochain compte plus que la décennie.",quote:"« Je maximise la valeur actionnariale. C'est ma mission légale. »"},
      {id:"banquier_num",name:"Banquier Num.",role:"Fintech",emoji:"📱",color:"#22d3ee",behavior:"innovation",atk:3,def:3,cost:3,effectDesc:"+3 Mana ce tour",rarity:"rare",lesson:"La Fintech a donné accès aux services financiers à 1,4 milliard de non-bancarisés.",lore:"La Fintech a démocratisé l'accès financier pour 1,4 milliard de personnes non bancarisées.",quote:"« Votre téléphone est désormais votre banque, votre identité, votre vote. »"},
      {id:"legislateur",name:"Législateur",role:"Gardien",emoji:"⚖️",color:"#38bdf8",behavior:"justice",atk:2,def:6,cost:3,effectDesc:"Neutralise tous boss 1 tour",rarity:"rare",lesson:"Le RGPD (2018) a forcé les géants du numérique à demander la permission.",lore:"Le RGPD européen (2018) a forcé les géants du numérique à demander la permission.",quote:"« La loi arrive après le problème. Mais elle arrive. »"},
      {id:"hacktiviste",name:"Hacktiviste",role:"Révélateur",emoji:"💻",color:"#a78bfa",behavior:"innovation",atk:4,def:2,cost:3,effectDesc:"-4 HP ennemi direct",rarity:"légendaire",lesson:"Panama Papers, Pandora Papers — chaque fuite prouve que l'opacité financière mondiale n'est pas inévitable.",lore:"WikiLeaks, les Panama Papers — chaque fuite a prouvé que l'opacité financière mondiale n'est pas inévitable.",quote:"« L'information veut être libre. Et moi je l'aide. »"},
      {id:"collectif_num",name:"Collectif Numérique",role:"Communs",emoji:"🌐",color:"#4ade80",behavior:"cooperation",atk:3,def:4,cost:3,effectDesc:"+2 ATK/DEF si alliance active",rarity:"rare",lesson:"Wikipedia, Linux, Signal — des biens communs numériques qui fonctionnent sans extraction de valeur.",lore:"Wikipedia, Linux, Signal — des biens communs numériques qui fonctionnent.",quote:"« Ce qui nous relie est plus fort que ce qui nous sépare. »"},
    ],
    enemyCards:[{id:"algo_b",name:"L'Algorithme",emoji:"📊",color:"#f97316",behavior:"inegalite",atk:4,def:5,effectDesc:"+1 ATK/tour"},{id:"lobby_b",name:"Le Lobby",emoji:"🕴️",color:"#e879f9",behavior:"pouvoir",atk:3,def:6,effectDesc:"Bloque 1 carte/tour"},{id:"plateform_b",name:"Plateforme",emoji:"📱",color:"#f59e0b",behavior:"extraction",atk:4,def:4,effectDesc:"Extrait 1 Mana/tour"},{id:"desinfo_b",name:"Désinformation",emoji:"📺",color:"#ef4444",behavior:"tension",atk:3,def:4,effectDesc:"Annule 1 combo actif"}],
  },
  {id:6,familia:"Famille Renard",era:"Mondialisation",color:"#e06020",accent:"#fed7aa",portrait:"🦊",
    bossHp:70,bossName:"Le Milliardaire",bossIcon:"🦊",bossDesc:"Ce n'est pas une personne. C'est le nom qu'on donne au bout du système.",
    hook:"La mondialisation a créé plus de richesse que toute l'histoire humaine cumulée. Et plus d'inégalités absolues aussi.",
    lines:["La Famille Renard vit dans le monde que le libre-échange sans garde-fous a créé.","Le Milliardaire n'est pas un ennemi personnel — il est le résultat logique du système.","5 phases d'héritage arrivent en même temps. Tu vois ce que tu as construit.","La question n'est pas 'est-ce juste ?' mais 'est-ce durable ?'"],
    playerRole:"Résistez à la concentration. Chaque décision compte.",
    bossDialogues:[{at:1.0,side:"boss",text:"J'ai plus de richesse que 50 pays réunis. Je n'ai rien volé. J'ai juste joué selon les règles."},{at:0.75,side:"clan",text:"Le Coopérateur prouve qu'une autre architecture est possible — et rentable."},{at:0.50,side:"boss",text:"Vos coopératives sont touchantes. Mes fonds les rachèteront dans 5 ans."},{at:0.25,side:"clan",text:"La taxe sur les super-profits passe. Lentement, irrévocablement."}],
    turnNarrative:["Les indices montent. Pour combien de portefeuilles sur 8 milliards ?","0,003 secondes pour exécuter l'ordre. Des milliers d'emplois disparaissent.","La BCE délibère. Chaque dixième de point façonne des millions de destins.","Donation philanthropique annoncée. L'impôt ne fait pas la une."],
    reactionLabel:"Ta réponse ici décide si le Futur a des fondations.",
    strategyPrompt:"Comment réponds-tu à la concentration extrême ?",
    guide:{
      objective:"Vaincre le Milliardaire (70 HP). Règle immunité : sans justice/regulation sur ton champ, -3 dégâts sur chaque attaque.",
      tips:["ESSENTIEL : garde toujours Justice ou Regulation sur le champ pour percer l'immunité","Banque Centrale (légendaire) réinitialise tous les effets ennemis — extrêmement puissant","Coopérateur (légendaire) +3 ATK/DEF si combo coop — combinez avec Collectif","La Journaliste = -3 HP direct + pioche 1 : excellent rapport coût/effet"],
      watchOut:"Sans justice/regulation sur le champ, tu perds 3 dégâts sur chaque attaque. L'immunité du capital est réelle.",
      comboPriority:"État de Droit → Coopération → Lucidité Critique"
    },
    playerCards:[
      {id:"courtier",name:"Le Courtier",role:"Trader",emoji:"📈",color:"#f59e0b",behavior:"risque",atk:5,def:2,cost:3,effectDesc:"Échangez ATK et DEF",rarity:"rare",lesson:"70% des volumes boursiers mondiaux sont du trading haute fréquence.",lore:"Le trading haute fréquence représente 70% des volumes boursiers mondiaux.",quote:"« Je ne joue pas avec l'argent des autres. Je l'optimise. »"},
      {id:"bce",name:"Banque Centrale",role:"Régulateur",emoji:"🏦",color:"#60a5fa",behavior:"regulation",atk:3,def:5,cost:3,effectDesc:"Réinitialise effets ennemis",rarity:"légendaire",lesson:"'Whatever it takes' — 3 mots de Draghi en 2012 qui ont sauvé l'euro.",lore:"'Whatever it takes' — trois mots de Mario Draghi en 2012 qui ont sauvé l'euro.",quote:"« Nous ferons tout ce qu'il faut. Et croyez-moi, ce sera suffisant. »"},
      {id:"salarie_ren",name:"Le Salarié",role:"Employé",emoji:"💼",color:"#fc8181",behavior:"injustice",atk:3,def:3,cost:2,effectDesc:"+3 dégâts directs",rarity:"commun",lesson:"1980-2020 : productivité +70%, salaires réels +12%.",lore:"Entre 1980 et 2020, la productivité américaine a augmenté de 70%. Les salaires réels ont augmenté de 12%.",quote:"« Je produis plus chaque année. Je ne gagne pas plus chaque année. »"},
      {id:"chef_etat",name:"Chef d'État",role:"Politique",emoji:"🎙️",color:"#d4a017",behavior:"compromis",atk:2,def:5,cost:3,effectDesc:"Immunise alliés 1 tour",rarity:"rare",lesson:"Gouverner contre les marchés financiers est difficile — mais possible.",lore:"Gouverner contre les marchés financiers mondiaux est difficile — mais possible.",quote:"« L'état de droit est la seule chose que les marchés ne peuvent pas acheter. »"},
      {id:"journaliste",name:"La Journaliste",role:"Enquêtrice",emoji:"🎤",color:"#67e8f9",behavior:"justice",atk:3,def:3,cost:2,effectDesc:"-3 HP ennemi + pioche 1",rarity:"rare",lesson:"600 journalistes, 117 pays, 11,9 millions de documents — Pandora Papers.",lore:"Les Pandora Papers (2021) ont révélé les avoirs offshore de 35 dirigeants mondiaux.",quote:"« Je ne change pas le monde. Je montre ce que le monde est vraiment. »"},
      {id:"cooperateur",name:"Coopérateur",role:"ESS",emoji:"🤝",color:"#4ade80",behavior:"cooperation",atk:3,def:4,cost:3,effectDesc:"+3 ATK/DEF si combo coop",rarity:"légendaire",lesson:"280 millions d'emplois dans le mouvement coopératif mondial.",lore:"Le mouvement coopératif mondial emploie 280 millions de personnes et génère 3000 milliards de chiffre d'affaires.",quote:"« Le profit n'est pas une fin. C'est un outil au service d'une mission. »"},
    ],
    enemyCards:[{id:"milliardaire_b",name:"Milliardaire",emoji:"🦊",color:"#f97316",behavior:"inegalite",atk:6,def:5,effectDesc:"Double effets si dernier"},{id:"paradis_b",name:"Paradis Fiscal",emoji:"🏝️",color:"#f87171",behavior:"cupidite",atk:4,def:6,effectDesc:"Annule ATK 1 tour"},{id:"fonds_b",name:"Fonds Vautour",emoji:"🦅",color:"#e879f9",behavior:"pouvoir",atk:5,def:4,effectDesc:"Vole 2 PV max"},{id:"capture_reg",name:"Capture régulatoire",emoji:"🎭",color:"#818cf8",behavior:"pouvoir",atk:3,def:7,effectDesc:"Bloque regulation/justice"}],
  },
  {id:7,familia:"Famille Cigogne",era:"Futur Proche",color:"#40c0ff",accent:"#bae6fd",portrait:"🦢",
    bossHp:80,bossName:"L'Effondrement",bossIcon:"💫",bossDesc:"Pas un ennemi à haïr. Un résultat à éviter.",
    hook:"Le futur n'est pas écrit. Mais il est conditionné par tout ce qui précède.",
    lines:["La Famille Cigogne hérite de tout. Tes choix. Tes erreurs. Tes succès.","L'Effondrement n'est pas une métaphore. C'est une possibilité systémique réelle.","La boucle peut se fermer ici — seulement si tu comprends pourquoi elle s'est ouverte.","Tu peux encore choisir. Pas librement. Mais délibérément. C'est différent."],
    playerRole:"Bouclez l'odyssée. La confiance est votre arme finale.",
    bossDialogues:[{at:1.0,side:"boss",text:"Les systèmes s'effondrent. Tu as construit sur les mêmes fondations depuis l'Ours."},{at:0.75,side:"clan",text:"Le Citoyen vote avec ses euros, ses choix quotidiens."},{at:0.50,side:"boss",text:"Ta confiance est naïve. Les marchés n'ont pas de mémoire collective."},{at:0.25,side:"clan",text:"La coopération est anti-fragile. Ce qui nous relie est plus fort que ce qui nous sépare."}],
    turnNarrative:["L'humanité face au miroir. Que garder de 10 000 ans d'économie ?","Le Citoyen choisit l'engagement. C'est déjà une décision économique.","L'Entrepreneur éthique prouve que rentabilité et sens peuvent coexister.","La confiance, perdue depuis l'Orfèvre — elle peut être retrouvée."],
    reactionLabel:"La fin de l'odyssée. Et le début de la tienne.",
    strategyPrompt:"Comment tu boucles la boucle ?",
    guide:{
      objective:"Vaincre l'Effondrement (80 HP). Règle synergie : tous les combos coûtent 1 mana de moins.",
      tips:["Profite de la règle synergie : enchaîne les combos à moindre coût","Partenaire (légendaire) +2 ATK/DEF PAR combo actif — très fort en fin de partie","Mémoire Collective révèle tous tes héritages accumulés (bonus pédagogique)","Entrepreneur Éthique si éthique ≥ 6 : +4 ATK/DEF — vise les cartes lumineuses"],
      watchOut:"L'Effondrement attaque fort d'emblée. Tes héritages positifs sont ta vraie armure.",
      comboPriority:"Clan Solidaire → Coopération → Abnégation (prudence+confiance)"
    },
    playerCards:[
      {id:"citoyen",name:"Le Citoyen",role:"Acteur Civique",emoji:"🕊️",color:"#38bdf8",behavior:"confiance",atk:2,def:4,cost:2,effectDesc:"+1 ATK par allié confiance",rarity:"commun",lesson:"La démocratie économique — coopératives, budgets participatifs, monnaies locales.",lore:"La démocratie économique est la continuation de la démocratie politique par d'autres moyens.",quote:"« Chaque euro dépensé est un vote pour le monde que je veux. »"},
      {id:"elu",name:"L'Élu",role:"Représentant",emoji:"🗳️",color:"#7dd3fc",behavior:"representation",atk:3,def:4,cost:3,effectDesc:"Choisissez l'effet d'1 allié",rarity:"rare",lesson:"La représentativité est le premier garde-fou contre l'autoritarisme économique.",lore:"La représentativité est le premier garde-fou contre l'autoritarisme économique.",quote:"« Je représente ceux qui votent. Et ceux qui ne votent plus. »"},
      {id:"donateur",name:"Le Donateur",role:"Philanthrope",emoji:"🎁",color:"#4ade80",behavior:"solidarite",atk:1,def:4,cost:2,effectDesc:"+8 PV + pioche 1",rarity:"commun",lesson:"La philanthropie choisit ses bénéficiaires. L'impôt progressif ne choisit pas.",lore:"La philanthropie de Gates, Soros, Musk choisit ses bénéficiaires. L'impôt progressif ne choisit pas.",quote:"« Je donne ce que je veux à qui je veux. Est-ce de la générosité ou du pouvoir ? »"},
      {id:"partenaire",name:"Le Partenaire",role:"Collaborateur",emoji:"🔗",color:"#22d3ee",behavior:"cooperation",atk:4,def:4,cost:3,effectDesc:"+2 ATK/DEF par combo actif",rarity:"légendaire",lesson:"Les réseaux coopératifs sont anti-fragiles : ils se renforcent sous le stress.",lore:"Les systèmes coopératifs sont anti-fragiles : ils se renforcent sous le stress.",quote:"« Ce qui nous relie est plus fort que ce qui nous sépare. »"},
      {id:"entrepreneur_eth",name:"Entrepreneur Éthique",role:"Fondateur",emoji:"🚀",color:"#34d399",behavior:"innovation",atk:5,def:4,cost:3,effectDesc:"Si éthique ≥ 6 : +4 ATK/DEF",rarity:"légendaire",lesson:"Les entreprises à mission (B Corp) inscrivent des obligations sociales dans les statuts.",lore:"L'économie de demain sera à la fois éthique et rentable — ou elle ne sera pas.",quote:"« Mon entreprise a une mission. Le profit est son carburant, pas sa destination. »"},
      {id:"memoire_coll",name:"Mémoire Collective",role:"Héritage",emoji:"🌍",color:"#c084fc",behavior:"memoire",atk:2,def:6,cost:2,effectDesc:"Révèle tous les héritages accumulés",rarity:"rare",lesson:"Tu es ici la somme de tout ce qui précède.",lore:"Tu es ici la somme de tout ce qui précède. La différence entre subir son histoire et la comprendre.",quote:"« La mémoire n'est pas un musée. C'est une boussole. »"},
    ],
    enemyCards:[{id:"effondr_b",name:"L'Effondrement",emoji:"💫",color:"#f87171",behavior:"tension",atk:6,def:5,effectDesc:"-3 PV + annule 1 combo/tour"},{id:"desillusion_b",name:"Désillusion",emoji:"🌪️",color:"#6b7280",behavior:"tension",atk:4,def:6,effectDesc:"Annule coopération"},{id:"nihilisme_b",name:"Nihilisme",emoji:"🕳️",color:"#374151",behavior:"tension",atk:5,def:4,effectDesc:"Réduit ATK de moitié"},{id:"oubli_b",name:"L'Oubli",emoji:"🌫️",color:"#9ca3af",behavior:"tension",atk:3,def:7,effectDesc:"Efface 1 héritage positif"}],
  },
];

// ─── COMBOS ───────────────────────────────────────────────────
const COMBOS = [
  {id:"reciprocite",name:"Réciprocité",needs:["confiance","solidarite"],icon:"🌀",bonus:"+4 ATK pour tous",color:"#4ade80",narrative:"Donner avant de recevoir — la fondation de toute économie."},
  {id:"clan",name:"Clan Solidaire",needs:["confiance","solidarite","memoire"],icon:"✨",bonus:"UNISON — +12 PV, immunité",color:"#60a5fa",unison:true,narrative:"La mémoire collective est le seul bouclier que l'Isolement ne peut briser."},
  {id:"savoir",name:"Transmission",needs:["memoire","innovation"],icon:"📚",bonus:"Piochez 3 cartes",color:"#a78bfa",narrative:"Le savoir transmis est la seule richesse qui se multiplie en se partageant."},
  {id:"marche",name:"Marché Vivant",needs:["echange","production"],icon:"🏛️",bonus:"+3 ATK + +5 PV",color:"#fbbf24",narrative:"L'échange crée plus de valeur que la somme de ses parties."},
  {id:"essor",name:"Essor",needs:["echange","innovation","production"],icon:"🚀",bonus:"UNISON — +3 Mana permanent",color:"#86efac",unison:true,narrative:"Quand marché et ingéniosité s'allient, des civilisations naissent."},
  {id:"loi",name:"État de Droit",needs:["justice","regulation"],icon:"⚖️",bonus:"Neutralise ennemi + +8 PV",color:"#38bdf8",narrative:"La loi n'est pas une contrainte — c'est la liberté organisée."},
  {id:"cooperation",name:"Coopération",needs:["cooperation","solidarite"],icon:"🤝",bonus:"+2 ATK/DEF au champ",color:"#22d3ee",narrative:"La coopération produit plus que la compétition."},
  {id:"abnegation",name:"Abnégation",needs:["prudence","confiance"],icon:"🛡️",bonus:"+10 PV + DEF +2",color:"#6ee7b7",narrative:"La prudence du sage protège mieux que l'armure du guerrier."},
  {id:"monopole",name:"Extraction",needs:["cupidite","pouvoir"],icon:"🐍",bonus:"-10 HP ennemi direct",color:"#f87171",dark:true,narrative:"Le pouvoir sans frein dévore ce qu'il prétend nourrir."},
  {id:"lucidite",name:"Lucidité Critique",needs:["memoire","justice","innovation"],icon:"🔍",bonus:"Révèle effets cachés + +4 PV",color:"#e0e0e0",narrative:"Comprendre un système est la première étape pour le changer."},
  {id:"tyrannie",name:"Tyrannie",needs:["cupidite","pouvoir","inegalite"],icon:"💀",bonus:"UNISON SOMBRE — Boss ×2",color:"#f43f5e",unison:true,dark:true,narrative:"L'inégalité érigée en système devient une violence structurelle."},
];

const PHASE_RULES = {
  1:{id:"gratuite",label:"Âge du Don",desc:"Pas de mana — cartes gratuites. La réciprocité précède le marché.",icon:"🤲",color:"#c97c3a"},
  2:{id:"monnaie_double",label:"Alchimie Monétaire",desc:"Jouer monnaie/valeur double le mana suivant.",icon:"💰",color:"#f0a030"},
  3:{id:"dime",label:"La Dîme",desc:"Sans 'production' sur le champ, -1 mana par tour.",icon:"👑",color:"#8b6940"},
  4:{id:"dette",label:"Économie de la Dette",desc:"Les cartes dark jouées infligent -2 PV en fin de tour.",icon:"📜",color:"#6060a0"},
  5:{id:"algo",label:"L'Algorithme",desc:"Le boss gagne +1 ATK cumulatif chaque tour.",icon:"📊",color:"#20a0a0"},
  6:{id:"immunite",label:"Immunité du Capital",desc:"Boss ignore 3 premiers dégâts sans justice/regulation sur le champ.",icon:"🦊",color:"#e06020"},
  7:{id:"synergie",label:"Synergie Collective",desc:"Tous les combos coûtent 1 mana de moins.",icon:"🌿",color:"#40c0ff"},
};

const PROFILES = {
  harmonie:{name:"Le Tisseur de Liens",icon:"🌿",color:"#4ade80",desc:"Tu as choisi la coopération comme méthode. L'histoire te donne raison — des coopératives de Rochdale aux mouvements open source.",question:"Comment construis-tu des espaces de confiance dans ton quotidien ?"},
  domination:{name:"Le Diagnosticien",icon:"🔍",color:"#f87171",desc:"Tu vois les structures de pouvoir derrière chaque institution. La lucidité sur les mécanismes d'oppression est le premier outil du changement.",question:"Une fois le diagnostic posé — quelle est ta prochaine action concrète ?"},
  equilibre:{name:"Le Négociateur",icon:"⚖️",color:"#d4a017",desc:"Tu refuses les absolutismes. Les meilleurs changements viennent souvent de l'intérieur des systèmes.",question:"Quel compromis es-tu prêt à refuser même s'il est pragmatique ?"},
  lucidite:{name:"L'Honnête Intellectuel",icon:"💡",color:"#e0e0e0",desc:"Tu refuses les réponses trop simples. L'économie est une science humaine — incertaine, contextuelle, politique.",question:"Comment transformes-tu cette lucidité en action sans tomber dans la paralysie ?"},
};

const TUTO = [
  {title:"Bienvenue dans l'Odyssée",body:"7 époques. L'effet domino : ce que tu décides en Préhistoire se répercute jusqu'à la Mondialisation.",icon:"🌍"},
  {title:"Jouer une carte",body:"1 clic = la carte est posée sur ton champ. Elle reste et attaque à chaque tour. La DEF réduit les dégâts reçus.",icon:"🃏"},
  {title:"Héritages & Dominos",body:"À la fin de chaque phase, tes choix génèrent des héritages positifs ou négatifs pour la phase suivante. C'est irréversible.",icon:"🔗"},
  {title:"Corruption ☠",body:"Les cartes sombres accumulent la corruption. À 3, un Monde Parallèle s'ouvre. C'est pédagogique, pas punitif — tu peux fermer et continuer.",icon:"☠"},
  {title:"Guide & Pause",body:"Bouton ❓ = guide stratégique de la phase. Bouton ⏸ = pause sans perdre ta partie. Explore librement.",icon:"📚"},
];

const PIVOTS = [
  {after:1,year:"3500 av. J.-C.",title:"La tablette de Mésopotamie",fact:"À Uruk, des scribes gravent sur argile les premières dettes de l'histoire. La dette précède la pièce de monnaie de 2500 ans.",consequence:"Avant l'or, il y avait déjà l'obligation. La monnaie n'a pas créé la dette — elle l'a juste rendue portable.",color:"#f0a030"},
  {after:2,year:"1150 ap. J.-C.",title:"La lettre de change italienne",fact:"Les marchands de Florence inventent la lettre de change. C'est l'ancêtre du virement bancaire.",consequence:"Toute la finance moderne repose sur ce moment. La valeur commence à se détacher du tangible.",color:"#8b6940"},
  {after:3,year:"1694",title:"La Banque d'Angleterre",fact:"Pour financer une guerre, le roi Guillaume III emprunte à des marchands. En échange, ils obtiennent le droit d'émettre de la monnaie.",consequence:"Pour la première fois, un État reconnaît qu'il a besoin de la finance privée pour survivre.",color:"#6060a0"},
  {after:4,year:"1944 → 1971",title:"Bretton Woods et sa rupture",fact:"En 1971, Nixon suspend la convertibilité du dollar en or. Pour la première fois, aucune monnaie mondiale n'est adossée à rien de physique.",consequence:"Depuis 1971, la monnaie mondiale repose uniquement sur la confiance dans les États — là où tout avait commencé.",color:"#20a0a0"},
  {after:5,year:"1980-1989",title:"La grande dérégulation",fact:"Reagan et Thatcher dérégulent les marchés financiers. Les capitaux circulent librement. Le travail, lui, ne le peut toujours pas.",consequence:"Cette asymétrie est la matrice de toutes les inégalités mondiales contemporaines.",color:"#e06020"},
  {after:6,year:"2008",title:"La crise que personne n'a vue venir",fact:"Lehman Brothers s'effondre. Les États ont injecté 13 000 milliards de dollars. Très peu de banquiers ont été poursuivis.",consequence:"La même architecture financière existe encore aujourd'hui.",color:"#40c0ff"},
];

// ─── HELPERS ─────────────────────────────────────────────────
function shuffle(a) {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
}

function checkCombos(behaviors) {
  return COMBOS.filter(c => c.needs.every(b => behaviors.includes(b)));
}

class AmbiancePlayer {
  constructor() {
    this.currentPlayer = null;
    this.baseVolume = 0.25;
    this.fadeDuration = 0.4; // secondes
    this._fadeInterval = null;
  }

  _fadeVolume(element, from, to, duration, onEnd) {
    if (!element) return;
    const startTime = performance.now();
    const step = () => {
      if (!element) return;
      const now = performance.now();
      const elapsed = (now - startTime) / 1000;
      const t = Math.min(1, elapsed / duration);
      element.volume = from + (to - from) * t;
      if (t < 1) {
        this._fadeInterval = requestAnimationFrame(step);
      } else {
        if (onEnd) onEnd();
        if (this._fadeInterval === step) this._fadeInterval = null;
      }
    };
    if (this._fadeInterval) cancelAnimationFrame(this._fadeInterval);
    this._fadeInterval = requestAnimationFrame(step);
  }

  async play(src) {
    if (!src) return;
    if (this.currentPlayer && this.currentPlayer.src === src && this.currentPlayer.volume > 0) return;

    const newPlayer = new Audio(src);
    newPlayer.loop = true;
    newPlayer.volume = 0;

    try {
      await newPlayer.play();
    } catch(e) {
      console.warn("Lecture audio bloquée", e);
      return;
    }

    if (this.currentPlayer) {
      // Fondu de sortie sur l'ancienne piste
      this._fadeVolume(this.currentPlayer, this.currentPlayer.volume, 0, this.fadeDuration, () => {
        // Une fois l'ancienne complètement baissée, on l'arrête
        if (this.currentPlayer) {
          this.currentPlayer.pause();
          this.currentPlayer = null;
        }
        // Puis on démarre la nouvelle avec un fondu d'entrée
        this.currentPlayer = newPlayer;
        this._fadeVolume(newPlayer, 0, this.baseVolume, this.fadeDuration);
      });
    } else {
      // Aucune piste en cours : simple fondu d'entrée
      this.currentPlayer = newPlayer;
      this._fadeVolume(newPlayer, 0, this.baseVolume, this.fadeDuration);
    }
  }

  stop(instant = false) {
    if (this._fadeInterval) cancelAnimationFrame(this._fadeInterval);
    if (this.currentPlayer) {
      if (instant) {
        this.currentPlayer.pause();
        this.currentPlayer.currentTime = 0;
        this.currentPlayer = null;
      } else {
        this._fadeVolume(this.currentPlayer, this.currentPlayer.volume, 0, 0.3, () => {
          if (this.currentPlayer) {
            this.currentPlayer.pause();
            this.currentPlayer = null;
          }
        });
      }
    }
  }

  setVolume(vol) {
    this.baseVolume = Math.min(0.5, Math.max(0, vol));
    if (this.currentPlayer) this.currentPlayer.volume = this.baseVolume;
  }

  mute() {
    if (this.currentPlayer) this.currentPlayer.volume = 0;
  }
  unmute() {
    if (this.currentPlayer) this.currentPlayer.volume = this.baseVolume;
  }
}

// ─── AUDIO ENGINE (enhanced) ──────────────────────────────────
class AudioEngine {
  constructor() {
    this.ctx = null;
    this.enabled = false;
    this.masterGain = null;
    this.sfxGain = null;
    this._muted = false;
    this._sfxMuted = false;
    this.ambiance = new AmbiancePlayer();
    this.timeouts = [];
  }

  async init() {
    // On n'initialise le contexte Web Audio que pour les effets (SFX)
    if (this.ctx) {
      if (this.ctx.state === "suspended") {
        try { await this.ctx.resume(); } catch(e) {}
      }
      return;
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.18;
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.connect(this.masterGain);
      this.masterGain.connect(this.ctx.destination);
      this.enabled = true;
      if (this.ctx.state === "suspended") {
        await this.ctx.resume();
      }
    } catch(e) {
      this.enabled = false;
    }
  }

  async _ensureRunning() {
    if (!this.ctx) return false;
    if (this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch(e) { return false; }
    }
    return this.ctx.state === "running" && this.enabled && !this._muted && !this._sfxMuted;
  }

  _schedule(fn, delay) {
    const id = setTimeout(() => {
      fn();
      this.timeouts = this.timeouts.filter(t => t !== id);
    }, delay);
    this.timeouts.push(id);
    return id;
  }

  async _note(freq, type = "sine", duration = 0.2, volume = 0.1, filterFreq = null) {
    if (!await this._ensureRunning()) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      const filter = filterFreq ? this.ctx.createBiquadFilter() : null;
      osc.type = type;
      osc.frequency.value = freq;
      if (filter) {
        filter.type = "lowpass";
        filter.frequency.value = filterFreq;
        osc.connect(filter);
        filter.connect(gain);
      } else {
        osc.connect(gain);
      }
      gain.connect(this.sfxGain);
      const now = this.ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
      osc.start();
      osc.stop(now + duration);
    } catch(e) {}
  }

  playCard() {
    this._note(880, "triangle", 0.2, 0.08, 3000);
    this._schedule(() => this._note(660, "triangle", 0.18, 0.07, 2500), 90);
  }

  playImposed() {
    this._note(220, "sawtooth", 0.35, 0.12, 800);
    this._schedule(() => this._note(160, "sawtooth", 0.4, 0.1, 600), 180);
  }

  playCombo() {
    [440, 550, 660, 880].forEach((f, i) => {
      this._schedule(() => this._note(f, "sine", 0.25, 0.09, 2000 + i * 500), i * 100);
    });
  }

  playUnison() {
    [220, 330, 440, 550, 660].forEach((f, i) => {
      this._schedule(() => this._note(f, "sine", 0.6, 0.12, 1000 + i * 300), i * 120);
    });
  }

  playHeritage(dark) {
    if (dark) {
      this._note(160, "sawtooth", 0.5, 0.13, 500);
    } else {
      this._note(528, "sine", 0.6, 0.1, 2800);
    }
  }

  playDmg() {
    this._note(80, "sawtooth", 0.3, 0.16, 400);
  }

  playHeal() {
    this._note(660, "sine", 0.3, 0.09, 2000);
    this._schedule(() => this._note(880, "sine", 0.25, 0.07), 120);
  }

  playVictory() {
    const freqs = [440, 550, 660, 880, 1100];
    freqs.forEach((f, i) => {
      this._schedule(() => this._note(f, "sine", 0.6, 0.1, 3000), i * 100);
    });
  }

  playOver() {
    const freqs = [220, 180, 150, 120];
    freqs.forEach((f, i) => {
      this._schedule(() => this._note(f, "sawtooth", 0.5, 0.13, 300), i * 200);
    });
  }

  // Ambiances – appelée au début de chaque phase
  async playPhaseAmbience(phaseId) {
    // Table de correspondance phase -> fichier son
    const phaseSounds = {
      1: "/sounds/phase1_prehistoire.wav",
      2: "/sounds/phase2_antiquite.wav",
      3: "/sounds/phase3_moyen_age.mp3",
      4: "/sounds/phase4_temps_modernes.mp3",
      5: "/sounds/phase5_contemporain.mp3",
      6: "/sounds/phase6_mondialisation.wav",
      7: "/sounds/phase7_futur.mp3"
    };
    const src = phaseSounds[phaseId];
    if (src) {
      await this.ambiance.play(src);
    } else {
      console.warn(`No ambiance sound for phase ${phaseId}`);
    }
  }

  // Déclencher un événement de climax (ex: boss à 30% PV)
  triggerClimax() {
    // Optionnel : jouer un court sting ou modifier l'ambiance
    this._note(60, "sine", 1.5, 0.05, 200);
    // On pourrait aussi changer le volume de l'ambiance ou mixer une couche supplémentaire
    if (this.ambiance.currentPlayer) {
      this.ambiance.setVolume(0.35); // monter le volume
      this._schedule(() => this.ambiance.setVolume(0.25), 5000);
    }
  }

  stopBg(instant = false) {
  this.ambiance.stop(instant);
}

  toggleMute() {
    this._muted = !this._muted;
    if (this._muted) {
      this.ambiance.mute();
      if (this.masterGain) this.masterGain.gain.value = 0;
    } else {
      this.ambiance.unmute();
      if (this.masterGain) this.masterGain.gain.value = 0.18;
    }
    return this._muted;
  }

  get muted() { return this._muted; }

  dispose() {
    this.stopBg();
    this.timeouts.forEach(clearTimeout);
    this.timeouts = [];
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
    }
  }
}

const audio = new AudioEngine();

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── PAUSE MENU ───────────────────────────────────────────────
function PauseMenu({ phase, turn, playerHP, bossHP, bossHPMax, onResume, onRestart, onGuide }) {
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,background:"rgba(2,4,10,0.97)",backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn 0.2s"}}>
      <div style={{maxWidth:380,width:"calc(100% - 32px)",textAlign:"center",animation:"fadeUp 0.3s ease-out",fontFamily:"'Syne',sans-serif"}}>
        <div style={{fontSize:32,marginBottom:10,opacity:0.6}}>⏸</div>
        <div style={{fontSize:11,letterSpacing:"0.35em",color:"rgba(255,255,255,0.30)",marginBottom:6,textTransform:"uppercase"}}>Pause</div>
        <h2 style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:5}}>{phase.era}</h2>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:20}}>Tour {turn} · vs {phase.bossName}</div>
        
        {/* Status snapshot */}
        <div style={{display:"flex",gap:8,justifyContent:"center",marginBottom:20}}>
          <div style={{padding:"8px 14px",borderRadius:8,background:"rgba(248,113,113,0.07)",border:"1px solid rgba(248,113,113,0.15)"}}>
            <div style={{fontSize:18,fontWeight:800,color:"#fff",fontFamily:"'JetBrains Mono',monospace"}}>{playerHP}</div>
            <div style={{fontSize:10,color:"rgba(248,113,113,0.6)"}}>TES PV</div>
          </div>
          <div style={{padding:"8px 14px",borderRadius:8,background:`rgba(255,255,255,0.30)`,border:"1px solid rgba(255,255,255,0.07)"}}>
            <div style={{fontSize:18,fontWeight:800,color:"#fff",fontFamily:"'JetBrains Mono',monospace"}}>{bossHP}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.25)"}}>BOSS PV</div>
          </div>
          <div style={{padding:"8px 14px",borderRadius:8,background:`rgba(255,255,255,0.30)`,border:"1px solid rgba(255,255,255,0.07)"}}>
            <div style={{fontSize:18,fontWeight:800,color:"#fff",fontFamily:"'JetBrains Mono',monospace"}}>{Math.round((1-bossHP/bossHPMax)*100)}%</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.25)"}}>BOSS DMG</div>
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          <button onClick={onResume} style={{padding:"11px 20px",borderRadius:8,background:"linear-gradient(135deg,#4ade80,#22d3ee)",border:"none",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>▶ Reprendre</button>
          <button onClick={onGuide} style={{padding:"9px 20px",borderRadius:8,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",color:"rgba(255,255,255,0.6)",fontSize:11,fontWeight:700,cursor:"pointer"}}>❓ Guide de la phase</button>
          <button onClick={onRestart} style={{padding:"9px 20px",borderRadius:8,background:"rgba(248,113,113,0.05)",border:"1px solid rgba(248,113,113,0.15)",color:"#f87171",fontSize:11,cursor:"pointer"}}>↺ Recommencer l'Odyssée</button>
        </div>
        <div style={{marginTop:14,fontSize:10,color:"rgba(255,255,255,0.1)",lineHeight:1.6}}>Ta partie est sauvegardée. Tu peux reprendre à tout moment.</div>
      </div>
    </div>
  );
}

// ─── PHASE GUIDE PANEL ────────────────────────────────────────
function PhaseGuide({ phase, onClose }) {
  const g = phase.guide;
  if (!g) return null;
  const rule = PHASE_RULES[phase.id];
  return (
    <div style={{position:"fixed",inset:0,zIndex:1500,background:"rgba(0,0,0,0.88)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}} onClick={onClose}>
      <div style={{maxWidth:500,width:"100%",maxHeight:"90vh",overflowY:"auto",borderRadius:14,background:"linear-gradient(150deg,#07111f,#04090f)",border:`1px solid ${phase.color}22`,boxShadow:`0 32px 80px rgba(0,0,0,0.9)`,animation:"fadeUp 0.25s ease-out"}} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{padding:"14px 16px",borderBottom:`1px solid ${phase.color}15`,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>{phase.portrait}</span>
          <div>
            <div style={{fontSize:11,color:phase.color,textTransform:"uppercase",letterSpacing:"0.2em",fontFamily:"'Syne',sans-serif"}}>Guide · {phase.era}</div>
            <div style={{fontSize:13,fontWeight:700,color:"#fff",fontFamily:"'Syne',sans-serif"}}>{phase.bossName}</div>
          </div>
          <div style={{flex:1}} />
          <button onClick={onClose} style={{width:26,height:26,borderRadius:6,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.35)",cursor:"pointer",fontSize:12}}>×</button>
        </div>

        <div style={{padding:"14px 16px",display:"flex",flexDirection:"column",gap:12}}>
          {/* Objective */}
          <div style={{padding:"10px 13px",borderRadius:9,background:`${phase.color}08`,border:`1px solid ${phase.color}20`}}>
            <div style={{fontSize:11,fontWeight:700,color:phase.color,textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:5,fontFamily:"'Syne',sans-serif"}}>🎯 Objectif</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",lineHeight:1.7}}>{g.objective}</div>
          </div>

          {/* Phase rule */}
          {rule && (
            <div style={{padding:"10px 13px",borderRadius:9,background:"rgba(255,165,0,0.06)",border:"1px solid rgba(255,165,0,0.2)"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#fb923c",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:5,fontFamily:"'Syne',sans-serif"}}>⚡ Règle de phase — {rule.label}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.6}}>{rule.desc}</div>
            </div>
          )}

          {/* Watch out */}
          <div style={{padding:"10px 13px",borderRadius:9,background:"rgba(248,113,113,0.05)",border:"1px solid rgba(248,113,113,0.18)"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#f87171",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:5,fontFamily:"'Syne',sans-serif"}}>⚠ Attention</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.7}}>{g.watchOut}</div>
          </div>

          {/* Tips */}
          <div style={{padding:"10px 13px",borderRadius:9,background:"rgba(74,222,128,0.04)",border:"1px solid rgba(74,222,128,0.14)"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#4ade80",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:8,fontFamily:"'Syne',sans-serif"}}>💡 Conseils stratégiques</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {g.tips.map((tip,i) => (
                <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                  <span style={{fontSize:11,color:"#4ade8055",flexShrink:0,marginTop:2}}>{i+1}.</span>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.6}}>{tip}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Combo priority */}
          <div style={{padding:"9px 13px",borderRadius:8,background:"rgba(96,165,250,0.05)",border:"1px solid rgba(96,165,250,0.14)"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#60a5fa",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:4,fontFamily:"'Syne',sans-serif"}}>⚡ Combos à viser</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.6}}>{g.comboPriority}</div>
          </div>

          <button onClick={onClose} style={{padding:"9px",borderRadius:7,background:`${phase.color}10`,border:`1px solid ${phase.color}22`,color:phase.color,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Compris, on joue ▶</button>
        </div>
      </div>
    </div>
  );
}

// ─── FLOATING TEXT ────────────────────────────────────────────
function FloatingText({ texts }) {
  return (
    <div style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:300}}>
      {texts.map(t => (
        <div key={t.id} style={{position:"absolute",left:t.x||"50%",top:t.y||"40%",transform:"translateX(-50%)",fontSize:13,fontWeight:800,color:t.color||"#4ade80",fontFamily:"'JetBrains Mono',monospace",textShadow:`0 0 8px ${t.color||"#4ade80"}`,animation:"floatUp 1s ease-out forwards",pointerEvents:"none",whiteSpace:"nowrap"}}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

function HPBar({ current, max, color }) {
  const pct = Math.max(0, (current / max) * 100);
  const c = pct > 60 ? color : pct > 30 ? "#fb923c" : "#f87171";
  return (
    <div style={{height:6,background:"rgba(0,0,0,0.5)",borderRadius:3,overflow:"hidden",flex:1}}>
      <div style={{width:`${pct}%`,height:"100%",background:c,borderRadius:3,transition:"width 0.5s ease"}} />
    </div>
  );
}

function Card({ card, onClick, canPlay, inPlay, mini, onSelect }) {
  const [hov, setHov] = useState(false);
  const [tapped, setTapped] = useState(false);       // mobile : 1er tap = aperçu, 2e tap = jouer
  const [portalPos, setPortalPos] = useState(null);
  const cardRef = useRef(null);
  const isMobile = useIsMobile();

  const c = card.color || "#888";
  const isDark = DARK_BEHAVIORS.has(card.behavior);
  const isImposed = card.imposed;
  const rarityC = RARITY_COLORS[card.rarity] || "#94a3b8";
  const isSelected = isMobile && tapped && canPlay;

  // ── Hover desktop ────────────────────────────────────────────
  const handleMouseEnter = () => {
    if (isMobile) return;
    setHov(true);
    if (!mini && cardRef.current) {
      const r = cardRef.current.getBoundingClientRect();
      const goUp = r.top > window.innerHeight / 2;
      setPortalPos({ left: r.left + r.width / 2, top: goUp ? r.top - 8 : r.bottom + 8, goUp });
    }
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setHov(false);
    setPortalPos(null);
  };

  // ── Tap mobile ───────────────────────────────────────────────
  // Mini (terrain) : tap unique → tooltip portal (ferme en tap hors zone)
  // Normal (main) : 1er tap → highlight + portal, 2e tap → jouer, tap hors → annule
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    e.stopPropagation();

    if (mini) {
      if (cardRef.current) {
        const r = cardRef.current.getBoundingClientRect();
        const goUp = r.top > window.innerHeight * 0.55;
        setPortalPos({ left: r.left + r.width / 2, top: goUp ? r.top - 8 : r.bottom + 8, goUp });
      }
      return;
    }

    if (!canPlay) return;

    if (!tapped) {
      // Premier tap
      setTapped(true);
      setHov(true);
      onSelect?.();  // ← Notifie le parent
      if (cardRef.current) {
        const r = cardRef.current.getBoundingClientRect();
        const goUp = r.top > window.innerHeight * 0.55;
        setPortalPos({ left: r.left + r.width / 2, top: goUp ? r.top - 8 : r.bottom + 8, goUp });
      }
    } else {
      // Deuxième tap : jouer
      setTapped(false);
      setHov(false);
      setPortalPos(null);
      onClick?.(card);
    }
  };

  useEffect(() => {
    if (!isSelected && tapped) {
      setTapped(false);
      setHov(false);
      setPortalPos(null);
    }
  }, [isSelected, tapped]);

  // Fermer tooltip si on tape ailleurs
  useEffect(() => {
    if (!isMobile || !portalPos) return;
    const close = () => { setPortalPos(null); setTapped(false); setHov(false); };
    const timer = setTimeout(() => document.addEventListener("touchstart", close, { once: true }), 50);
    return () => clearTimeout(timer);
  }, [portalPos, isMobile]);

  // ── Mini ─────────────────────────────────────────────────────
  if (mini) return (
    <div
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      style={{
        width: isMobile ? 68 : 82,
        height: isMobile ? 102 : 122,
        borderRadius: 7, flexShrink: 0,
        background: isImposed
          ? "linear-gradient(150deg,#1a0005,#100009)"
          : isDark ? "linear-gradient(150deg,#120007,#080010)"
          : "linear-gradient(150deg,#060d16,#03070d)",
        border: inPlay ? `1.5px solid ${c}40`
          : isImposed ? "1.5px solid rgba(220,38,38,0.5)"
          : `1px solid ${c}10`,
        position: "relative", overflow: "hidden",
        display: "flex", flexDirection: "column", alignItems: "center",
        transition: "transform .15s",
        transform: hov ? "translateY(-4px)" : "none",
        animation: isImposed ? "inheritedCard 2s ease-in-out infinite" : "none",
        cursor: "default", userSelect: "none",
      }}
    >
      {inPlay && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${c},transparent)` }} />}
      <div style={{ position: "absolute", top: 3, right: 3, fontSize: 10, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono',monospace" }}>{card.cost ?? 0}</div>
      <div style={{ fontSize: isMobile ? 15 : 18, paddingTop: 8 }}>{card.emoji}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: isImposed ? "#ef4444" : `${c}cc`, textAlign: "center", paddingInline: 3, lineHeight: 1.2, marginTop: 2 }}>{card.name}</div>
      <div style={{ display: "flex", gap: 3, marginTop: 3 }}>
        <span style={{ fontSize: 10, color: "#f87171", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>⚔{card.atk}</span>
        <span style={{ fontSize: 10, color: "#60a5fa", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>🛡{card.def}</span>
      </div>
      {card.rarity && <div style={{ position: "absolute", bottom: 2, fontSize: 10, color: rarityC, fontWeight: 700 }}>{card.rarity === "légendaire" ? "★" : card.rarity === "rare" ? "◈" : "·"}</div>}
      {portalPos && createPortal(<MiniCardTooltip card={card} pos={portalPos} c={c} isDark={isDark} isImposed={isImposed} rarityC={rarityC} />, document.body)}
    </div>
  );

  // ── Normale (main) ────────────────────────────────────────────
  
  

  return (
    <>
      <div
        ref={cardRef}
        onClick={() => !isMobile && canPlay && onClick?.(card)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        style={{
          width: isMobile ? 90 : 108,
          height: isMobile ? 142 : 170,
          borderRadius: 10, flexShrink: 0,
          background: isImposed ? "linear-gradient(150deg,#1a0005,#10000a)"
            : isDark ? "linear-gradient(150deg,#140008,#08000f)"
            : "linear-gradient(150deg,#060d18,#030810)",
          border: isSelected ? `2px solid ${c}` : hov && canPlay ? `1.5px solid ${c}`
            : isImposed ? "1.5px solid rgba(220,38,38,0.6)"
            : `1px solid ${c}18`,
          boxShadow: isSelected ? `0 0 20px ${c}50, 0 8px 28px rgba(0,0,0,0.9)`
            : hov && canPlay ? `0 0 18px ${c}35, 0 8px 28px rgba(0,0,0,0.9)`
            : isImposed ? "0 0 12px rgba(220,38,38,0.2)"
            : "0 2px 8px rgba(0,0,0,0.5)",
          cursor: canPlay ? "pointer" : "default",
          transform: isSelected ? "translateY(-8px) scale(1.03)" : hov && canPlay ? "translateY(-6px) scale(1.02)" : "none",
          transition: "all 0.15s ease",
          opacity: canPlay ? 1 : 0.32,
          position: "relative", overflow: "hidden",
          userSelect: "none",
          display: "flex", flexDirection: "column", alignItems: "center",
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 30% 15%,${c}07,transparent 50%)`, pointerEvents: "none" }} />

        {/* Header coût + badge */}
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 5px 0" }}>
          <div style={{ width: 17, height: 17, borderRadius: "50%", background: canPlay ? `${c}18` : "rgba(255,255,255,0.02)", border: canPlay ? `1.5px solid ${c}50` : "1.5px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: canPlay ? c : "rgba(255,255,255,0.12)", fontFamily: "'JetBrains Mono',monospace" }}>
            {card.cost ?? 0}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: isImposed ? "#ef4444" : isDark ? "#f87171" : `${c}55`, background: isImposed ? "rgba(220,38,38,0.08)" : isDark ? "rgba(248,113,113,0.05)" : `${c}06`, padding: "1px 3px", borderRadius: 3 }}>
            {isImposed ? "⚠" : isDark ? "☠" : "◎"}
          </div>
        </div>

        {card.rarity && <div style={{ position: "absolute", top: 3, left: 4, fontSize: 11, color: rarityC, fontWeight: 700 }}>{card.rarity === "légendaire" ? "★" : card.rarity === "rare" ? "◈" : ""}</div>}

        <div style={{ fontSize: isMobile ? 22 : 26, marginTop: 2, marginBottom: 1 }}>{card.emoji}</div>

        <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, color: isImposed ? "#ef4444" : "#fff", padding: "0 6px", textAlign: "center", lineHeight: 1.15, fontFamily: "'Syne', sans-serif", maxHeight: 30, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" }}>
          {card.name}
        </div>

        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 3, padding: "0 6px", textAlign: "center", lineHeight: 1.1, maxHeight: 20, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
          {card.role}
        </div>

        <div style={{ display: "flex", width: "calc(100% - 8px)", borderRadius: 5, overflow: "hidden", border: "1px solid rgba(255,255,255,0.04)", marginBottom: 3 }}>
          <div style={{ flex: 1, padding: "3px 0", background: "rgba(248,113,113,0.06)", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
            <span style={{ fontSize: 9, color: "#f87171" }}>⚔</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#f87171", fontFamily: "'JetBrains Mono',monospace" }}>{card.atk}</span>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.04)" }} />
          <div style={{ flex: 1, padding: "3px 0", background: "rgba(96,165,250,0.06)", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
            <span style={{ fontSize: 9, color: "#60a5fa" }}>🛡</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", fontFamily: "'JetBrains Mono',monospace" }}>{card.def}</span>
          </div>
        </div>

        <div style={{ marginInline: 4, padding: "3px 5px", borderRadius: 4, background: `${c}07`, border: `1px solid ${c}10`, fontSize: 10, color: `${c}88`, lineHeight: 1.2, textAlign: "center", width: "calc(100% - 8px)", flex: 1, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: isMobile ? 2 : 3, WebkitBoxOrient: "vertical", wordBreak: "break-word", marginBottom: canPlay ? 22 : 16 }}>
          {card.effectDesc}
        </div>

        {/* Behavior tag */}
        <div style={{ position: "absolute", bottom: canPlay ? 22 : 4, left: "50%", transform: "translateX(-50%)", fontSize: 10, fontWeight: 600, color: BC[card.behavior] || c, background: (BC[card.behavior] || c) + "1a", padding: "1px 6px", borderRadius: 6, whiteSpace: "nowrap", maxWidth: "92%", overflow: "hidden", textOverflow: "ellipsis", transition: "bottom .15s" }}>
          {card.behavior}
        </div>

        {/* Bouton Jouer */}
        {canPlay && (
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 22, background: (hov || isSelected) ? `${c}22` : `${c}0d`, borderTop: `1px solid ${c}${(hov || isSelected) ? "40" : "18"}`, borderBottomLeftRadius: 9, borderBottomRightRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s, border-color .15s" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: (hov || isSelected) ? c : `${c}60`, fontFamily: "'Syne',sans-serif", transition: "color .15s" }}>
              {(hov || isSelected) ? (isMobile ? "↑ Tap pour jouer" : "▶ Jouer") : "· · ·"}
            </span>
          </div>
        )}
      </div>

      {(hov || isSelected) && portalPos && createPortal(
        <CardZoomPortal card={card} pos={portalPos} c={c} isDark={isDark} isImposed={isImposed} rarityC={rarityC} canPlay={canPlay} isMobile={isMobile} />,
        document.body
      )}
    </>
  );
}

function CardZoomPortal({ card, pos, c, isDark, isImposed, rarityC, canPlay, isMobile }) {
  const W = isMobile ? 160 : 148;
  const H = isMobile ? 250 : 234;

  let left, top;

  if (isMobile) {
    // Sur mobile : centré horizontalement, toujours au-dessus de la main (bas de l'écran)
    left = Math.max(8, Math.min(window.innerWidth / 2 - W / 2, window.innerWidth - W - 8));
    top = window.innerHeight - H - 90; // au-dessus de la zone main
  } else {
    left = Math.min(Math.max(pos.left - W / 2, 8), window.innerWidth - W - 8);
    top = pos.goUp ? pos.top - H : pos.top;
  }

  return (
    <div style={{
      position: "fixed", zIndex: 99999,
      left, top, width: W, height: H,
      borderRadius: 12, pointerEvents: "none",
      background: isImposed ? "linear-gradient(150deg,#1a0005,#10000a)"
        : isDark ? "linear-gradient(150deg,#140008,#08000f)"
        : "linear-gradient(150deg,#060d18,#030810)",
      border: `1.5px solid ${c}60`,
      boxShadow: `0 0 28px ${c}30, 0 16px 40px rgba(0,0,0,0.95)`,
      display: "flex", flexDirection: "column", alignItems: "center",
      overflow: "hidden",
      animation: "cardZoomIn .12s ease-out",
    }}>
      <style>{`@keyframes cardZoomIn{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 30% 15%,${c}09,transparent 55%)`, pointerEvents: "none" }} />

      <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 7px 0" }}>
        <div style={{ width: 20, height: 20, borderRadius: "50%", background: `${c}18`, border: `1.5px solid ${c}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono',monospace" }}>{card.cost ?? 0}</div>
        {card.rarity && <div style={{ fontSize: 13, color: rarityC, fontWeight: 700 }}>{card.rarity === "légendaire" ? "★" : card.rarity === "rare" ? "◈" : ""}</div>}
        <div style={{ fontSize: 12, fontWeight: 700, color: isImposed ? "#ef4444" : isDark ? "#f87171" : `${c}66`, background: `${c}08`, padding: "1px 4px", borderRadius: 3 }}>{isImposed ? "⚠" : isDark ? "☠" : "◎"}</div>
      </div>

      <div style={{ fontSize: 34, marginTop: 4, marginBottom: 2 }}>{card.emoji}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: isImposed ? "#ef4444" : "#fff", padding: "0 8px", textAlign: "center", lineHeight: 1.2, fontFamily: "'Syne', sans-serif" }}>{card.name}</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", margin: "2px 0 5px", padding: "0 8px", textAlign: "center", lineHeight: 1.2 }}>{card.role}</div>

      <div style={{ display: "flex", width: "calc(100% - 12px)", borderRadius: 5, overflow: "hidden", border: "1px solid rgba(255,255,255,0.05)", marginBottom: 5 }}>
        <div style={{ flex: 1, padding: "4px 0", background: "rgba(248,113,113,0.07)", display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
          <span style={{ fontSize: 11, color: "#f87171" }}>⚔</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#f87171", fontFamily: "'JetBrains Mono',monospace" }}>{card.atk}</span>
        </div>
        <div style={{ width: 1, background: "rgba(255,255,255,0.05)" }} />
        <div style={{ flex: 1, padding: "4px 0", background: "rgba(96,165,250,0.07)", display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>
          <span style={{ fontSize: 11, color: "#60a5fa" }}>🛡</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#60a5fa", fontFamily: "'JetBrains Mono',monospace" }}>{card.def}</span>
        </div>
      </div>

      <div style={{ marginInline: 6, padding: "5px 7px", borderRadius: 5, background: `${c}08`, border: `1px solid ${c}12`, fontSize: 11, color: `${c}99`, lineHeight: 1.35, textAlign: "center", width: "calc(100% - 12px)", flex: 1, overflow: "hidden", wordBreak: "break-word", marginBottom: 24, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical" }}>
        {card.effectDesc}
      </div>

      <div style={{ position: "absolute", bottom: 5, left: "50%", transform: "translateX(-50%)", fontSize: 11, fontWeight: 600, color: BC[card.behavior] || c, background: (BC[card.behavior] || c) + "1a", padding: "2px 8px", borderRadius: 6, whiteSpace: "nowrap" }}>
        {card.behavior}
      </div>
    </div>
  );
}


function MiniCardTooltip({ card, pos, c, isDark, isImposed, rarityC }) {
  const isMobile = useIsMobile();
  return <CardZoomPortal card={card} pos={pos} c={c} isDark={isDark} isImposed={isImposed} rarityC={rarityC} canPlay={false} isMobile={isMobile} />;
}

// ─── CHARACTERS MODAL ─────────────────────────────────────────
function CharactersModal({ phaseIdx, onClose }) {
  const [selPhase, setSelPhase] = useState(phaseIdx + 1);
  const [selChar, setSelChar] = useState(null);
  const phase = PHASES[selPhase - 1];
  const chars = phase?.playerCards || [];

  useEffect(() => { setSelChar(chars[0] || null); }, [selPhase]); // eslint-disable-line

  return (
    <div style={{position:"fixed",inset:0,zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.88)",backdropFilter:"blur(8px)"}} onClick={onClose}>
      <div style={{width:"min(820px,96vw)",maxHeight:"min(580px,92vh)",borderRadius:16,background:"linear-gradient(145deg,#080d14,#050910)",border:"1px solid rgba(255,255,255,0.06)",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.9)",overflow:"hidden",animation:"fadeUp 0.25s ease-out"}} onClick={e => e.stopPropagation()}>
        <div style={{padding:"14px 18px",borderBottom:"1px solid rgba(255,255,255,0.05)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <span style={{fontSize:18}}>{phase?.portrait}</span>
          <div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.32 )",textTransform:"uppercase",letterSpacing:"0.2em",fontFamily:"'Syne',sans-serif"}}>Galerie des Personnages</div>
            <div style={{fontSize:14,fontWeight:700,color:"#fff",fontFamily:"'Syne',sans-serif"}}>{phase?.familia} · {phase?.era}</div>
          </div>
          <div style={{flex:1}} />
          <button onClick={onClose} style={{width:26,height:26,borderRadius:6,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.35)",cursor:"pointer",fontSize:12}}>×</button>
        </div>
        <div style={{display:"flex",gap:1,padding:"8px 12px 0",overflowX:"auto",flexShrink:0}}>
          {PHASES.map(p => (
            <button key={p.id} onClick={() => setSelPhase(p.id)} style={{padding:"5px 9px",borderRadius:"7px 7px 0 0",whiteSpace:"nowrap",background:selPhase===p.id?`${p.color}12`:"transparent",border:selPhase===p.id?`1px solid ${p.color}25`:"1px solid transparent",borderBottom:"none",color:selPhase===p.id?p.color:"rgba(255,255,255,0.32 )",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>
              {p.portrait} {p.era}
            </button>
          ))}
        </div>
        <div style={{flex:1,display:"flex",minHeight:0}}>
          <div style={{width:175,borderRight:"1px solid rgba(255,255,255,0.04)",overflowY:"auto",flexShrink:0,padding:"8px"}}>
            {chars.map(ch => (
              <button key={ch.id} onClick={() => setSelChar(ch)} style={{width:"100%",padding:"8px 9px",borderRadius:8,marginBottom:3,textAlign:"left",cursor:"pointer",background:selChar?.id===ch.id?`${ch.color}10`:"rgba(255,255,255,0.01)",border:selChar?.id===ch.id?`1px solid ${ch.color}30`:"1px solid transparent",transition:"all 0.15s"}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:18,lineHeight:1}}>{ch.emoji}</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:selChar?.id===ch.id?ch.color:"rgba(255,255,255,0.6)",fontFamily:"'Syne',sans-serif",lineHeight:1.2}}>{ch.name}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.32 )"}}>{ch.role}</div>
                    {ch.rarity && <div style={{fontSize:11,fontWeight:700,color:RARITY_COLORS[ch.rarity],marginTop:1}}>{ch.rarity==="légendaire"?"★ légendaire":ch.rarity==="rare"?"◈ rare":"· commun"}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
          {selChar ? (
            <div style={{flex:1,overflowY:"auto",padding:"18px 20px"}}>
              <div style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:16}}>
                <div style={{fontSize:52,lineHeight:1,filter:`drop-shadow(0 0 20px ${selChar.color}60)`}}>{selChar.emoji}</div>
                <div>
                  <div style={{fontSize:11,color:RARITY_COLORS[selChar.rarity]||"#94a3b8",textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:2,fontFamily:"'Syne',sans-serif"}}>{selChar.rarity==="légendaire"?"★ légendaire":selChar.rarity==="rare"?"◈ rare":"· commun"}</div>
                  <div style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif",lineHeight:1,marginBottom:4}}>{selChar.name}</div>
                  <div style={{fontSize:11,color:selChar.color}}>{selChar.role}</div>
                  <div style={{display:"flex",gap:8,marginTop:5}}>
                    <span style={{fontSize:12,color:"#f87171",fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>⚔ {selChar.atk}</span>
                    <span style={{fontSize:12,color:"#60a5fa",fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>🛡 {selChar.def}</span>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.32 )"}}>{selChar.behavior}</span>
                  </div>
                </div>
              </div>
              {selChar.lore && <div style={{padding:"12px 14px",borderRadius:10,background:`${selChar.color}06`,border:`1px solid ${selChar.color}18`,marginBottom:12}}><div style={{fontSize:11,color:"rgba(255,255,255,0.6)",lineHeight:1.8,fontFamily:"'DM Serif Display',serif"}}>{selChar.lore}</div></div>}
              {selChar.quote && <div style={{padding:"10px 14px",borderRadius:8,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",marginBottom:12}}><div style={{fontSize:12,color:"rgba(255,255,255,0.45)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif",lineHeight:1.6}}>{selChar.quote}</div></div>}
              {selChar.lesson && <div style={{padding:"8px 12px",borderRadius:7,background:`${selChar.color}09`,border:`1px solid ${selChar.color}20`}}><div style={{fontSize:11,fontWeight:700,color:selChar.color,letterSpacing:"0.12em",marginBottom:4,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Leçon Économique</div><div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.7,fontFamily:"'DM Serif Display',serif"}}>{selChar.lesson}</div></div>}
            </div>
          ) : <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,0.1)",fontSize:12}}>Sélectionnez un personnage</div>}
        </div>
      </div>
    </div>
  );
}

// ─── CODEX DRAWER ─────────────────────────────────────────────
function Codex({ unlockedPivots, lessons, onClose }) {
  const [tab, setTab] = useState("pivots");
  return (
    <div style={{position:"fixed",inset:0,zIndex:700,display:"flex",justifyContent:"flex-end"}} onClick={onClose}>
      <div style={{width:"min(340px,92vw)",height:"100%",background:"linear-gradient(160deg,#06090f,#040710)",borderLeft:"1px solid rgba(255,255,255,0.05)",display:"flex",flexDirection:"column",animation:"slideRight 0.22s ease-out",boxShadow:"-16px 0 50px rgba(0,0,0,0.8)"}} onClick={e => e.stopPropagation()}>
        <div style={{padding:"14px 14px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:"#fff",fontFamily:"'Syne',sans-serif"}}>Codex de l'Odyssée</div>
            <button onClick={onClose} style={{width:22,height:22,borderRadius:5,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.35)",cursor:"pointer",fontSize:11}}>×</button>
          </div>
          <div style={{display:"flex",gap:2}}>
            {[["pivots","Pivots"],["lecons","Leçons"],["regles","Règles"],["combos","Combos"]].map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} style={{flex:1,padding:"5px 0",borderRadius:"6px 6px 0 0",background:tab===id?"rgba(255,255,255,0.05)":"transparent",border:tab===id?"1px solid rgba(255,255,255,0.07)":"1px solid transparent",borderBottom:tab===id?"1px solid #06090f":"none",color:tab===id?"#fff":"rgba(255,255,255,0.25)",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>{label}</button>
            ))}
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"11px 12px"}}>
          {tab === "pivots" && (
            <>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.32 )",marginBottom:9,lineHeight:1.6}}>{unlockedPivots.length === 0 ? "Progressez pour débloquer les pivots historiques." : `${unlockedPivots.length}/6 pivots débloqués`}</div>
              {PIVOTS.map(p => {
                const u = unlockedPivots.includes(p.after);
                return (
                  <div key={p.after} style={{marginBottom:10,padding:"10px",borderRadius:8,background:u?`${p.color}05`:"rgba(255,255,255,0.01)",border:u?`1px solid ${p.color}18`:"1px solid rgba(255,255,255,0.04)",opacity:u?1:0.25}}>
                    <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:u?5:0}}>
                      <div style={{fontSize:11,fontWeight:700,color:u?p.color:"rgba(255,255,255,0.32 )",fontFamily:"'Syne',sans-serif"}}>{u ? p.year : "???"}</div>
                      <div style={{fontSize:11,fontWeight:700,color:u?"#fff":"rgba(255,255,255,0.32 )",fontFamily:"'Syne',sans-serif"}}>{u ? p.title : "[Verrouillé]"}</div>
                    </div>
                    {u && <><div style={{fontSize:12,color:"rgba(255,255,255,0.4)",lineHeight:1.6,marginBottom:4}}>{p.fact}</div><div style={{fontSize:11,color:p.color,fontStyle:"italic",fontFamily:"'DM Serif Display',serif"}}>{p.consequence}</div></>}
                  </div>
                );
              })}
            </>
          )}
          {tab === "lecons" && (
            <>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.32 )",marginBottom:9,fontFamily:"'Syne',sans-serif"}}>{lessons.length} leçon{lessons.length > 1 ? "s" : ""} mémorisée{lessons.length > 1 ? "s" : ""}</div>
              {lessons.length === 0 && <div style={{fontSize:12,color:"rgba(255,255,255,0.12)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif"}}>Posez des cartes pour accumuler des leçons économiques.</div>}
              {lessons.map((l, i) => (
                <div key={i} style={{marginBottom:7,padding:"8px 10px",borderRadius:7,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.04)"}}>
                  <div style={{display:"flex",gap:6,alignItems:"flex-start"}}>
                    <span style={{fontSize:12,flexShrink:0}}>{l.emoji}</span>
                    <div>
                      <div style={{fontSize:11,fontWeight:700,color:l.color,marginBottom:1,fontFamily:"'Syne',sans-serif"}}>{l.name}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",lineHeight:1.6,fontFamily:"'DM Serif Display',serif"}}>{l.lesson}</div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {tab === "regles" && (
            <div>
              {Object.entries(PHASE_RULES).map(([pid, rule]) => (
                <div key={pid} style={{marginBottom:9,padding:"8px 10px",borderRadius:7,background:`${rule.color}05`,border:`1px solid ${rule.color}12`}}>
                  <div style={{display:"flex",gap:5,alignItems:"center",marginBottom:3}}>
                    <span style={{fontSize:11}}>{rule.icon}</span>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.25)",fontFamily:"'Syne',sans-serif"}}>Phase {pid}</div>
                    <div style={{fontSize:12,fontWeight:700,color:rule.color,fontFamily:"'Syne',sans-serif"}}>{rule.label}</div>
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",lineHeight:1.6}}>{rule.desc}</div>
                </div>
              ))}
            </div>
          )}
          {tab === "combos" && (
            <div>
              {COMBOS.map(c => (
                <div key={c.id} style={{marginBottom:8,padding:"9px 11px",borderRadius:8,background:`${c.color}08`,border:`1px solid ${c.color}20`}}>
                  <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                    <span style={{fontSize:14}}>{c.icon}</span>
                    <div style={{fontSize:10.5,fontWeight:700,color:c.color,fontFamily:"'Syne',sans-serif"}}>{c.name}{c.unison?" ⚡":""}{c.dark?" ☠":""}</div>
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:4}}>{c.needs.join(" + ")}</div>
                  <div style={{fontSize:11,color:c.color,fontWeight:700,marginBottom:4}}>{c.bonus}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif"}}>{c.narrative}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── TUTORIAL OVERLAY ─────────────────────────────────────────
function TutorialOverlay({ onDone }) {
  const [step, setStep] = useState(0);
  const s = TUTO[step];
  return (
    <div style={{position:"fixed",inset:0,zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.92)",backdropFilter:"blur(6px)"}}>
      <div style={{maxWidth:360,padding:"28px 24px",borderRadius:14,background:"linear-gradient(150deg,#07111f,#040c16)",border:"1px solid rgba(255,255,255,0.06)",textAlign:"center",animation:"fadeUp 0.3s ease-out",fontFamily:"'Syne',sans-serif"}}>
        <div style={{fontSize:44,marginBottom:10,filter:"drop-shadow(0 0 18px #38bdf830)"}}>{s.icon}</div>
        <div style={{fontSize:11,letterSpacing:"0.25em",color:"rgba(255,255,255,0.30)",marginBottom:5}}>{step + 1}/{TUTO.length}</div>
        <div style={{fontSize:17,fontWeight:700,color:"#fff",marginBottom:8}}>{s.title}</div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",lineHeight:1.8,marginBottom:20,fontFamily:"'DM Serif Display',serif"}}>{s.body}</div>
        <div style={{display:"flex",justifyContent:"center",gap:3,marginBottom:16}}>
          {TUTO.map((_,i) => <div key={i} style={{width:i===step?12:3,height:3,borderRadius:2,background:i<=step?"#38bdf8":"rgba(255,255,255,0.07)",transition:"all 0.3s"}} />)}
        </div>
        <div style={{display:"flex",gap:5,justifyContent:"center"}}>
          {step > 0 && <button onClick={() => setStep(s => s - 1)} style={{padding:"6px 14px",borderRadius:6,background:"rgba(255,255,255,0.30)",border:"1px solid rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.3)",fontSize:11,cursor:"pointer"}}>←</button>}
          <button onClick={() => { if (step < TUTO.length - 1) setStep(s => s + 1); else onDone(); }} style={{padding:"8px 20px",borderRadius:6,background:"linear-gradient(135deg,#38bdf8,#0ea5e9)",border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>
            {step < TUTO.length - 1 ? "Suivant →" : "Commencer ⟹"}
          </button>
        </div>
        <button onClick={onDone} style={{marginTop:9,background:"none",border:"none",color:"rgba(255,255,255,0.32 )",fontSize:11,cursor:"pointer"}}>Passer le tutoriel</button>
      </div>
    </div>
  );
}

// ─── HERITAGE REVEAL ──────────────────────────────────────────
function HeritageReveal({ heritages, phaseName, onContinue }) {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (shown < heritages.length) {
      const t = setTimeout(() => setShown(s => s + 1), 650);
      return () => clearTimeout(t);
    } else if (heritages.length > 0) {
      audio.playHeritage(heritages.some(h => h.type !== "bonus"));
    }
  }, [shown, heritages.length]);

  if (!heritages.length) return null;
  return (
    <div style={{position:"fixed",inset:0,zIndex:1100,background:"rgba(2,4,10,0.97)",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px 18px"}}>
      <div style={{maxWidth:540,width:"100%",textAlign:"center"}}>
        <div style={{fontSize:11,letterSpacing:"0.3em",color:"rgba(255,255,255,0.12)",marginBottom:8,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Effet Domino</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif",marginBottom:5}}>Ce que tu as semé en {phaseName}…</div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:22,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>…se récolte maintenant.</div>
        <div style={{display:"flex",flexDirection:"column",gap:9,marginBottom:24}}>
          {heritages.map((h, i) => (
            <div key={h.id} style={{padding:"13px 15px",borderRadius:11,background:h.type!=="bonus"?"rgba(220,38,38,0.05)":"rgba(74,222,128,0.05)",border:`1.5px solid ${h.color}28`,opacity:i<shown?1:0,transform:i<shown?"none":"translateY(8px)",transition:"all 0.5s ease-out"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:11}}>
                <span style={{fontSize:22,flexShrink:0}}>{h.icon}</span>
                <div style={{textAlign:"left",flex:1}}>
                  <div style={{display:"flex",gap:5,alignItems:"center",marginBottom:3}}>
                    <div style={{fontSize:12,fontWeight:800,color:h.color,fontFamily:"'Syne',sans-serif"}}>{h.name}</div>
                    <div style={{fontSize:11,padding:"1px 5px",borderRadius:8,background:`${h.color}10`,border:`1px solid ${h.color}22`,color:h.color,fontWeight:700}}>{h.type==="bonus"?"✦ Bonus":h.type==="malus"?"⚠ Malus":"☠ Systémique"}</div>
                    {h.forced && <div style={{fontSize:11,color:"#dc2626",fontWeight:700}}>INÉVITABLE</div>}
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.65,marginBottom:4,fontFamily:"'DM Serif Display',serif"}}>{h.desc}</div>
                  <div style={{fontSize:12,fontWeight:700,color:h.color,background:`${h.color}0a`,padding:"3px 7px",borderRadius:4,display:"inline-block"}}>{h.effect}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {shown >= heritages.length && (
          <div style={{animation:"fadeUp 0.4s ease-out"}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.32 )",marginBottom:14,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>Tes choix ont façonné le monde suivant. Ce n'est pas une métaphore.</div>
            <button onClick={onContinue} style={{padding:"10px 22px",borderRadius:7,background:"linear-gradient(135deg,#4ade80,#22d3ee)",border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Voir la suite ⟹</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PIVOT SCREEN ─────────────────────────────────────────────
function PivotScreen({ pivot, onContinue }) {
  const [rev, setRev] = useState(false);
  useEffect(() => { const t = setTimeout(() => setRev(true), 500); return () => clearTimeout(t); }, []);
  return (
    <div style={{position:"fixed",inset:0,zIndex:1100,background:"#03060d",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 20px"}}>
      <div style={{maxWidth:580,width:"100%",textAlign:"center",animation:"fadeUp 0.5s ease-out"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,justifyContent:"center",marginBottom:22}}>
          <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${pivot.color}35)`}} />
          <div style={{padding:"5px 14px",borderRadius:18,background:`${pivot.color}10`,border:`1px solid ${pivot.color}30`,fontSize:12,fontWeight:700,color:pivot.color,fontFamily:"'Syne',sans-serif"}}>{pivot.year}</div>
          <div style={{flex:1,height:1,background:`linear-gradient(90deg,${pivot.color}35,transparent)`}} />
        </div>
        <div style={{fontSize:11,letterSpacing:"0.3em",color:"rgba(255,255,255,0.30)",marginBottom:9,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Pivot Historique</div>
        <h2 style={{fontSize:24,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif",marginBottom:22}}>{pivot.title}</h2>
        <div style={{padding:"16px 18px",borderRadius:12,background:`${pivot.color}07`,border:`1px solid ${pivot.color}22`,marginBottom:14,textAlign:"left",position:"relative"}}>
          <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:pivot.color,borderRadius:"12px 0 0 12px"}} />
          <div style={{fontSize:11,fontWeight:700,color:pivot.color,letterSpacing:"0.12em",marginBottom:7,paddingLeft:10,fontFamily:"'Syne',sans-serif"}}>LE FAIT</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",lineHeight:1.8,paddingLeft:10,fontFamily:"'DM Serif Display',serif"}}>{pivot.fact}</div>
        </div>
        {rev && (
          <div style={{padding:"12px 16px",borderRadius:10,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",marginBottom:24,textAlign:"left",animation:"fadeUp 0.4s ease-out"}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.25)",letterSpacing:"0.12em",marginBottom:5,fontFamily:"'Syne',sans-serif"}}>CONSÉQUENCE</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",lineHeight:1.7}}>{pivot.consequence}</div>
          </div>
        )}
        <button onClick={onContinue} style={{padding:"10px 26px",borderRadius:8,background:`linear-gradient(135deg,${pivot.color},${pivot.color}80)`,border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Continuer l'Odyssée ⟹</button>
      </div>
    </div>
  );
}

// ─── STRATEGY SCREEN ──────────────────────────────────────────
function StrategyScreen({ phase, heritages, onChoose }) {
  const [sel, setSel] = useState(null);
  const list = STRATEGIES[phase.id] || [];
  return (
    <div style={{position:"fixed",inset:0,zIndex:1100,background:"#040810",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px 14px",overflowY:"auto"}}>
      <div style={{maxWidth:560,width:"100%",animation:"fadeUp 0.4s ease-out"}}>
        <div style={{textAlign:"center",marginBottom:18}}>
          <div style={{fontSize:26,marginBottom:5}}>{phase.portrait}</div>
          <div style={{fontSize:11,letterSpacing:"0.3em",color:"rgba(255,255,255,0.12)",marginBottom:6,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Phase {phase.id} · {phase.era}</div>
          <div style={{fontSize:17,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif",marginBottom:5}}>{phase.strategyPrompt}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontFamily:"'DM Serif Display',serif",fontStyle:"italic",lineHeight:1.7}}>{phase.hook}</div>
        </div>
        {heritages.length > 0 && (
          <div style={{marginBottom:14,padding:"9px 11px",borderRadius:8,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.32 )",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Héritages actifs</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{heritages.map(h => (<div key={h.id} style={{display:"flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:7,background:`${h.color}08`,border:`1px solid ${h.color}20`,fontSize:11,color:h.color,fontWeight:700}}>{h.icon} {h.name}</div>))}</div>
          </div>
        )}
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {list.map(s => (
            <button key={s.id} onClick={() => setSel(s.id)} style={{padding:"12px 14px",borderRadius:10,textAlign:"left",cursor:"pointer",background:sel===s.id?`${s.color}0d`:"rgba(255,255,255,0.02)",border:sel===s.id?`1.5px solid ${s.color}40`:"1px solid rgba(255,255,255,0.06)",transition:"all 0.2s"}}>
              <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                <span style={{fontSize:18,flexShrink:0}}>{s.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:700,color:sel===s.id?s.color:"rgba(255,255,255,0.7)",fontFamily:"'Syne',sans-serif",marginBottom:2}}>{s.name}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",lineHeight:1.6,fontFamily:"'DM Serif Display',serif",marginBottom:4}}>{s.desc}</div>
                  <div style={{fontSize:12,color:s.color,background:`${s.color}0a`,padding:"2px 7px",borderRadius:4,display:"inline-block"}}>{s.effect}</div>
                </div>
                {sel === s.id && <span style={{fontSize:14,color:s.color,flexShrink:0}}>✓</span>}
              </div>
            </button>
          ))}
        </div>
        <div style={{textAlign:"center",marginTop:16}}>
          <button
            onClick={() => onChoose(list.find(s => s.id === sel) || list[0])}
            style={{padding:"10px 24px",borderRadius:7,background:sel?"linear-gradient(135deg,#4ade80,#22d3ee)":"rgba(255,255,255,0.05)",border:sel?"none":"1px solid rgba(255,255,255,0.09)",color:sel?"#fff":"rgba(255,255,255,0.25)",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}
          >
            {sel ? "Commencer avec cette stratégie ⟹" : "Choisir une stratégie"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PARALLEL WORLD MODAL — FIXED (always closeable) ──────────
function ParallelWorldModal({ world, onClose, onUnlockCard }) {
  const [step, setStep] = useState("revelation"); // "revelation" | "enigma" | "success" | "failure"
  const [selected, setSelected] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const e = world.enigma;

  useEffect(() => {
    const handler = (ev) => { if (ev.key === "Escape" && step === "revelation") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, step]);

  const handleAnswer = (idx) => {
    setSelected(idx);
    const correct = e.options[idx].correct;
    if (correct) {
      setTimeout(() => {
        onUnlockCard(e.unlock);
        setStep("success");
      }, 800);
    } else {
      setAttempts(a => a + 1);
      setTimeout(() => setStep("failure"), 800);
    }
  };

  const retry = () => { setSelected(null); setStep("enigma"); };

  const optionColor = (idx) => {
    if (selected === null) return { bg:"rgba(255,255,255,0.30)", border:"rgba(255,255,255,0.08)", text:"rgba(255,255,255,0.65)" };
    if (e.options[idx].correct) return { bg:"rgba(74,222,128,0.08)", border:"rgba(74,222,128,0.4)", text:"#4ade80" };
    if (idx === selected) return { bg:"rgba(248,113,113,0.08)", border:"rgba(248,113,113,0.4)", text:"#f87171" };
    return { bg:"rgba(255,255,255,0.02)", border:"rgba(255,255,255,0.04)", text:"rgba(255,255,255,0.25)" };
  };

  return (
    <div
      style={{position:"fixed",inset:0,zIndex:1100,background:"rgba(0,0,0,0.94)",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px 16px",backdropFilter:"blur(6px)"}}
      onClick={step === "revelation" ? onClose : undefined}
    >
      <div
        style={{maxWidth:520,width:"100%",animation:"fadeUp 0.35s ease-out",position:"relative"}}
        onClick={e => e.stopPropagation()}
      >
        {/* Header badge */}
        <div style={{textAlign:"center",marginBottom:10}}>
          <span style={{fontSize:10,letterSpacing:"0.3em",color:"rgba(255,255,255,0.12)",textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>
            ◈ Monde Parallèle — {world.name}
          </span>
        </div>

        {/* ── STEP 1: REVELATION ── */}
        {step === "revelation" && (
          <div style={{borderRadius:14,background:`${world.color}07`,border:`1.5px solid ${world.color}25`,overflow:"hidden"}}>
            <div style={{padding:"20px 20px 14px",textAlign:"center",borderBottom:`1px solid ${world.color}15`}}>
              <div style={{fontSize:36,marginBottom:8,filter:`drop-shadow(0 0 20px ${world.color})`}}>{world.bossIcon}</div>
              <div style={{fontSize:16,fontWeight:800,color:world.color,fontFamily:"'Syne',sans-serif",marginBottom:4}}>{world.subtitle}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.7,fontFamily:"'DM Serif Display',serif",fontStyle:"italic",marginBottom:10}}>{world.revelation}</div>
              <div style={{padding:"8px 12px",borderRadius:7,background:"rgba(255,255,255,0.30)",border:`1px solid ${world.color}15`,fontSize:11,color:"rgba(255,255,255,0.3)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif"}}>{world.mirror}</div>
            </div>
            <div style={{padding:"14px 20px",background:"rgba(0,0,0,0.2)"}}>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",textAlign:"center",marginBottom:10}}>
                Une carte spéciale t'attend — si tu peux répondre à une question sur ce monde.
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={onClose}
                  style={{flex:1,padding:"8px",borderRadius:7,background:"rgba(255,255,255,0.30)",border:"1px solid rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:11,fontFamily:"'Syne',sans-serif"}}>
                  Ignorer
                </button>
                <button onClick={() => setStep("enigma")}
                  style={{flex:2,padding:"8px",borderRadius:7,background:`linear-gradient(135deg,${world.color}cc,${world.color}66)`,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Syne',sans-serif"}}>
                  Relever le défi ▶
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: ENIGME ── */}
        {step === "enigma" && (
          <div style={{borderRadius:14,background:"rgba(8,8,20,0.97)",border:`1.5px solid ${world.color}30`,overflow:"hidden"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${world.color}15`,background:`${world.color}07`}}>
              <div style={{fontSize:10,fontWeight:700,color:world.color,textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:6,fontFamily:"'Syne',sans-serif"}}>Situation</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.7,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>{e.situation}</div>
            </div>
            <div style={{padding:"16px 20px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.85)",marginBottom:12,fontFamily:"'Syne',sans-serif",lineHeight:1.5}}>❓ {e.question}</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {e.options.map((opt, idx) => {
                  const c = optionColor(idx);
                  return (
                    <button key={idx} onClick={() => selected === null && handleAnswer(idx)}
                      style={{
                        padding:"10px 13px",borderRadius:8,textAlign:"left",cursor:selected === null ? "pointer" : "default",
                        background:c.bg,border:`1px solid ${c.border}`,color:c.text,
                        fontSize:11,lineHeight:1.55,fontFamily:"'DM Serif Display',serif",
                        transition:"all 0.2s",display:"flex",gap:10,alignItems:"flex-start",
                      }}>
                      <span style={{flexShrink:0,fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:10,marginTop:1,opacity:0.5}}>
                        {String.fromCharCode(65+idx)}.
                      </span>
                      <span>{opt.text}</span>
                    </button>
                  );
                })}
              </div>
              {attempts > 0 && selected === null && (
                <div style={{marginTop:10,fontSize:10,color:"rgba(255,255,255,0.32 )",textAlign:"center"}}>Tentative {attempts+1} — relis bien la situation.</div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 3A: SUCCESS ── */}
        {step === "success" && (
          <div style={{borderRadius:14,background:"rgba(4,20,12,0.97)",border:"1.5px solid rgba(74,222,128,0.3)",overflow:"hidden",textAlign:"center"}}>
            <div style={{padding:"20px 20px 14px",borderBottom:"1px solid rgba(74,222,128,0.1)"}}>
              <div style={{fontSize:11,color:"rgba(74,222,128,0.5)",textTransform:"uppercase",letterSpacing:"0.2em",fontFamily:"'Syne',sans-serif",marginBottom:6}}>Carte débloquée</div>
              <div style={{fontSize:38,marginBottom:6,filter:"drop-shadow(0 0 18px rgba(74,222,128,0.6))"}}>{e.unlock.emoji}</div>
              <div style={{fontSize:17,fontWeight:800,color:e.unlock.color,fontFamily:"'Syne',sans-serif",marginBottom:3}}>{e.unlock.name}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:10}}>{e.unlock.role}</div>
              <div style={{display:"inline-block",padding:"4px 10px",borderRadius:5,background:"rgba(74,222,128,0.07)",border:"1px solid rgba(74,222,128,0.2)",fontSize:11,color:"#4ade80",fontFamily:"'Syne',sans-serif",marginBottom:12}}>
                {e.unlock.effectDesc}
              </div>
              {selected !== null && (
                <div style={{padding:"9px 13px",borderRadius:8,background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.15)",fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.65,fontFamily:"'DM Serif Display',serif",fontStyle:"italic",textAlign:"left",marginBottom:8}}>
                  {e.options[selected]?.feedback}
                </div>
              )}
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif",lineHeight:1.6}}>
                « {e.unlock.lesson} »
              </div>
            </div>
            <div style={{padding:"12px 20px"}}>
              <button onClick={onClose}
                style={{width:"100%",padding:"9px",borderRadius:7,background:"linear-gradient(135deg,rgba(74,222,128,0.2),rgba(74,222,128,0.08))",border:"1px solid rgba(74,222,128,0.25)",color:"#4ade80",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Syne',sans-serif"}}>
                Retour à l'Odyssée ▶
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3B: FAILURE ── */}
        {step === "failure" && (
          <div style={{borderRadius:14,background:"rgba(20,4,4,0.97)",border:"1.5px solid rgba(248,113,113,0.2)",overflow:"hidden",textAlign:"center"}}>
            <div style={{padding:"20px 20px 14px",borderBottom:"1px solid rgba(248,113,113,0.1)"}}>
              <div style={{fontSize:28,marginBottom:8}}>🔁</div>
              <div style={{fontSize:14,fontWeight:700,color:"rgba(248,113,113,0.8)",fontFamily:"'Syne',sans-serif",marginBottom:8}}>Pas tout à fait…</div>
              {selected !== null && (
                <div style={{padding:"9px 13px",borderRadius:8,background:"rgba(248,113,113,0.05)",border:"1px solid rgba(248,113,113,0.15)",fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.65,fontFamily:"'DM Serif Display',serif",fontStyle:"italic",textAlign:"left",marginBottom:10}}>
                  {e.options[selected]?.feedback}
                </div>
              )}
              <div style={{fontSize:11,color:"rgba(255,255,255,0.25)"}}>Relis la situation — la réponse est dedans.</div>
            </div>
            <div style={{padding:"12px 20px",display:"flex",gap:8}}>
              <button onClick={onClose}
                style={{flex:1,padding:"8px",borderRadius:7,background:"rgba(255,255,255,0.30)",border:"1px solid rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:11,fontFamily:"'Syne',sans-serif"}}>
                Ignorer
              </button>
              <button onClick={retry}
                style={{flex:2,padding:"8px",borderRadius:7,background:`linear-gradient(135deg,${world.color}aa,${world.color}55)`,border:"none",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Syne',sans-serif"}}>
                Réessayer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── IMPOSED CARD NOTIF ───────────────────────────────────────
function ImposedCardNotif({ card, onDismiss }) {
  useEffect(() => {
    audio.playImposed();
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  return (
    <div style={{position:"fixed",top:56,right:12,zIndex:700,maxWidth:270,animation:"slideRight 0.3s ease-out"}}>
      <div style={{padding:"11px 13px",borderRadius:10,background:"linear-gradient(135deg,#1a0005,#0f000a)",border:"1.5px solid rgba(220,38,38,0.35)",boxShadow:"0 8px 28px rgba(220,38,38,0.18)"}}>
        <div style={{fontSize:11,fontWeight:700,color:"#ef4444",textTransform:"uppercase",letterSpacing:"0.15em",marginBottom:5,fontFamily:"'Syne',sans-serif"}}>⚠ Carte imposée</div>
        <div style={{display:"flex",gap:7,alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:22}}>{card.emoji}</span>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"#ef4444",fontFamily:"'Syne',sans-serif"}}>{card.name}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.25)"}}>{card.role}</div>
          </div>
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",lineHeight:1.6,marginBottom:5}}>{card.imposedNote}</div>
        <button onClick={onDismiss} style={{width:"100%",padding:"3px",borderRadius:4,background:"rgba(220,38,38,0.07)",border:"1px solid rgba(220,38,38,0.18)",color:"rgba(255,255,255,0.35)",fontSize:11,cursor:"pointer"}}>Compris</button>
      </div>
    </div>
  );
}

// ─── NARRATIVE BANNER ─────────────────────────────────────────
function NarrativeBanner({ text, color, side, triggerKey }) {
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!text) return;
    setMsg(text);
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 4200);
    return () => clearTimeout(t);
  }, [triggerKey, text]);

  if (!visible || !msg) return null;
  const boss = side === "boss";
  return (
    <div style={{position:"fixed",top:50,left:"50%",transform:"translateX(-50%)",zIndex:500,maxWidth:500,width:"calc(100% - 20px)",animation:"narrativeIn 0.3s ease-out",pointerEvents:"none"}}>
      <div style={{padding:"7px 11px",borderRadius:8,background:boss?"rgba(8,0,0,0.95)":"rgba(0,7,3,0.95)",border:`1px solid ${boss?"#f8717120":color+"20"}`,backdropFilter:"blur(12px)",display:"flex",gap:7,alignItems:"center",boxShadow:"0 6px 24px rgba(0,0,0,0.8)"}}>
        <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.2em",color:boss?"#f87171":color,flexShrink:0,writingMode:"vertical-rl",transform:"rotate(180deg)",opacity:0.6}}>{boss?"BOSS":"CLAN"}</div>
        <div style={{width:1,height:20,background:boss?"rgba(248,113,113,0.15)":color+"18",flexShrink:0}} />
        <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",lineHeight:1.5,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>« {msg} »</div>
      </div>
    </div>
  );
}

// ─── LESSON POPUP ─────────────────────────────────────────────
function LessonPopup({ card, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  if (!card?.lesson) return null;
  return (
    <div style={{position:"fixed",bottom:126,left:"50%",transform:"translateX(-50%)",zIndex:600,maxWidth:420,width:"calc(100% - 28px)",animation:"fadeUp 0.25s ease-out"}} onClick={onClose}>
      <div style={{padding:"9px 11px",borderRadius:9,background:`linear-gradient(135deg,${card.color}0c,rgba(0,0,0,0.96))`,border:`1px solid ${card.color}20`,backdropFilter:"blur(10px)",display:"flex",gap:7,alignItems:"flex-start",cursor:"pointer"}}>
        <span style={{fontSize:15,flexShrink:0}}>{card.emoji}</span>
        <div style={{flex:1}}>
          <div style={{fontSize:11,fontWeight:700,color:card.color,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:2}}>{card.imposed?"Analyse":"Leçon"} · {card.name}</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.65,fontFamily:"'DM Serif Display',serif"}}>{card.lesson}</div>
        </div>
        <span style={{fontSize:11,color:"rgba(255,255,255,0.1)",flexShrink:0}}>×</span>
      </div>
    </div>
  );
}

// ─── UNISON FLASH ─────────────────────────────────────────────
function UnisonFlash({ combo, onDone }) {
  useEffect(() => {
    audio.playUnison();
    const t = setTimeout(onDone, 3800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div onClick={onDone} style={{position:"fixed",inset:0,zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",background:combo.dark?"rgba(8,0,0,0.97)":"rgba(0,1,10,0.97)",animation:"fadeIn 0.2s"}}>
      <div style={{textAlign:"center",maxWidth:380,padding:"0 20px"}} onClick={e => e.stopPropagation()}>
        <div style={{fontSize:56,marginBottom:9,animation:"pulse 0.8s ease-in-out infinite",filter:`drop-shadow(0 0 32px ${combo.color})`}}>{combo.icon}</div>
        <div style={{fontSize:11,fontWeight:700,color:`${combo.color}55`,textTransform:"uppercase",letterSpacing:"0.3em",marginBottom:7}}>⚡ UNISON</div>
        <div style={{fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif",marginBottom:4}}>{combo.name}</div>
        <div style={{fontSize:12,color:combo.color,marginBottom:11}}>{combo.bonus}</div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif",marginBottom:13,lineHeight:1.7}}>« {combo.narrative} »</div>
        <button onClick={onDone} style={{padding:"6px 17px",borderRadius:6,background:`${combo.color}12`,border:`1px solid ${combo.color}28`,color:combo.color,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Continuer ▶</button>
      </div>
    </div>
  );
}

// ─── GAME OVER ────────────────────────────────────────────────
function GameOverScreen({ phase, onRestart }) {
  const [show, setShow] = useState(false);
  useEffect(() => { audio.playOver(); const t = setTimeout(() => setShow(true), 500); return () => clearTimeout(t); }, []);
  return (
    <div style={{position:"fixed",inset:0,zIndex:1200,background:"rgba(1,0,0,0.98)",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn 0.4s"}}>
      <div style={{textAlign:"center",maxWidth:360,padding:"0 22px"}}>
        <div style={{fontSize:50,marginBottom:10,filter:"drop-shadow(0 0 20px #f87171)"}}>💀</div>
        <div style={{fontSize:11,letterSpacing:"0.3em",color:"rgba(255,255,255,0.12)",marginBottom:6,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Ton clan s'est effondré</div>
        <h2 style={{fontSize:20,fontWeight:800,color:"#f87171",fontFamily:"'Syne',sans-serif",marginBottom:11}}>{phase.bossName} t'a vaincu</h2>
        {show && (
          <div style={{animation:"fadeUp 0.4s ease-out"}}>
            <div style={{padding:"11px 13px",borderRadius:9,background:"rgba(248,113,113,0.04)",border:"1px solid rgba(248,113,113,0.09)",marginBottom:18,fontSize:11,color:"rgba(255,255,255,0.28)",lineHeight:1.8,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>« L'histoire ne se souvient que de ceux qui ont su relever la tête. »</div>
            <button onClick={onRestart} style={{padding:"10px 22px",borderRadius:7,background:"linear-gradient(135deg,#f87171,#ef4444)",border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Recommencer l'Odyssée</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FINAL SCREEN ─────────────────────────────────────────────
function FinalScreen({ allHeritages, profileVotes, stats, onRestart }) {
  const counts = {};
  profileVotes.forEach(p => { counts[p] = (counts[p] || 0) + 1; });
  const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "lucidite";
  const profile = PROFILES[dominant] || PROFILES.lucidite;
  const [stage, setStage] = useState(0);
  useEffect(() => { audio.playVictory(); }, []);
  useEffect(() => {
    if (stage < 5) { const t = setTimeout(() => setStage(s => s + 1), 900); return () => clearTimeout(t); }
  }, [stage]);
  const pos = allHeritages.filter(h => h.type === "bonus");
  const neg = allHeritages.filter(h => h.type !== "bonus");
  return (
    <div style={{position:"fixed",inset:0,background:"#020509",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 16px",overflowY:"auto"}}>
      <div style={{maxWidth:560,width:"100%",textAlign:"center"}}>
        {stage >= 0 && <div style={{fontSize:56,marginBottom:10,filter:`drop-shadow(0 0 28px ${profile.color}50)`,animation:"pulse 3s ease-in-out infinite"}}>{profile.icon}</div>}
        {stage >= 1 && <div style={{animation:"fadeUp 0.5s ease-out"}}><div style={{fontSize:11,letterSpacing:"0.3em",color:"rgba(255,255,255,0.1)",marginBottom:5,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Ton Profil Économique</div><h1 style={{fontSize:24,fontWeight:800,color:profile.color,fontFamily:"'Syne',sans-serif",marginBottom:13}}>{profile.name}</h1></div>}
        {stage >= 2 && (
          <div style={{display:"flex",gap:6,justifyContent:"center",marginBottom:16,flexWrap:"wrap",animation:"fadeUp 0.5s ease-out"}}>
            {[["Héritages +",pos.length,"#4ade80"],["Héritages −",neg.length,"#f87171"],["Combos",stats.combosUsed||0,"#fbbf24"],["Leçons",stats.lessons||0,"#a78bfa"]].map(([l,v,c]) => (
              <div key={l} style={{padding:"7px 10px",borderRadius:7,background:`${c}07`,border:`1px solid ${c}12`,textAlign:"center"}}>
                <div style={{fontSize:16,fontWeight:800,color:"#fff",fontFamily:"'JetBrains Mono',monospace"}}>{v}</div>
                <div style={{fontSize:11,color:`${c}55`,fontFamily:"'Syne',sans-serif"}}>{l}</div>
              </div>
            ))}
          </div>
        )}
        {stage >= 2 && <div style={{padding:"12px 15px",borderRadius:11,background:`${profile.color}06`,border:`1px solid ${profile.color}14`,marginBottom:16,animation:"fadeUp 0.5s ease-out"}}><div style={{fontSize:11,color:"rgba(255,255,255,0.45)",lineHeight:1.8,fontFamily:"'DM Serif Display',serif"}}>{profile.desc}</div></div>}
        {stage >= 3 && allHeritages.length > 0 && (
          <div style={{marginBottom:16,animation:"fadeUp 0.5s ease-out"}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.18)",letterSpacing:"0.13em",marginBottom:8,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Ton Héritage Complet</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",justifyContent:"center"}}>
              {allHeritages.map(h => (<div key={h.id} style={{display:"flex",alignItems:"center",gap:3,padding:"3px 7px",borderRadius:7,background:`${h.color}08`,border:`1px solid ${h.color}18`,fontSize:11,color:h.color,fontWeight:700}}>{h.icon} {h.name}</div>))}
            </div>
          </div>
        )}
        {stage >= 4 && <div style={{marginBottom:16,animation:"fadeUp 0.5s ease-out"}}><div style={{fontSize:13,color:profile.color,fontFamily:"'DM Serif Display',serif",fontStyle:"italic",lineHeight:1.8,padding:"11px 14px",borderRadius:9,background:`${profile.color}07`,border:`1px solid ${profile.color}16`}}>« {profile.question} »</div></div>}
        {stage >= 4 && <div style={{marginBottom:22,padding:"11px 14px",borderRadius:9,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.04)",animation:"fadeUp 0.5s ease-out"}}><div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.18)",letterSpacing:"0.12em",marginBottom:7,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Ce que ce jeu voulait dire</div><div style={{fontSize:11,color:"rgba(255,255,255,0.38)",lineHeight:1.8,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>Tu n'as pas joué un jeu de stratégie. Tu as vécu une démonstration. Chaque mécanique que tu as ressentie — la carte imposée, l'héritage inévitable, le boss qui grossit quand tu n'agis pas — existe dans l'économie réelle.</div></div>}
        {stage >= 5 && <button onClick={onRestart} style={{padding:"10px 22px",borderRadius:7,background:"linear-gradient(135deg,#4ade80,#22d3ee)",border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Rejouer l'Odyssée</button>}
      </div>
    </div>
  );
}

// ─── CINEMATIC ────────────────────────────────────────────────
function Cinematic({ phase, heritages, onStart }) {
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const current = phase.lines[lineIdx] || "";
  const rule = PHASE_RULES[phase.id];
  const [showSkip, setShowSkip] = useState(false);

  useEffect(() => { audio.init(); audio.playPhaseAmbience(phase.id); }, [phase.id]);

  useEffect(() => {
    const timer = setTimeout(() => setShowSkip(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (charIdx >= current.length) return;
    const t = setTimeout(() => setCharIdx(c => c + 1), 17);
    return () => clearTimeout(t);
  }, [charIdx, current]);

  useEffect(() => {
    audio.init();
    audio.playPhaseAmbience(phase.id);
    // Nettoyage : on arrête l'ambiance à la sortie du cinématique
    //return () => audio.stopBg();
  }, [phase.id]);

  const advance = () => {
    if (charIdx < current.length) { setCharIdx(current.length); return; }
    if (lineIdx < phase.lines.length - 1) { setLineIdx(l => l + 1); setCharIdx(0); }
    else onStart();
  };

  const skipCinematic = () => {
    onStart();
  };



  return (
    <div 
      style={{
        position:"fixed",
        inset:0,
        zIndex:1000,
        background:"#030609",
        fontFamily:"'Syne',sans-serif",
        display:"flex",
        flexDirection:"column",
        cursor:"pointer"}} 
        onClick={advance}
    >
        {showSkip && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // important pour ne pas déclencher advance
              skipCinematic();
            }}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 1010,
              padding: "10px 18px",
              background: "rgba(0,0,0,0.7)",
              border: "1px solid rgba(255,255,255,0.30)",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
              backdropFilter: "blur(8px)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(0,0,0,0.7)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.30)";
            }}
          >
            ⏭️ Passer la cinématique
          </button>
        )}

      <div style={{
        flex:1,
        display:"grid",
        gridTemplateColumns: window.innerWidth > 600 ? "1fr 1fr" : "1fr",
        gridTemplateRows: window.innerWidth > 600 ? "1fr" : "auto 1fr",
        minHeight:0
      }}>
        <div style={{
      display: "flex", flexDirection: "row",
      alignItems: "center", gap: 12,
      padding: "16px 16px 10px",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      background: `radial-gradient(ellipse at 30% 50%,${phase.color}0a,transparent 65%)`,
    }}>
      <div style={{ fontSize: 42, filter: `drop-shadow(0 0 24px ${phase.color}44)`, animation: "float 4s ease-in-out infinite", flexShrink: 0 }}>{phase.portrait}</div>
      <div>
        <div style={{ fontSize: 10, letterSpacing: "0.3em", color: phase.color, textTransform: "uppercase", opacity: 0.6, marginBottom: 2 }}>Phase {phase.id}/7 · {phase.era}</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>{phase.era}</div>
        {rule && <div style={{ marginTop: 6, padding: "4px 8px", borderRadius: 6, background: `${phase.color}07`, border: `1px solid ${phase.color}15`, display: "inline-block" }}><span style={{ fontSize: 10, fontWeight: 700, color: phase.color }}>{rule.icon} {rule.label}</span></div>}
        {heritages.length > 0 && heritages.map(h => (
          <div key={h.id} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 6px", borderRadius: 5, background: `${h.color}08`, border: `1px solid ${h.color}15`, marginLeft: 4, fontSize: 10, color: h.color, fontWeight: 700 }}>{h.icon} {h.name}</div>
        ))}
      </div>
    </div>
        <div style={{display:"flex",flexDirection:"column",justifyContent:"center",padding:"32px 28px",background:"linear-gradient(135deg,rgba(2,5,14,0.95),rgba(1,2,8,0.99))"}}>
          <div style={{fontSize:11,fontWeight:700,color:phase.color,textTransform:"uppercase",letterSpacing:"0.3em",marginBottom:14,opacity:0.6}}>Narratif · Phase {phase.id}</div>
          <div style={{fontSize:14,color:"rgba(255,255,255,0.7)",lineHeight:1.9,fontFamily:"'DM Serif Display',serif",marginBottom:18,minHeight:80}}>
            {current.slice(0, charIdx)}
            {charIdx < current.length && <span style={{animation:"blink 0.6s step-end infinite",borderRight:`2px solid ${phase.color}`,marginLeft:2}}>&nbsp;</span>}
          </div>
          <div style={{display:"flex",gap:3,marginBottom:20}}>{phase.lines.map((_,i) => (<div key={i} style={{height:2,flex:1,borderRadius:1,background:i<lineIdx?phase.color:i===lineIdx?`${phase.color}60`:"rgba(255,255,255,0.05)",transition:"background 0.4s"}} />))}</div>
          <div style={{padding:"10px 12px",borderRadius:8,background:`${phase.color}06`,border:`1px solid ${phase.color}12`,marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.25)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:3}}>Ton rôle</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",lineHeight:1.65,fontFamily:"'DM Serif Display',serif"}}>{phase.playerRole}</div>
          </div>
          <div style={{padding:"8px 11px",borderRadius:7,background:"rgba(255,255,255,0.01)",border:"1px solid rgba(255,255,255,0.04)"}}>
            <div style={{fontSize:11,fontWeight:700,color:phase.accent,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:3,opacity:0.5}}>⚠ Effet domino</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",fontStyle:"italic",fontFamily:"'DM Serif Display',serif"}}>{phase.reactionLabel}</div>
          </div>
        </div>
      </div>
      <div style={{padding:"10px 20px",borderTop:"1px solid rgba(255,255,255,0.04)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.12)"}}>vs. {phase.bossIcon} {phase.bossName} — {phase.bossDesc}</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:5,padding:"4px 10px",fontFamily:"'JetBrains Mono',monospace"}}>{lineIdx < phase.lines.length - 1 ? "Clic pour continuer ▶" : "Commencer la bataille ▶"}</div>
      </div>
    </div>
  );
}

// ─── VICTORY SCREEN ───────────────────────────────────────────
function VictoryScreen({ phase, phaseStats, onContinue }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 400); return () => clearTimeout(t); }, []);
  const dr = phaseStats.darkPlayed / Math.max(1, phaseStats.darkPlayed + phaseStats.lightPlayed);
  const tone = dr > 0.6 ? "sombre" : dr < 0.3 ? "lumineux" : "équilibré";
  const tc = dr > 0.6 ? "#f87171" : dr < 0.3 ? "#4ade80" : "#fbbf24";
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,1,5,0.98)",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn 0.4s",zIndex:900}}>
      <div style={{textAlign:"center",maxWidth:400,padding:"0 22px"}}>
        <div style={{fontSize:46,marginBottom:10,filter:`drop-shadow(0 0 24px ${phase.color}60)`,animation:"float 3s ease-in-out infinite"}}>{phase.portrait}</div>
        <div style={{fontSize:11,letterSpacing:"0.3em",color:"rgba(255,255,255,0.12)",marginBottom:6,textTransform:"uppercase",fontFamily:"'Syne',sans-serif"}}>Phase {phase.id} terminée</div>
        <h2 style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:"'Syne',sans-serif",marginBottom:5}}>{phase.bossName} vaincu</h2>
        {show && (
          <div style={{animation:"fadeUp 0.5s ease-out"}}>
            <div style={{padding:"10px 13px",borderRadius:9,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginBottom:14,display:"flex",justifyContent:"center",gap:10,flexWrap:"wrap"}}>
              {[["Combos",phaseStats.combosUsed,"#fbbf24"],["Mémoire",phaseStats.memoire||0,"#a78bfa"],["Justice",phaseStats.justice||0,"#38bdf8"],["Coopération",phaseStats.coopPlayed,"#4ade80"]].map(([l,v,c]) => (
                <div key={l} style={{textAlign:"center"}}>
                  <div style={{fontSize:16,fontWeight:800,color:"#fff",fontFamily:"'JetBrains Mono',monospace"}}>{v}</div>
                  <div style={{fontSize:11,color:`${c}55`}}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{padding:"8px 12px",borderRadius:8,background:`${tc}08`,border:`1px solid ${tc}20`,marginBottom:14}}>
              <div style={{fontSize:11,color:tc,fontWeight:700,marginBottom:3}}>Ton jeu a été {tone}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",fontFamily:"'DM Serif Display',serif",lineHeight:1.6}}>{phase.reactionLabel}</div>
            </div>
            <button onClick={onContinue} style={{padding:"10px 22px",borderRadius:7,background:`linear-gradient(135deg,${phase.color},${phase.color}80)`,border:"none",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif"}}>Voir l'héritage ⟹</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── useGame HOOK ─────────────────────────────────────────────
function useGame(phaseData, activeHeritages, strategy) {
  const phase = phaseData;

  const finalBossHP = useMemo(() => {
    const hpMod = activeHeritages.reduce((a, h) => {
      if (h.bossHPExtra) a += h.bossHPExtra;
      if (h.bossHPMinus) a -= h.bossHPMinus;
      return a;
    }, 0);
    const stratMod = strategy?.bonus?.type === "revolt"
      ? -(strategy.bonus.bossHPMinus || 0)
      : strategy?.bonus?.type === "bossReduce"
      ? -Math.floor((phase.bossHp + hpMod) * strategy.bonus.pct)
      : 0;
    return Math.max(10, phase.bossHp + hpMod + stratMod);
  }, []); // eslint-disable-line

  const startPlayerHP = useMemo(() => {
    let hp = 30;
    if (strategy?.bonus?.type === "survival") hp += strategy.bonus.hp || 0;
    if (strategy?.bonus?.type === "outsideSystem") hp += strategy.bonus.hp || 0;
    return hp;
  }, []); // eslint-disable-line

  // FIX MANA: single source of truth — maxMana is a fixed number per phase
  // heritage mana bonus is applied at refill, not baked into maxMana
  const manaBonus = useMemo(() =>
    activeHeritages.reduce((a, h) => a + (h.manaBonus || 0), 0) +
    (strategy?.bonus?.type === "manaPlus" ? (strategy.bonus.mana || 2) : 0) +
    (strategy?.bonus?.type === "techBonus" ? (strategy.bonus.mana || 2) : 0)
  , []); // eslint-disable-line

  const effectiveMaxMana = INITIAL_MAX_MANA + manaBonus;

  const behaviorATKPenalties = useMemo(() => {
    return activeHeritages.reduce((a, h) => {
      if (h.behaviorATKPenalty) Object.assign(a, h.behaviorATKPenalty);
      return a;
    }, {});
  }, []); // eslint-disable-line

  const buildDeck = useCallback(() => {
    const base = [...phase.playerCards];
    const imposedCards = [];
    activeHeritages.forEach(h => {
      if (h.imposedCard && IMPOSED_CARDS[h.imposedCard]) {
        imposedCards.push({ ...IMPOSED_CARDS[h.imposedCard], fromHeritage: h.id });
      }
    });
    if (strategy?.bonus?.imposedCard && IMPOSED_CARDS[strategy.bonus.imposedCard]) {
      imposedCards.push({ ...IMPOSED_CARDS[strategy.bonus.imposedCard], fromStrategy: true });
    }
    return shuffle([...base, ...base, ...imposedCards]);
  }, [phase, activeHeritages, strategy]);

  const [playerHP, setPlayerHP] = useState(startPlayerHP);
  // FIX MANA: start mana = effectiveMaxMana, no double addition
  const [mana, setMana] = useState(effectiveMaxMana);
  const [playerField, setPlayerField] = useState([]);
  const [hand, setHand] = useState([]);
  const [turn, setTurn] = useState(1);
  const [combos, setCombos] = useState([]);
  const [floats, setFloats] = useState([]);
  const [lesson, setLesson] = useState(null);
  const [unisonCombo, setUnisonCombo] = useState(null);
  const [narrative, setNarrative] = useState({ text: "", side: "clan", key: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [corruption, setCorruption] = useState(0);
  const [ethique, setEthique] = useState(6);
  const [lastCardPlayed, setLastCardPlayed] = useState(null);
  const [lastCombo, setLastCombo] = useState(null);
  const [bossATKBonus] = useState(() =>
    activeHeritages.reduce((a, h) => a + (h.bossStartATKBonus || 0), 0) +
    (strategy?.bonus?.type === "reformBonus" ? -(strategy.bonus.bossATKMinus || 0) : 0)
  );
  const [immunityTurns, setImmunityTurns] = useState(() =>
    activeHeritages.reduce((a, h) => a + (h.startImmunityTurns || 0), 0)
  );
  const [imposedNotif, setImposedNotif] = useState(null);
  const [collectedLessonsMap, setCollectedLessonsMap] = useState({});
  const [phaseStats, setPhaseStats] = useState({
    confiance: 0, solidarite: 0, justice: 0, regulation: 0,
    memoire: 0, coopPlayed: 0, combosUsed: 0,
    darkPlayed: 0, lightPlayed: 0,
    syndicatPlayed: false, regulationPlayed: 0
  });

  const bossHPRef = useRef(finalBossHP);
  const [bossHPDisplay, setBossHPDisplay] = useState(finalBossHP);
  const processingRef = useRef(false);
  const deckRef = useRef([]);

  const addFloat = useCallback((text, color, x = "50%", y = "40%") => {
    const id = Date.now() + Math.random();
    setFloats(f => [...f, { id, text, color, x, y }]);
    const timer = setTimeout(() => setFloats(f => f.filter(t => t.id !== id)), 1200);
    return () => clearTimeout(timer);
  }, []);

  const drawCards = useCallback((n = 1) => {
    const drawn = [];
    for (let i = 0; i < n; i++) {
      if (deckRef.current.length === 0) deckRef.current = buildDeck();
      drawn.push(deckRef.current.shift());
    }
    setHand(h => [...h, ...drawn].slice(0, 7));
    return drawn;
  }, [buildDeck]);

  useEffect(() => {
    deckRef.current = buildDeck();
    const startCount = 4
      + (strategy?.bonus?.type === "exile" ? (strategy.bonus.extraCards || 0) : 0)
      + (activeHeritages.find(h => h.extraStartCards)?.extraStartCards || 0);
    const drawn = deckRef.current.splice(0, startCount);
    setHand(drawn);
    const imp = drawn.find(c => c.imposed);
    if (imp) setImposedNotif(imp);
    spawnEnemy();
  }, []); // eslint-disable-line

  const [enemyField, setEnemyField] = useState([]);

  const spawnEnemy = useCallback(() => {
    const c = phase.enemyCards;
    if (!c.length) return;
    const card = { ...c[Math.floor(Math.random() * c.length)], uid: Date.now() + Math.random() };
    setEnemyField([card]);
  }, [phase]);

  const getCardCost = useCallback((card, currentTurn) => {
    let cost = card.cost || 0;
    const extraCost = activeHeritages.reduce((a, h) => a + (h.cardCostExtra || 0), 0);
    if (strategy?.bonus?.type === "trustLoop" && card.behavior === "confiance") return 0;
    if (strategy?.bonus?.type === "freeCoop" && currentTurn <= (strategy.bonus.turns || 3) &&
        (card.behavior === "confiance" || card.behavior === "solidarite")) return 0;
    return Math.max(0, cost + extraCost);
  }, [activeHeritages, strategy]);

  const getCardATK = useCallback((card) => {
    let atk = card.atk;
    if (behaviorATKPenalties[card.behavior]) atk = Math.max(0, atk + behaviorATKPenalties[card.behavior]);
    if (strategy?.bonus?.type === "atkBehavior" && card.behavior === strategy.bonus.behavior) atk += strategy.bonus.value;
    return atk;
  }, [behaviorATKPenalties, strategy]);

  const getBossATK = useCallback((currentTurn) => {
    let atk = BOSS_BASE_ATK + bossATKBonus;
    if (phase.id === 5) atk += (currentTurn - 1);
    return atk;
  }, [bossATKBonus, phase.id]);

  const applyDamageToBoss = useCallback((atk, card, currentField) => {
    let dmg = atk;
    if (phase.id === 6) {
      const behaviors = currentField.map(c => c.behavior);
      const hasProtection = behaviors.includes("justice") || behaviors.includes("regulation");
      const red = activeHeritages.find(h => h.immunityReduction)?.immunityReduction || 0;
      if (!hasProtection) dmg = Math.max(0, dmg - (3 - red));
    }
    if (activeHeritages.find(h => h.id === "cadre_regulatoire") && card.behavior === "justice") dmg += 2;
    if (strategy?.bonus?.type === "pressBonus" && card.behavior === "justice") dmg += strategy.bonus.value;
    if (dmg > 0) {
      audio.playDmg();
      bossHPRef.current = Math.max(0, bossHPRef.current - dmg);
      setBossHPDisplay(bossHPRef.current);
    }
    return dmg;
  }, [phase.id, activeHeritages, strategy]);

  const playCard = useCallback((card) => {
    if (processingRef.current) return;
    const cost = getCardCost(card, turn);
    if (cost > mana) return;

    processingRef.current = true;
    setIsProcessing(true);
    audio.playCard();
    setLastCardPlayed({ ...card, _t: Date.now() });

    setHand(h => h.filter(c => c.id !== card.id));
    setMana(m => m - cost);

    const isDark = DARK_BEHAVIORS.has(card.behavior);
    const cardATK = getCardATK(card);

    setPhaseStats(s => ({
      ...s,
      [card.behavior]: (s[card.behavior] || 0) + 1,
      darkPlayed: isDark ? s.darkPlayed + 1 : s.darkPlayed,
      lightPlayed: !isDark ? s.lightPlayed + 1 : s.lightPlayed,
      syndicatPlayed: card.id === "syndicat" || s.syndicatPlayed,
      coopPlayed: card.behavior === "cooperation" ? s.coopPlayed + 1 : s.coopPlayed,
      regulationPlayed: card.behavior === "regulation" ? s.regulationPlayed + 1 : s.regulationPlayed,
    }));

    if (isDark) setCorruption(c => c + 1);
    else setEthique(e => Math.min(10, e + 0.5));

    if (card.lesson) {
      setLesson(card);
      setCollectedLessonsMap(prev => {
        if (prev[card.id]) return prev;
        return { ...prev, [card.id]: { id: card.id, name: card.name, emoji: card.emoji, color: card.color, lesson: card.lesson } };
      });
    }

    setPlayerField(prevField => {
      const newField = [...prevField, { ...card, uid: Date.now() }];

      if (card.behavior === "solidarite" || card.behavior === "confiance") {
        setPlayerHP(h => Math.min(PLAYER_HP_CAP, h + 4));
        audio.playHeal();
        addFloat("+4 PV", "#4ade80", "55%", "60%");
      }
      if (card.behavior === "memoire") {
        drawCards(2);
        addFloat("+2 cartes", "#a78bfa", "45%", "65%");
      }
      if (card.behavior === "production") {
        // FIX MANA: cap mana at effectiveMaxMana
        setMana(m => Math.min(effectiveMaxMana, m + 1));
        addFloat("+1 Mana", "#fbbf24", "50%", "62%");
      }
      if (card.behavior === "monnaie" && phase.id === 2) {
        setMana(m => Math.min(effectiveMaxMana + 2, m + 2));
      }

      if (cardATK > 0) {
        const dmg = applyDamageToBoss(cardATK, card, newField);
        if (dmg > 0) addFloat(`-${dmg} HP ennemi`, "#f87171", "50%", "45%");
      }

      if (card.imposed) {
        setPlayerHP(h => Math.max(0, h - 1));
        addFloat("Coût de reproduction -1", "#f87171", "50%", "55%");
      }

      const behaviors = newField.map(c => c.behavior);
      const active = checkCombos(behaviors);
      if (active.length) {
        audio.playCombo();
        const newCombos = active.filter(c => !combos.find(e => e.id === c.id));
        setCombos(active);
        setPhaseStats(s => ({ ...s, combosUsed: s.combosUsed + newCombos.length }));
        newCombos.forEach(c => {
          setLastCombo({ ...c, _t: Date.now() }); // ← AJOUTER CETTE LIGNE
          if (c.unison) {
            setUnisonCombo(c);
          } else if (!c.dark) {
            setPlayerHP(h => Math.min(PLAYER_HP_CAP, h + 4));
            audio.playHeal();
            addFloat(`✦ ${c.name}`, "#4ade80", "50%", "35%");
          }
          if (c.dark) {
            const bd = applyDamageToBoss(10, card, newField);
            if (bd > 0) addFloat(`☠ ${c.name} -${bd}`, "#f87171", "50%", "35%");
          }
        });
      }

      return newField;
    });

    setTimeout(() => {
      processingRef.current = false;
      setIsProcessing(false);
    }, 100);
  }, [turn, mana, combos, getCardCost, getCardATK, applyDamageToBoss, drawCards, addFloat, effectiveMaxMana, phase.id]);

  const endTurn = useCallback(() => {
    if (processingRef.current || playerHP <= 0) return;

    const nextTurn = turn + 1;
    setTurn(nextTurn);

    setPlayerField(currentField => {
      if (phase.id === 3 && !currentField.some(c => c.behavior === "production")) {
        setMana(m => Math.max(0, m - 1));
        addFloat("Dîme -1 Mana", "#f97316", "50%", "50%");
      }
      if (phase.id === 4) {
        const darkCount = currentField.filter(c => DARK_BEHAVIORS.has(c.behavior)).length;
        if (darkCount > 0) {
          setPlayerHP(h => Math.max(0, h - darkCount * 2));
          addFloat(`Intérêts dette -${darkCount * 2} PV`, "#f87171", "50%", "45%");
        }
      }
      return currentField;
    });

    const taxDrain = activeHeritages.reduce((a, h) => a + (h.bossHPPerTurn || 0), 0)
      + (strategy?.bonus?.type === "taxBonus" ? (strategy.bonus.bossHPPerTurn || 0) : 0);
    if (taxDrain > 0) {
      bossHPRef.current = Math.max(0, bossHPRef.current - taxDrain);
      setBossHPDisplay(bossHPRef.current);
      addFloat(`Taxe -${taxDrain}`, "#38bdf8", "60%", "50%");
    }

    const bossATK = getBossATK(nextTurn - 1);
    if (immunityTurns > 0) {
      setImmunityTurns(i => i - 1);
      addFloat("Immunité !", "#38bdf8", "50%", "50%");
    } else {
      setPlayerField(currentField => {
        const totalDef = currentField.reduce((a, c) => a + c.def, 0);
        const dmgToPlayer = Math.max(0, bossATK - Math.floor(totalDef / 2));
        if (dmgToPlayer > 0) {
          setPlayerHP(h => Math.max(0, h - dmgToPlayer));
          audio.playDmg();
          addFloat(`Boss -${dmgToPlayer} PV`, "#f87171", "45%", "55%");
          const nIdx = Math.floor((1 - (bossHPRef.current / finalBossHP)) * phase.bossDialogues.length);
          const dlg = phase.bossDialogues[Math.min(nIdx, phase.bossDialogues.length - 1)];
          if (dlg) setNarrative({ text: dlg.text, side: dlg.side, key: Date.now() });
        }
        return currentField;
      });
    }

    if (Math.random() < 0.4 && bossHPRef.current > 0) spawnEnemy();

    // Draw to fill hand
    setHand(h => {
      const needed = Math.min(2, 7 - h.length);
      if (needed > 0) {
        const drawn = [];
        for (let i = 0; i < needed; i++) {
          if (deckRef.current.length === 0) deckRef.current = buildDeck();
          drawn.push(deckRef.current.shift());
        }
        return [...h, ...drawn].slice(0, 7);
      }
      return h;
    });

    // FIX MANA: refill to effectiveMaxMana exactly — no double bonus
    setMana(effectiveMaxMana);
  }, [turn, playerHP, immunityTurns, activeHeritages, strategy, phase, getBossATK, finalBossHP, spawnEnemy, addFloat, effectiveMaxMana, buildDeck]);
  
  const addCardToHand = useCallback((card) => {
    setHand(prev => [...prev, { ...card, uid: Date.now() + Math.random() }].slice(0, 7));
  }, []);

  const resetField = useCallback(() => {
    if (mana < 1) return;
    setPlayerField([]);
    setEnemyField([]);
    setCombos([]);
    setMana(m => m - 1);
    setHand(h => {
      const needed = Math.min(2, 7 - h.length);
      const drawn = [];
      for (let i = 0; i < needed; i++) {
        if (deckRef.current.length === 0) deckRef.current = buildDeck();
        drawn.push(deckRef.current.shift());
      }
      return [...h, ...drawn].slice(0, 7);
    });
    spawnEnemy();
  }, [mana, spawnEnemy, buildDeck]);

  const bossDefeated = bossHPRef.current <= 0;
  const playerDefeated = playerHP <= 0;
  const collectedLessons = useMemo(() => Object.values(collectedLessonsMap), [collectedLessonsMap]);

  return {
    playerHP, mana, maxMana: effectiveMaxMana, playerField, enemyField, hand, addCardToHand, turn,
    combos, floats, lesson, setLesson, unisonCombo, setUnisonCombo, addFloat,
    narrative, isProcessing, corruption, ethique, phaseStats,
    bossHP: finalBossHP, bossHPDisplay, imposedNotif, setImposedNotif,
    collectedLessons, playCard, endTurn, resetField, drawCards,
    getCardCost, getCardATK, bossDefeated, playerDefeated, lastCardPlayed, lastCombo,
  };
}

function SidebarDrawer({ open, onClose, isMobile, p, rule, strategy, heritages, G, setGuideOpen, setParallelOpen }) {
  if (!open) return null;

  const content = (
    <div style={{
      width: isMobile ? "80vw" : "100%",
      maxWidth: isMobile ? 300 : "100%",
      height: "100%",
      background: isMobile ? "rgba(5,7,16,0.98)" : "rgba(0,0,0,0.25)",
      borderLeft: "1px solid rgba(255,255,255,0.06)",
      display: "flex", flexDirection: "column",
      overflowY: "auto", overflowX: "hidden",
    }}>
      {isMobile && (
        <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Infos de phase</span>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 6, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
      )}

      {/* Règle */}
      <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.25)", marginBottom: 6 }}>Règle de phase</div>
        {rule && <div style={{ padding: "6px 8px", borderRadius: 6, background: `${p.color}07`, border: `1px solid ${p.color}15` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: p.color }}>{rule.icon} {rule.label}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 2, lineHeight: 1.4 }}>{rule.desc}</div>
        </div>}
      </div>

      {/* Guide */}
      <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexShrink: 0 }}>
        <button onClick={() => { setGuideOpen(true); if (isMobile) onClose(); }}
          style={{ width: "100%", padding: "8px 8px", borderRadius: 6, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 11, fontWeight: 700, textAlign: "left", display: "flex", alignItems: "center", gap: 5, minHeight: 40 }}>
          <span>❓</span><span>Guide de phase</span>
        </button>
      </div>

      {/* Stratégie */}
      {strategy && <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.25)", marginBottom: 6 }}>Stratégie</div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>{strategy.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: strategy.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{strategy.name}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.32)", lineHeight: 1.3, marginTop: 1 }}>{strategy.effect}</div>
          </div>
        </div>
      </div>}

      {/* Héritages */}
      {heritages.length > 0 && <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.25)", marginBottom: 6 }}>Héritages</div>
        {heritages.map(h => (
          <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 6px", borderRadius: 5, background: `${h.color}06`, border: `1px solid ${h.color}13`, marginBottom: 3 }}>
            <span style={{ fontSize: 11 }}>{h.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: h.color, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.name}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.2)", lineHeight: 1.2 }}>{h.effect}</div>
            </div>
          </div>
        ))}
      </div>}

      {/* Combos */}
      {G.combos.length > 0 && <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.25)", marginBottom: 6 }}>Combos actifs</div>
        {G.combos.map(c => (
          <div key={c.id} style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 13 }}>{c.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: c.color, fontWeight: 700 }}>{c.name}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", lineHeight: 1.2 }}>{c.bonus}</div>
            </div>
          </div>
        ))}
      </div>}

      {/* Footer */}
      <div style={{ padding: "8px 10px", marginTop: "auto", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center" }}>
          Éthique : <span style={{ color: "#4ade80", fontFamily: "'JetBrains Mono', monospace" }}>{Math.round(G.ethique)}</span>
        </div>
        {G.corruption >= 3 && (
          <div style={{ fontSize: 11, color: "#dc2626", textAlign: "center", marginTop: 4, cursor: "pointer", padding: "4px 0" }} onClick={() => { setParallelOpen(true); if (isMobile) onClose(); }}>
            ⚠ Monde parallèle →
          </div>
        )}
      </div>
    </div>
  );

  if (!isMobile) return content;

  // Mobile : drawer depuis la droite
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", justifyContent: "flex-end" }}>
      {/* Backdrop */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} onClick={onClose} />
      <div style={{ position: "relative", zIndex: 1, height: "100%", animation: "slideRight .2s ease-out" }}>
        {content}
      </div>
    </div>,
    document.body
  );
}

// ─── GAME ARENA ───────────────────────────────────────────────

function GameArena({ phase, heritages, strategy, onPhaseComplete, onGameOver, onLessonCollected, onRestart, onChars, onCodex, onGuide, onPause, onMute, onToggleSidebar, onManual,}) {
  const G = useGame(phase, heritages, strategy);
  const [parallelOpen, setParallelOpen] = useState(false);
  const parallelTriggered = useRef(false);
  const [paused, setPaused] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const isMobile = useIsMobile();
  const [showSidebar, setShowSidebar] = useState(!isMobile);
  const [fieldExpanded, setFieldExpanded] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState(null);
  
  const pWorld = PARALLEL_WORLDS[phase.id];

  function ArenaHeader({ p, G, onManual, onChars, onCodex, onGuide, onPause, onMute, showSidebar, onToggleSidebar, isMobile }) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: isMobile ? 5 : 8,
        padding: `0 ${isMobile ? 8 : 10}px`, height: isMobile ? 52 : 46, minHeight: isMobile ? 52 : 46,
        flexShrink: 0, background: "#050710",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        zIndex: 10, boxSizing: "border-box", overflowX: "hidden",
      }}>
        {/* Phase */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
          <span style={{ fontSize: isMobile ? 16 : 18, flexShrink: 0 }}>{p.portrait}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: p.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.era}</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>Tour {G.turn}</div>
          </div>
        </div>

        <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.07)", flexShrink: 0 }} />

        {/* HP joueur */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#f87171", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}>🫀{G.playerHP}</span>
          {!isMobile && <div style={{ width: 40, height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${Math.round((G.playerHP / 40) * 100)}%`, background: p.color, borderRadius: 3, transition: "width .3s" }} /></div>}
        </div>

        <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.07)", flexShrink: 0 }} />

        {/* HP boss */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
          <span style={{ fontSize: 13, flexShrink: 0 }}>{p.bossIcon}</span>
          {!isMobile && <div style={{ width: 40, height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${Math.round((G.bossHPDisplay / G.bossHP) * 100)}%`, background: "#fb923c", borderRadius: 3, transition: "width .3s" }} /></div>}
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, minWidth: 18, textAlign: "right" }}>{G.bossHPDisplay}</span>
        </div>

        <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.07)", flexShrink: 0 }} />

        {/* Mana — condensé sur mobile */}
        <div style={{ display: "flex", gap: 2, alignItems: "center", flexShrink: 0 }}>
          {Array.from({ length: Math.min(G.maxMana, isMobile ? 5 : 8) }).map((_, i) => (
            <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: i < G.mana ? p.color : `${p.color}15`, border: `1px solid ${p.color}28`, flexShrink: 0 }} />
          ))}
          <span style={{ fontSize: 9, color: p.color, fontFamily: "'JetBrains Mono',monospace", marginLeft: 2, whiteSpace: "nowrap", flexShrink: 0 }}>{G.mana}/{G.maxMana}</span>
        </div>

        {G.corruption > 0 && (
          <>
            <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.07)", flexShrink: 0 }} />
            <div style={{ fontSize: 10, fontWeight: 700, color: "#f87171", background: "rgba(248,113,113,0.1)", padding: "1px 5px", borderRadius: 4, flexShrink: 0, whiteSpace: "nowrap" }}>☠{G.corruption}</div>
          </>
        )}

        <div style={{ flex: 1, minWidth: 4 }} />

        {/* Boutons — réduits sur mobile */}
        <div style={{ display: "flex", gap: 2, flexShrink: 0, alignItems: "center" }}>
          {!isMobile && <button onClick={onManual} style={hdrBtn(false)}>📖 Livret</button>}
          {!isMobile && onChars && <button onClick={onChars} style={hdrBtn(false)}>👤 Perso.</button>}
          {!isMobile && onCodex && <button onClick={onCodex} style={hdrBtn(true)}>📖 Codex</button>}
          {!isMobile && <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.07)", margin: "0 1px" }} />}
          <button onClick={onGuide} style={hdrBtnIcon(isMobile)} title="Guide">❓</button>
          <button onClick={onPause} style={hdrBtnIcon(isMobile)} title="Pause">⏸</button>
          <button onClick={onMute} style={hdrBtnIcon(isMobile)}>🔊</button>
          <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.07)", margin: "0 1px" }} />
          <button onClick={onToggleSidebar} style={hdrBtn(showSidebar)} title={showSidebar ? "Masquer" : "Infos"}>
            {showSidebar ? "◧" : "▣"}
          </button>
          {/* Sur mobile : bouton ⋮ pour accéder aux extras */}
          {isMobile && <MobileMenu onManual={onManual} onChars={onChars} onCodex={onCodex} />}
        </div>
      </div>
    );
  }

  // Styles boutons header
  const hdrBtn = (accent) => ({
    padding: "0 8px", height: 26, borderRadius: 6,
    background: accent ? "rgba(96,165,250,0.07)" : "rgba(255,255,255,0.04)",
    border: `1px solid ${accent ? "rgba(96,165,250,0.2)" : "rgba(255,255,255,0.1)"}`,
    color: accent ? "#60a5fa" : "rgba(255,255,255,0.5)",
    cursor: "pointer", fontSize: 11, fontWeight: 700,
    fontFamily: "'Syne', sans-serif", whiteSpace: "nowrap",
  });
  const hdrBtnIcon = (isMobile) => ({
    padding: "0 6px", height: isMobile ? 36 : 26, minWidth: isMobile ? 36 : "auto",
    borderRadius: 6, background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.5)",
    cursor: "pointer", fontSize: isMobile ? 14 : 11,
    display: "flex", alignItems: "center", justifyContent: "center",
  });

  function MobileMenu({ onManual, onChars, onCodex }) {
    const [open, setOpen] = useState(false);
    return (
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ ...hdrBtnIcon(true), fontSize: 16 }}
        >⋮</button>
        {open && (
          <div style={{
            position: "fixed", top: 56, right: 8, zIndex: 500,
            background: "#0a0d18", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10, overflow: "hidden", minWidth: 160,
            boxShadow: "0 8px 28px rgba(0,0,0,0.8)",
            animation: "fadeUp .15s ease-out",
          }}>
            {[
              ["📖 Livret", onManual],
              onChars ? ["👤 Perso.", onChars] : null,
              onCodex ? ["📖 Codex", onCodex] : null,
            ].filter(Boolean).map(([label, fn]) => (
              <button key={label} onClick={() => { fn(); setOpen(false); }} style={{
                display: "block", width: "100%", padding: "12px 16px",
                background: "none", border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)",
                color: "rgba(255,255,255,0.6)", fontSize: 13, fontWeight: 700,
                textAlign: "left", cursor: "pointer", fontFamily: "'Syne',sans-serif",
              }}>{label}</button>
            ))}
          </div>
        )}
        {open && <div style={{ position: "fixed", inset: 0, zIndex: 499 }} onClick={() => setOpen(false)} />}
      </div>
    );
  }
  
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !parallelOpen && !guideOpen) setPaused(p => !p);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [parallelOpen, guideOpen]);

  // Propager les leçons collectées au parent
  const lessonsRef = useRef(G.collectedLessons);
  useEffect(() => {
    const prev = lessonsRef.current;
    if (G.collectedLessons.length !== prev.length) {
      lessonsRef.current = G.collectedLessons;
      onLessonCollected(G.collectedLessons);
    }
  }, [G.collectedLessons.length]); // eslint-disable-line

  // Monde parallèle : déclenchement automatique à corruption >= 3, une seule fois
  useEffect(() => {
    if (G.corruption >= 3 && !parallelTriggered.current) {
      parallelTriggered.current = true;
      setParallelOpen(true);
    }
  }, [G.corruption]);

  useEffect(() => { if (G.playerDefeated) { audio.playOver(); onGameOver(); } }, [G.playerDefeated, onGameOver]);
  useEffect(() => { if (G.bossDefeated) audio.playVictory(); }, [G.bossDefeated]);

  const p = phase;
  const rule = PHASE_RULES[phase.id];
  const tNarrative = p.turnNarrative[(G.turn - 1) % p.turnNarrative.length];
  const totalDef = G.playerField.reduce((a, c) => a + c.def, 0);
  const predictedDmg = Math.max(0, (BOSS_BASE_ATK + (phase.id === 5 ? G.turn - 1 : 0)) - Math.floor(totalDef / 2));

  if (G.bossDefeated) return (
    <VictoryScreen phase={p} phaseStats={G.phaseStats} onContinue={() => onPhaseComplete(G.phaseStats)} />
  );

  // ─── Styles inline centralisés ───────────────────────────────────────────
  const css = {
    // Conteneur racine
    root: {
      display: "flex", flexDirection: "column", height: "100vh",
      background: "#07090f", fontFamily: "'Syne', sans-serif",
      position: "relative", overflow: "hidden",
    },

    // ── Header ──────────────────────────────────────────────────────────────
    header: {
      display: "flex", alignItems: "center", gap: 8,
      padding: "0 10px", height: 46, minHeight: 46, flexShrink: 0,
      background: "#050710", borderBottom: "1px solid rgba(255,255,255,0.07)",
      zIndex: 10, boxSizing: "border-box",
    },
    hSep: { width: 1, height: 20, background: "rgba(255,255,255,0.07)", flexShrink: 0 },
    hSpacer: { flex: 1, minWidth: 4 },

    // Identité de phase
    phaseBlock: { display: "flex", alignItems: "center", gap: 6, flexShrink: 1, minWidth: 0, overflow: "hidden" },
    phasePortrait: { fontSize: 18, flexShrink: 0 },
    phaseEra: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: p.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    phaseTurn: { fontSize: 10, color: "rgba(255,255,255,0.25)" },

    // HP groups
    hpGroup: { display: "flex", alignItems: "center", gap: 5, flexShrink: 0 },
    hpLabel: (color) => ({ fontSize: 11, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap" }),
    hpBar: { width: 48, height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden", flexShrink: 0 },
    hpFill: (pct, color) => ({ height: "100%", width: `${pct}%`, background: color, borderRadius: 3, transition: "width .3s" }),
    hpNum: { fontSize: 10, color: "rgba(255,255,255,0.3)", fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 22, textAlign: "right" },

    // Mana dots
    manaDot: (active) => ({
      width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
      background: active ? p.color : `${p.color}15`,
      border: `1px solid ${p.color}28`,
      transition: "background .25s",
    }),
    manaVal: { fontSize: 10, color: p.color, fontFamily: "'JetBrains Mono', monospace", marginLeft: 4, whiteSpace: "nowrap", flexShrink: 0 },

    // Corruption
    corruptBadge: {
      fontSize: 10, fontWeight: 700, color: "#f87171",
      background: "rgba(248,113,113,0.1)", padding: "2px 7px",
      borderRadius: 5, flexShrink: 0, whiteSpace: "nowrap",
    },

    // Boutons header
    hdrBtns: { display: "flex", gap: 3, flexShrink: 0, alignItems: "center" },
    hBtn: (accent) => ({
      padding: "0 8px", height: 26, borderRadius: 6,
      background: accent ? "rgba(96,165,250,0.07)" : "rgba(255,255,255,0.04)",
      border: `1px solid ${accent ? "rgba(96,165,250,0.2)" : "rgba(255,255,255,0.1)"}`,
      color: accent ? "#60a5fa" : "rgba(255,255,255,0.5)",
      cursor: "pointer", fontSize: 11, fontWeight: 700,
      fontFamily: "'Syne', sans-serif", whiteSpace: "nowrap",
    }),

    // ── Main grid ───────────────────────────────────────────────────────────
    mainGrid: { flex: 1, display: "grid", gridTemplateColumns: "1fr 180px", minHeight: 0, overflow: "hidden" },

    // ── Battle (colonne gauche) ─────────────────────────────────────────────
    battle: { display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" },

    // Ticker narratif
    ticker: { padding: "4px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", height: 28, flexShrink: 0 },
    tickerText: { fontSize: 11, color: "rgba(255,255,255,0.5)", fontStyle: "italic", fontFamily: "'DM Serif Display', serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

    // Section de terrain
    fieldSection: { padding: "8px 14px 6px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 },
    fieldLabel: { fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 },
    fieldThreat: { color: "rgba(248,100,100,0.55)", fontSize: 10 },
    fieldCombos: { color: "#4ade80", fontSize: 10 },
    cardsWrap: { display: "flex", flexWrap: "wrap", gap: 7, minHeight: 118, alignContent: "flex-start", paddingBottom: 3 },
    emptyHint: { fontSize: 11, color: "rgba(255,255,255,0.18)", fontStyle: "italic", padding: "6px 2px", alignSelf: "center" },

    // Description boss
    bossDesc: {
      padding: "7px 10px", borderRadius: 7,
      background: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.05)",
      fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.5,
      maxWidth: 240, alignSelf: "flex-start",
    },

    // ── Main (cartes en main) ───────────────────────────────────────────────
    handWrap: { borderTop: "1px solid rgba(255,255,255,0.1)", padding: "7px 10px 8px", flexShrink: 0 },
    handToolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 },
    handLabel: { fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" },
    handActions: { display: "flex", gap: 4 },
    actBtn: (variant, enabled) => {
      const base = { padding: "3px 9px", height: 26, borderRadius: 5, fontSize: 11, cursor: enabled ? "pointer" : "default", fontFamily: "'Syne', sans-serif", border: "1px solid transparent", whiteSpace: "nowrap" };
      if (variant === "end") return { ...base, fontWeight: 700, background: enabled ? `${p.color}12` : "transparent", borderColor: enabled ? `${p.color}30` : "rgba(255,255,255,0.07)", color: enabled ? p.color : "rgba(255,255,255,0.1)" };
      return { ...base, background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.07)", color: enabled ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.12)" };
    },
    handScroll: { display: "flex", gap: 7, overflowX: "auto", paddingBottom: 4, alignItems: "flex-start" },
    handEmpty: { fontSize: 11, color: "rgba(255,255,255,0.15)", fontStyle: "italic", padding: "0 6px", alignSelf: "center" },

    // ── Sidebar ─────────────────────────────────────────────────────────────
    sidebar: { borderLeft: "1px solid rgba(255,255,255,0.04)", display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden", background: "rgba(0,0,0,0.25)" },
    sbSection: { padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", flexShrink: 0 },
    sbTitle: { fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.25)", marginBottom: 6 },
    ruleCard: { padding: "6px 8px", borderRadius: 6, background: `${p.color}07`, border: `1px solid ${p.color}15` },
    ruleLabel: { fontSize: 11, fontWeight: 700, color: p.color },
    ruleDesc: { fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 2, lineHeight: 1.4 },
    guideBtn: { width: "100%", padding: "6px 8px", borderRadius: 6, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 11, fontWeight: 700, textAlign: "left", display: "flex", alignItems: "center", gap: 5 },
    stratRow: { display: "flex", alignItems: "center", gap: 7 },
    heritageItem: (h) => ({ display: "flex", alignItems: "center", gap: 5, padding: "3px 6px", borderRadius: 5, background: `${h.color}06`, border: `1px solid ${h.color}13`, marginBottom: 3 }),
    comboItem: { display: "flex", gap: 5, alignItems: "center", marginBottom: 4 },
    sbFooter: { padding: "8px 10px", marginTop: "auto", borderTop: "1px solid rgba(255,255,255,0.04)" },
    parallelLink: { fontSize: 11, color: "#dc2626", textAlign: "center", marginTop: 4, cursor: "pointer" },
  };

return (
    <div style={{ display: "flex", flexDirection: "column", height: "100dvh", background: "#07090f", fontFamily: "'Syne', sans-serif", position: "relative", overflow: "hidden" }}>

    {/* Overlays inchangés */}
    <FloatingText texts={G.floats} />
    <NarrativeBanner text={G.narrative.text} color={p.color} side={G.narrative.side} triggerKey={G.narrative.key} />
    {G.lesson && <LessonPopup card={G.lesson} onClose={() => G.setLesson(null)} />}
    {G.unisonCombo && <UnisonFlash combo={G.unisonCombo} onDone={() => G.setUnisonCombo(null)} />}
    {G.imposedNotif && <ImposedCardNotif card={G.imposedNotif} onDismiss={() => G.setImposedNotif(null)} />}
    {parallelOpen && pWorld && <ParallelWorldModal world={pWorld} onClose={() => setParallelOpen(false)} onUnlockCard={(card) => { G.addCardToHand(card); G.addFloat(`✨ ${card.name} débloquée !`, "#4ade80", "50%", "35%"); audio.playVictory?.(); }} />}
    {paused && <PauseMenu phase={p} turn={G.turn} playerHP={G.playerHP} bossHP={G.bossHPDisplay} bossHPMax={G.bossHP} onResume={() => setPaused(false)} onRestart={onRestart} onGuide={() => { setPaused(false); setGuideOpen(true); }} />}
    {guideOpen && <PhaseGuide phase={p} onClose={() => setGuideOpen(false)} />}
    {showManual && <LivretExplication onClose={() => setShowManual(false)} />}

    {/* Header */}
    <ArenaHeader
      p={p} G={G}
      onManual={() => setShowManual(true)}
      onChars={onChars} onCodex={onCodex}
      onGuide={() => setGuideOpen(true)}
      onPause={() => setPaused(true)}
      onMute={() => audio.toggleMute()}
      showSidebar={showSidebar}
      onToggleSidebar={() => setShowSidebar(s => !s)}
      isMobile={isMobile}
    />

    {/* Main grid — sidebar intégrée uniquement sur desktop */}
    <div style={{ flex: 1, display: "grid", gridTemplateColumns: (!isMobile && showSidebar) ? "1fr 180px" : "1fr", minHeight: 0, overflow: "hidden" }}>

      {/* Colonne gauche : terrain */}
      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>

        {/* Ticker */}
        <div style={{ padding: "4px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", height: 26, flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontStyle: "italic", fontFamily: "'DM Serif Display', serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tNarrative}</div>
        </div>

        {/* Terrain ennemi */}
        <div style={{ padding: isMobile ? "4px 8px" : "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
          <BossArena
            phase={p}
            bossHP={G.bossHPDisplay}
            bossHPMax={G.bossHP}
            playerHP={G.playerHP}
            playerHPMax={30}
            mana={G.mana}
            maxMana={G.maxMana}
            playerField={G.playerField}
            turn={G.turn}
            lastCardPlayed={G.lastCardPlayed}
            lastCombo={G.lastCombo}
          />
        </div>

        {/* Terrain joueur */}
        <div style={{ 
          borderBottom: "1px solid rgba(255,255,255,0.06)", 
          flexShrink: 0,
          overflow: "hidden",
          transition: "all 0.3s ease"
        }}>
          {/* Barre d'en-tête cliquable */}
          <div 
            onClick={() => setFieldExpanded(!fieldExpanded)}
            style={{
              padding: isMobile ? "5px 10px" : "6px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
              minHeight: isMobile ? 30 : 34,
              background: "rgba(0,0,0,0.2)"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                🎴 Ton champ · {G.playerField.length} carte{G.playerField.length > 1 ? 's' : ''}
              </span>
              {G.combos.length > 0 && (
                <span style={{ color: "#4ade80", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? 100 : 200 }}>
                  ✦ {G.combos.map(c => c.name).join(" · ")}
                </span>
              )}
            </div>
            <span style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.3)",
              transform: fieldExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
              marginLeft: 8,
              flexShrink: 0
            }}>
              ▼
            </span>
          </div>

          {/* Contenu des cartes - wrapper pour l'animation */}
          <div style={{
            maxHeight: fieldExpanded ? (isMobile ? 300 : 500) : 0,
            overflow: fieldExpanded ? "visible" : "hidden",
            transition: "max-height 0.3s ease"
          }}>
            {/* TERRAIN ORIGINAL INTACT */}
            <div style={{ 
              padding: isMobile ? "6px 10px 4px" : "8px 14px 6px",
              overflow: "visible",
              position: "relative",
              zIndex: 5,
              minHeight: G.playerField.length === 0 ? (isMobile ? 50 : 60) : "auto"
            }}>
              <div style={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: 6, 
                minHeight: isMobile ? 50 : 60,
                alignContent: "flex-start", 
                paddingBottom: 3,
                position: "relative",
                zIndex: 2
              }}>
                {G.playerField.map(c => <Card key={c.uid || c.id} card={c} inPlay mini />)}
                {G.playerField.length === 0 && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.18)", fontStyle: "italic", padding: "6px 2px", alignSelf: "center" }}>Joue tes cartes ici…</div>}
              </div>
            </div>
            </div>
            </div>

        {/* Main */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", padding: isMobile ? "6px 8px 10px" : "7px 10px 8px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Main · {G.hand.length}</div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => G.drawCards(1)} disabled={G.mana < 1} style={{ padding: isMobile ? "6px 10px" : "3px 9px", height: isMobile ? 36 : 26, borderRadius: 5, fontSize: 11, cursor: G.mana >= 1 ? "pointer" : "default", fontFamily: "'Syne',sans-serif", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.03)", color: G.mana >= 1 ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.12)", whiteSpace: "nowrap" }}>+ Carte</button>
              <button onClick={G.resetField} disabled={G.mana < 1} style={{ padding: isMobile ? "6px 10px" : "3px 9px", height: isMobile ? 36 : 26, borderRadius: 5, fontSize: 11, cursor: G.mana >= 1 ? "pointer" : "default", fontFamily: "'Syne',sans-serif", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.03)", color: G.mana >= 1 ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.12)", whiteSpace: "nowrap" }}>Défausser</button>
              <button onClick={G.endTurn} disabled={G.isProcessing} style={{ padding: isMobile ? "6px 10px" : "3px 10px", height: isMobile ? 36 : 26, borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: G.isProcessing ? "default" : "pointer", fontFamily: "'Syne',sans-serif", border: `1px solid ${G.isProcessing ? "rgba(255,255,255,0.07)" : p.color + "30"}`, background: G.isProcessing ? "transparent" : `${p.color}12`, color: G.isProcessing ? "rgba(255,255,255,0.1)" : p.color, whiteSpace: "nowrap" }}>Fin du tour ▶</button>
            </div>
          </div>

          {/* Scroll cartes — touch-action: pan-x */}
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, alignItems: "flex-start", touchAction: "pan-x", WebkitOverflowScrolling: "touch" }}>
            {G.hand.map((card, i) => {
              const cost = G.getCardCost(card, G.turn);
              const cardUniqueId = card.id + i;
              return (
                <Card 
                  key={cardUniqueId} 
                  card={{ ...card, cost }} 
                  onClick={G.playCard} 
                  canPlay={cost <= G.mana && !G.isProcessing}
                  isSelected={selectedCardId === cardUniqueId}
                  onSelect={() => setSelectedCardId(selectedCardId === cardUniqueId ? null : cardUniqueId)}
                />
              );
            })}
            {G.hand.length === 0 && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.15)", fontStyle: "italic", padding: "0 6px", alignSelf: "center" }}>Main vide — clique sur + Carte</div>}
          </div>
        </div>
      </div>

      {/* Sidebar desktop uniquement dans le grid */}
      {!isMobile && showSidebar && (
        <SidebarDrawer
          open={true} onClose={() => setShowSidebar(false)}
          isMobile={false} p={p} rule={rule}
          strategy={strategy} heritages={heritages}
          G={G} setGuideOpen={setGuideOpen}
          setParallelOpen={setParallelOpen}
        />
      )}
    </div>

    {/* Sidebar mobile : drawer portal */}
    {isMobile && (
      <SidebarDrawer
        open={showSidebar} onClose={() => setShowSidebar(false)}
        isMobile={true} p={p} rule={rule}
        strategy={strategy} heritages={heritages}
        G={G} setGuideOpen={setGuideOpen}
        setParallelOpen={setParallelOpen}
      />
    )}
  </div>
);
}

// ─── BOSS PORTRAITS & ÉTATS ───────────────────────────────────
// Chaque boss a 4 états visuels : calm / menacing / hurt / enraged
const BOSS_STATES = {
  calm:     { filter: "none",                                    glowColor: "rgba(255,150,50,0.15)",  label: "" },
  menacing: { filter: "saturate(1.3) hue-rotate(-10deg)",        glowColor: "rgba(255,80,0,0.25)",    label: "⚡ Concentré" },
  hurt:     { filter: "saturate(0.6) brightness(0.85)",          glowColor: "rgba(100,100,255,0.2)", label: "💢 Blessé" },
  enraged:  { filter: "saturate(2) hue-rotate(-20deg) brightness(1.2)", glowColor: "rgba(255,0,0,0.4)", label: "🔥 ENRAGÉ" },
};

// Dialogues contextuels enrichis par phase + état
const BOSS_CONTEXTUAL = {
  onPlayerCard: {
    confiance:   ["La confiance ? Un luxe que tu ne peux pas te permettre.", "Tu fais confiance aux autres. Je t'attendrai."],
    solidarite:  ["L'union fait la force. Jusqu'à ce que je la brise.", "Solidaires ? Ça ne durera pas."],
    innovation:  ["Innover ne sert à rien si le système reste le même.", "Créativité contre structure. Je parie sur la structure."],
    domination:  ["Tu utilises mes propres armes. Intéressant.", "Ah. Tu apprends vite."],
    cupidite:    ["Maintenant tu me ressembles.", "La cupidité... bienvenue dans mon monde."],
    justice:     ["La justice ? Je connais des juges.", "Les lois s'achètent. Je sais où."],
    cooperation: ["La coopération ralentit tout le monde.", "Ensemble mais lents. Seul mais rapide."],
    memoire:     ["Tu te souviens. Ça ne changera rien.", "Le passé ne paie pas de loyers."],
    default:     ["Intéressant.", "Continue. Ça m'amuse.", "Tu résistes ? Bien."],
  },
  onCombo: [
    "Un combo ! Ça ne changera pas l'issue.",
    "Impressionnant... pour du théâtre.",
    "Mes algorithmes ont déjà prévu ça.",
    "Tes alliés ne feront pas le poids.",
    "Belle synergie. Pour combien de temps encore ?",
  ],
  onTurnStart: [
    "Mon tour. Comme toujours.",
    "Le temps travaille pour moi.",
    "Chaque tour, ta résistance coûte.",
    "Tu t'épuises. Moi, non.",
    "Les intérêts s'accumulent pendant que tu dors.",
  ],
  onPlayerHurt: [
    "Voilà. C'est ça, la réalité.",
    "La douleur est pédagogique.",
    "Maintenant tu comprends ce que c'est.",
    "Le marché n'est pas sentimental.",
    "Tu ressentiras ça chaque mois. Comme tes locataires.",
  ],
  onBossHurt: [
    "Aïe. Tu as fait quelque chose de juste.",
    "Ne te réjouis pas trop vite.",
    "Je peux encaisser ça. Et toi ?",
    "Ce n'est qu'une bataille.",
    "Intéressant... vraiment.",
  ],
  onLowHP: [
    "Tu... tu commences à comprendre le système.",
    "Peut-être que j'ai sous-estimé quelque chose.",
    "Je ne suis pas invincible. Mais le système, lui, l'est.",
    "Si tu me bats... le suivant sera pire.",
  ],
  onVeryLowHP: [
    "Non. Ce n'est pas censé se passer comme ça.",
    "Tu... tu as vraiment compris quelque chose.",
    "Je perds... mais apprends bien. Le vrai combat commence après.",
  ],
};

// ─── ATTACK ANIMATION DATA ────────────────────────────────────
const ATTACK_TYPES = [
  { id: "strike",   icon: "⚡", label: "Frappe directe",    color: "#f87171", shake: "strong" },
  { id: "drain",    icon: "🩸", label: "Drainage",          color: "#e879f9", shake: "medium" },
  { id: "corrupt",  icon: "☠",  label: "Corruption",        color: "#dc2626", shake: "strong" },
  { id: "pressure", icon: "📉", label: "Pression du marché", color: "#f97316", shake: "light"  },
  { id: "suppress", icon: "🔇", label: "Suppression",       color: "#6b7280", shake: "medium" },
];

// ─── HELPER : pick random from array ─────────────────────────
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ─── BOSS PORTRAIT ────────────────────────────────────────────
function BossPortrait({ phase, bossHP, bossHPMax, state, isAttacking, lastAttack }) {
  const pct = bossHP / bossHPMax;
  const bState = BOSS_STATES[state] || BOSS_STATES.calm;

  // HP bar color
  const hpColor = pct > 0.6 ? "#4ade80" : pct > 0.3 ? "#fb923c" : "#f87171";

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      gap: 6, padding: "10px 14px",
      background: "linear-gradient(160deg, #08040f, #040210)",
      borderRadius: 12,
      border: `1.5px solid ${pct < 0.3 ? "#f87171" : pct < 0.6 ? "#fb923c" : "rgba(255,255,255,0.07)"}`,
      boxShadow: `0 0 ${pct < 0.3 ? 32 : 16}px ${bState.glowColor}`,
      transition: "box-shadow 0.5s ease, border-color 0.5s ease",
      position: "relative", overflow: "hidden",
      minWidth: 130,
    }}>
      {/* Ambient pulse quand enragé */}
      {state === "enraged" && (
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at center, rgba(255,0,0,0.08), transparent 70%)",
          animation: "bossEnragePulse 1.2s ease-in-out infinite",
          pointerEvents: "none",
        }} />
      )}

      {/* Badge état */}
      {bState.label && (
        <div style={{
          fontSize: 10, fontWeight: 700, color: "#f87171",
          background: "rgba(248,113,113,0.1)", padding: "2px 7px",
          borderRadius: 10, border: "1px solid rgba(248,113,113,0.2)",
          animation: state === "enraged" ? "bossEnragePulse 0.8s ease-in-out infinite" : "none",
        }}>
          {bState.label}
        </div>
      )}

      {/* Emoji boss — principal asset visuel */}
      <div style={{
        fontSize: 52, lineHeight: 1,
        filter: bState.filter,
        transform: isAttacking ? "scale(1.15) translateX(-6px)" : "scale(1)",
        transition: "transform 0.15s ease, filter 0.4s ease",
        animation: state === "enraged" ? "bossShakeLight 0.4s ease-in-out infinite" : "none",
        position: "relative",
        zIndex: 1,
      }}>
        {phase.bossIcon}
      </div>

      {/* Nom du boss */}
      <div style={{
        fontSize: 11, fontWeight: 800, color: "#fff",
        fontFamily: "'Syne', sans-serif", textAlign: "center",
        lineHeight: 1.2, maxWidth: 110,
      }}>
        {phase.bossName}
      </div>

      {/* HP bar */}
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontFamily: "'Syne',sans-serif", textTransform: "uppercase", letterSpacing: "0.1em" }}>HP</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: hpColor, fontFamily: "'JetBrains Mono',monospace" }}>{bossHP}</span>
        </div>
        <div style={{ height: 6, background: "rgba(0,0,0,0.6)", borderRadius: 3, overflow: "hidden", position: "relative" }}>
          <div style={{
            width: `${pct * 100}%`, height: "100%",
            background: `linear-gradient(90deg, ${hpColor}, ${hpColor}cc)`,
            borderRadius: 3,
            transition: "width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            position: "relative",
          }}>
            {/* Shimmer */}
            <div style={{
              position: "absolute", top: 0, left: "-100%", width: "100%", height: "100%",
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
              animation: "hpShimmer 2s ease-in-out infinite",
            }} />
          </div>
        </div>
        {/* HP segments */}
        <div style={{ display: "flex", gap: 2 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 2, borderRadius: 1,
              background: (i / 5) < pct ? `${hpColor}60` : "rgba(255,255,255,0.03)",
              transition: "background 0.4s ease",
            }} />
          ))}
        </div>
      </div>

      {/* Dernier type d'attaque */}
      {lastAttack && (
        <div style={{
          fontSize: 10, color: lastAttack.color,
          background: `${lastAttack.color}10`,
          border: `1px solid ${lastAttack.color}25`,
          padding: "2px 7px", borderRadius: 6,
          fontFamily: "'Syne',sans-serif", fontWeight: 700,
          animation: "fadeUp 0.3s ease-out",
        }}>
          {lastAttack.icon} {lastAttack.label}
        </div>
      )}
    </div>
  );
}

// ─── ATTACK BEAM (animation de coup) ─────────────────────────
function AttackBeam({ active, color, direction = "boss-to-player" }) {
  if (!active) return null;
  const fromRight = direction === "boss-to-player";
  return (
    <div style={{
      position: "absolute",
      top: "50%", transform: "translateY(-50%)",
      left: fromRight ? "auto" : 0,
      right: fromRight ? 0 : "auto",
      width: "60%", height: 3,
      background: `linear-gradient(${fromRight ? "to left" : "to right"}, ${color}, transparent)`,
      borderRadius: 2,
      animation: "beamFlash 0.35s ease-out forwards",
      pointerEvents: "none", zIndex: 50,
    }} />
  );
}

// ─── IMPACT FLASH (flash sur le récepteur) ───────────────────
function ImpactFlash({ active, color }) {
  if (!active) return null;
  return (
    <div style={{
      position: "absolute", inset: 0,
      background: `radial-gradient(ellipse at center, ${color}40, transparent 60%)`,
      borderRadius: 12,
      animation: "impactFlash 0.4s ease-out forwards",
      pointerEvents: "none", zIndex: 40,
    }} />
  );
}

// ─── COMBAT LOG ───────────────────────────────────────────────
function CombatLog({ entries }) {
  const logRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasNewEntries, setHasNewEntries] = useState(false);
  const prevEntriesLength = useRef(0);

  // Détecte les nouvelles entrées
  useEffect(() => {
    if (entries.length > prevEntriesLength.current) {
      setHasNewEntries(true);
      // Reset l'indicateur après 3 secondes
      const timer = setTimeout(() => setHasNewEntries(false), 3000);
      prevEntriesLength.current = entries.length;
      return () => clearTimeout(timer);
    }
  }, [entries.length]);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [entries, isExpanded]);

  const lastEntry = entries[entries.length - 1];
  const maxHeight = isExpanded ? 200 : 40; // Hauteur réduite quand replié

  return (
    <div style={{
      background: "rgba(0,0,0,0.3)",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.04)",
      transition: "all 0.3s ease",
      overflow: "hidden"
    }}>
      {/* Barre toujours visible avec dernière entrée */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: "6px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          transition: "background 0.2s",
          background: hasNewEntries ? "rgba(255,255,255,0.03)" : "transparent"
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
        onMouseLeave={(e) => e.currentTarget.style.background = hasNewEntries ? "rgba(255,255,255,0.03)" : "transparent"}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {lastEntry ? (
            <div style={{
              display: "flex", gap: 6, alignItems: "center",
              fontSize: 11, fontFamily: "'DM Serif Display', serif",
              overflow: "hidden"
            }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{lastEntry.icon}</span>
              <span style={{ 
                color: lastEntry.color || "rgba(255,255,255,0.5)", 
                whiteSpace: "nowrap", 
                overflow: "hidden", 
                textOverflow: "ellipsis",
                flex: 1
              }}>
                {lastEntry.text}
              </span>
            </div>
          ) : (
            <div style={{ color: "rgba(255,255,255,0.15)", fontStyle: "italic", fontSize: 11, fontFamily: "'DM Serif Display', serif" }}>
              Le combat commence…
            </div>
          )}
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8, flexShrink: 0 }}>
          {entries.length > 1 && (
            <span style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.3)",
              fontWeight: 600,
              minWidth: 18,
              textAlign: "center"
            }}>
              {entries.length}
            </span>
          )}
          <span style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.4)",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s"
          }}>
            ▼
          </span>
          {hasNewEntries && (
            <span style={{
              width: 6, height: 6,
              borderRadius: "50%",
              background: "#4ade80",
              animation: "pulse 1s infinite"
            }} />
          )}
        </div>
      </div>

      {/* Zone expandable avec le log complet */}
      <div style={{
        maxHeight: isExpanded ? maxHeight : 0,
        overflowY: "auto",
        transition: "max-height 0.3s ease",
        borderTop: isExpanded ? "1px solid rgba(255,255,255,0.04)" : "none"
      }}>
        <div ref={logRef} style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          padding: "6px 8px",
          fontSize: 11,
          fontFamily: "'DM Serif Display', serif",
        }}>
          {entries.slice(0, -1).map((e, i) => ( // Toutes sauf la dernière (déjà visible)
            <div key={i} style={{
              display: "flex", gap: 6, alignItems: "flex-start",
              opacity: 0.4 + (i / entries.length) * 0.6,
            }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{e.icon}</span>
              <span style={{ color: e.color || "rgba(255,255,255,0.5)", lineHeight: 1.4 }}>{e.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── BOSS SPEECH BUBBLE ───────────────────────────────────────
function BossSpeech({ text, color, triggerKey }) {
  const [visible, setVisible] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!text || !triggerKey) return;
    setMsg(text);
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 4500);
    return () => clearTimeout(t);
  }, [triggerKey]);

  if (!visible) return null;

  return (
    <div style={{
      position: "relative",
      padding: "10px 13px",
      borderRadius: 10,
      background: "linear-gradient(135deg, rgba(15,0,5,0.97), rgba(8,0,12,0.97))",
      border: `1px solid ${color}30`,
      boxShadow: `0 4px 20px rgba(0,0,0,0.8), 0 0 12px ${color}15`,
      animation: "speechIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      maxWidth: 280,
    }}>
      {/* Flèche */}
      <div style={{
        position: "absolute", left: 16, top: -7,
        width: 12, height: 8,
        background: `linear-gradient(135deg, rgba(15,0,5,0.97), rgba(8,0,12,0.97))`,
        border: `1px solid ${color}30`,
        clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
        borderBottom: "none",
      }} />

      {/* Texte typo */}
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", lineHeight: 1.6, fontFamily: "'DM Serif Display', serif", fontStyle: "italic" }}>
        « {msg} »
      </div>

      {/* Label boss */}
      <div style={{ fontSize: 9, color: `${color}70`, fontFamily: "'Syne', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginTop: 5 }}>
        — L'Adversaire
      </div>
    </div>
  );
}

// ─── PLAYER STATUS BAR ────────────────────────────────────────
function PlayerStatus({ playerHP, maxHP, mana, maxMana, phase, isHurt, playerField }) {
  const pct = playerHP / maxHP;
  const hpColor = pct > 0.6 ? "#4ade80" : pct > 0.3 ? "#fb923c" : "#f87171";
  const totalDef = playerField.reduce((a, c) => a + (c.def || 0), 0);
  const totalAtk = playerField.reduce((a, c) => a + (c.atk || 0), 0);

  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 8,
      padding: "10px 14px",
      background: "linear-gradient(160deg, #040d08, #020808)",
      borderRadius: 12,
      border: `1.5px solid ${pct < 0.3 ? "#f87171" : "rgba(255,255,255,0.07)"}`,
      boxShadow: isHurt ? "0 0 24px rgba(248,113,113,0.3)" : "none",
      transition: "box-shadow 0.4s ease",
      position: "relative", overflow: "hidden",
      minWidth: 130,
    }}>
      {isHurt && (
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at center, rgba(248,113,113,0.12), transparent)",
          animation: "impactFlash 0.5s ease-out forwards",
          pointerEvents: "none",
        }} />
      )}

      {/* Famille */}
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ fontSize: 22 }}>{phase.portrait}</span>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: phase.color, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'Syne',sans-serif" }}>{phase.familia}</div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>{phase.era}</div>
        </div>
      </div>

      {/* HP bar joueur */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'Syne',sans-serif" }}>PV</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: hpColor, fontFamily: "'JetBrains Mono',monospace" }}>{playerHP} / {maxHP}</span>
        </div>
        <div style={{ height: 6, background: "rgba(0,0,0,0.6)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{
            width: `${pct * 100}%`, height: "100%",
            background: `linear-gradient(90deg, ${hpColor}, ${hpColor}99)`,
            borderRadius: 3,
            transition: "width 0.5s ease",
          }} />
        </div>
      </div>

      {/* Mana */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'Syne',sans-serif" }}>Mana</span>
          <span style={{ fontSize: 10, color: phase.color, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{mana}/{maxMana}</span>
        </div>
        <div style={{ display: "flex", gap: 3 }}>
          {Array.from({ length: maxMana }).map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 5, borderRadius: 2,
              background: i < mana ? phase.color : `${phase.color}15`,
              border: `1px solid ${phase.color}28`,
              transition: "background 0.25s ease",
            }} />
          ))}
        </div>
      </div>

      {/* Stats de terrain */}
      {playerField.length > 0 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: 11, color: "#f87171" }}>⚔</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#f87171", fontFamily: "'JetBrains Mono',monospace" }}>{totalAtk}</span>
          </div>
          <div style={{ width: 1, background: "rgba(255,255,255,0.06)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: 11, color: "#60a5fa" }}>🛡</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#60a5fa", fontFamily: "'JetBrains Mono',monospace" }}>{totalDef}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BOSS ARENA — COMPOSANT PRINCIPAL ────────────────────────
export function BossArena({
  phase,
  bossHP,
  bossHPMax,
  playerHP,
  playerHPMax = 40,
  mana,
  maxMana,
  playerField = [],
  turn,
  lastCardPlayed = null,
  lastCombo = null,
  bossJustAttacked = false,
  bossAttackDmg = 0,
  playerJustPlayed = false,
}) {
  const [bossState, setBossState] = useState("calm");
  const [isAttacking, setIsAttacking] = useState(false);
  const [playerIsHurt, setPlayerIsHurt] = useState(false);
  const [bossIsHurt, setBossIsHurt] = useState(false);
  const [showBeam, setShowBeam] = useState(false);
  const [showImpact, setShowImpact] = useState({ boss: false, player: false });
  const [speech, setSpeech] = useState({ text: "", key: 0 });
  const [lastAttackType, setLastAttackType] = useState(null);
  const [combatLog, setCombatLog] = useState([]);
  const prevBossHP = useRef(bossHPMax);
  const prevPlayerHP = useRef(playerHPMax);

  // ── Ajouter une entrée au log ────────────────────────────────
  const addLog = useCallback((icon, text, color) => {
    setCombatLog(prev => [...prev.slice(-12), { icon, text, color }]);
  }, []);

  // ── Parler (boss dialogue) ───────────────────────────────────
  const bossSay = useCallback((text) => {
    setSpeech({ text, key: Date.now() });
  }, []);

  // ── Calcul de l'état boss selon HP ──────────────────────────
  useEffect(() => {
    const pct = bossHP / bossHPMax;
    if (pct < 0.25)      setBossState("enraged");
    else if (pct < 0.5)  setBossState("hurt");
    else if (pct < 0.75) setBossState("menacing");
    else                 setBossState("calm");
  }, [bossHP, bossHPMax]);

  // ── Réaction boss quand il reçoit des dégâts ────────────────
  useEffect(() => {
    const dmg = prevBossHP.current - bossHP;
    if (dmg > 0) {
      prevBossHP.current = bossHP;
      setBossIsHurt(true);
      setShowImpact(p => ({ ...p, boss: true }));
      setTimeout(() => { setBossIsHurt(false); setShowImpact(p => ({ ...p, boss: false })); }, 500);

      const pct = bossHP / bossHPMax;
      addLog("⚔", `Tu infliges ${dmg} dégâts au boss !`, "#4ade80");

      if (pct < 0.25 && bossState !== "enraged") {
        bossSay(pick(BOSS_CONTEXTUAL.onVeryLowHP));
      } else if (pct < 0.5) {
        bossSay(pick(BOSS_CONTEXTUAL.onLowHP));
      } else {
        bossSay(pick(BOSS_CONTEXTUAL.onBossHurt));
      }
    }
  }, [bossHP]); // eslint-disable-line

  // ── Réaction boss quand le joueur reçoit des dégâts ─────────
  useEffect(() => {
    const dmg = prevPlayerHP.current - playerHP;
    if (dmg > 0) {
      prevPlayerHP.current = playerHP;
      setPlayerIsHurt(true);
      setShowImpact(p => ({ ...p, player: true }));
      setIsAttacking(true);

      const attack = pick(ATTACK_TYPES);
      setLastAttackType(attack);

      // Beam puis impact
      setShowBeam(true);
      setTimeout(() => setShowBeam(false), 350);
      setTimeout(() => {
        setIsAttacking(false);
        setPlayerIsHurt(false);
        setShowImpact(p => ({ ...p, player: false }));
      }, 600);

      addLog(attack.icon, `${phase.bossName} attaque — ${dmg} dégâts (${attack.label})`, attack.color);

      if (Math.random() > 0.4) bossSay(pick(BOSS_CONTEXTUAL.onPlayerHurt));
    }
  }, [playerHP]); // eslint-disable-line

  // ── Réaction quand le joueur joue une carte ──────────────────
  useEffect(() => {
    if (!lastCardPlayed) return;
    const { behavior, name } = lastCardPlayed;
    addLog("🃏", `Tu joues ${name} (${behavior})`, "#a78bfa");
    const lines = BOSS_CONTEXTUAL.onPlayerCard[behavior] || BOSS_CONTEXTUAL.onPlayerCard.default;
    if (Math.random() > 0.45) bossSay(pick(lines));
  }, [lastCardPlayed]); // eslint-disable-line

  // ── Réaction aux combos ──────────────────────────────────────
  useEffect(() => {
    if (!lastCombo) return;
    addLog("✦", `COMBO — ${lastCombo.name} ! ${lastCombo.bonus}`, lastCombo.color);
    bossSay(pick(BOSS_CONTEXTUAL.onCombo));
  }, [lastCombo]); // eslint-disable-line

  // ── Narration de tour ─────────────────────────────────────────
  useEffect(() => {
    if (turn <= 1) return;
    if (Math.random() > 0.55) bossSay(pick(BOSS_CONTEXTUAL.onTurnStart));
    addLog("⏱", `Tour ${turn} commence.`, "rgba(255,255,255,0.25)");
  }, [turn]); // eslint-disable-line

  const bossPct = bossHP / bossHPMax;
  const bossColor = bossPct > 0.6 ? "#fb923c" : bossPct > 0.3 ? "#f87171" : "#dc2626";

  return (
    <>
      {/* ── CSS injecté ── */}
      <style>{`
        @keyframes bossEnragePulse {
          0%, 100% { opacity: 0.6; }
          50%       { opacity: 1; }
        }
        @keyframes bossShakeLight {
          0%, 100% { transform: translateX(0); }
          25%       { transform: translateX(-2px); }
          75%       { transform: translateX(2px); }
        }
        @keyframes hpShimmer {
          0%   { left: -100%; }
          100% { left: 100%; }
        }
        @keyframes beamFlash {
          0%   { opacity: 1; transform: scaleX(1) translateY(-50%); }
          70%  { opacity: 0.6; transform: scaleX(1.05) translateY(-50%); }
          100% { opacity: 0; transform: scaleX(0.8) translateY(-50%); }
        }
        @keyframes impactFlash {
          0%   { opacity: 1; }
          60%  { opacity: 0.5; }
          100% { opacity: 0; }
        }
        @keyframes speechIn {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.05); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatUp {
          0%   { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-40px); }
        }
      `}</style>

      <div style={{
        display: "flex", flexDirection: "column", gap: 10,
        padding: "10px 14px",
        background: "linear-gradient(160deg, #06080f, #040610)",
        borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.05)",
        position: "relative",
        overflow: "hidden",
      }}>

        {/* Ambiance de fond selon état boss */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: bossState === "enraged"
            ? "radial-gradient(ellipse at 80% 30%, rgba(220,38,38,0.06), transparent 60%)"
            : bossState === "hurt"
            ? "radial-gradient(ellipse at 80% 30%, rgba(96,165,250,0.04), transparent 60%)"
            : "radial-gradient(ellipse at 80% 30%, rgba(251,146,60,0.03), transparent 60%)",
          transition: "background 1s ease",
        }} />

        {/* ── Titre de section ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <div style={{ height: 1, flex: 1, background: "rgba(255,255,255,0.04)" }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.2em", fontFamily: "'Syne',sans-serif" }}>⚔ Duel ⚔</span>
          <div style={{ height: 1, flex: 1, background: "rgba(255,255,255,0.04)" }} />
        </div>

        {/* ── Portraits face-à-face ── */}
        <div style={{ display: "flex", alignItems: "stretch", gap: 0, position: "relative" }}>

          {/* Portrait joueur */}
          <div style={{ flex: 1, position: "relative" }}>
            <PlayerStatus
              playerHP={playerHP} maxHP={playerHPMax}
              mana={mana} maxMana={maxMana}
              phase={phase} isHurt={playerIsHurt}
              playerField={playerField}
            />
            {showImpact.player && <ImpactFlash active color="#f87171" />}
          </div>

          {/* Zone centrale — VS + beam + tour */}
          <div style={{
            width: 60, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 4,
            position: "relative", flexShrink: 0,
            zIndex: 2,
          }}>
            <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(255,255,255,0.12)", fontFamily: "'Syne',sans-serif", letterSpacing: "0.08em" }}>VS</div>
            <div style={{
              fontSize: 9, color: "rgba(255,255,255,0.2)",
              fontFamily: "'JetBrains Mono',monospace", fontWeight: 700,
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.05)",
              padding: "2px 6px", borderRadius: 4,
            }}>T{turn}</div>

            {/* Beam d'attaque — boss vers joueur */}
            {showBeam && (
              <div style={{
                position: "absolute", top: "50%", left: -30, right: -30,
                height: 3,
                background: `linear-gradient(to left, ${bossColor}, transparent)`,
                transform: "translateY(-50%)",
                animation: "beamFlash 0.35s ease-out forwards",
                borderRadius: 2, zIndex: 10,
              }} />
            )}
          </div>

          {/* Portrait boss */}
          <div style={{ flex: 1, position: "relative" }}>
            <BossPortrait
              phase={phase}
              bossHP={bossHP} bossHPMax={bossHPMax}
              state={bossState}
              isAttacking={isAttacking}
              lastAttack={lastAttackType}
            />
            {showImpact.boss && <ImpactFlash active color="#4ade80" />}
          </div>
        </div>

        {/* ── Bulle de dialogue du boss ── */}
        <BossSpeech
          text={speech.text}
          color={bossColor}
          triggerKey={speech.key}
        />

        {/* ── Combat Log ── */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5, fontFamily: "'Syne',sans-serif" }}>
            Journal de combat
          </div>
          <CombatLog entries={combatLog} />
        </div>

        {/* ── Indicateur menace ── */}
        {bossState === "enraged" && (
          <div style={{
            padding: "7px 10px", borderRadius: 8,
            background: "rgba(220,38,38,0.06)",
            border: "1px solid rgba(220,38,38,0.2)",
            animation: "fadeUp 0.3s ease-out",
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#f87171", fontFamily: "'Syne',sans-serif", textAlign: "center" }}>
              🔥 BOSS ENRAGÉ — Dégâts augmentés · Attaque à chaque tour
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────
export default function PesacoreV5() {
  const { user, isAuthenticated } = useAuth();
  const [stage, setStage] = useState("title");
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [allHeritages, setAllHeritages] = useState([]);
  const [phaseHeritages, setPhaseHeritages] = useState([]);
  const [pendingHeritages, setPendingHeritages] = useState([]);
  const [pendingPivot, setPendingPivot] = useState(null);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [profileVotes, setProfileVotes] = useState([]);
  const [globalStats, setGlobalStats] = useState({ combosUsed: 0, lessons: 0 });
  const [globalLessons, setGlobalLessons] = useState([]);
  const [unlockedPivots, setUnlockedPivots] = useState([]);
  const [codexOpen, setCodexOpen] = useState(false);
  const [charsOpen, setCharsOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialDone, setTutorialDone] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const currentPhase = PHASES[phaseIdx];
  const isSuperAdmin = Boolean(
    user?.role === 'superadmin' || user?.isSuperAdmin === true
  );

  const userPesas = typeof user?.pesas === 'string' 
    ? parseInt(user?.pesas, 10) 
    : (user?.pesas ?? 0);

  const hasEnoughPesas = userPesas >= 2200;
  

  useEffect(() => {
    if (isSuperAdmin) {
      if (stage === "blocked") {
        setStage("title");
      }
      return;
    }

    // Utilisateur normal
    if (!user || !hasEnoughPesas) {
      setStage("blocked");
    }
  }, [isSuperAdmin, user, hasEnoughPesas, stage]);

  useEffect(() => {
    return () => audio.dispose();
  }, []);

  function startPhase(idx) {
    const phase = PHASES[idx];
    const active = allHeritages.filter(h => !h.to || h.to === phase.id);
    setPhaseHeritages(active);
    setPhaseIdx(idx);
    setStage("cinematic");
  }

  function handleCinematicDone() { setStage("strategy"); }

  function handleStrategyChosen(strat) {
    setCurrentStrategy(strat);
    if (strat.profile) setProfileVotes(v => [...v, strat.profile]);
    setStage("battle");
  }

  function handlePhaseComplete(phaseStats) {
    const computed = computeHeritage(currentPhase.id, phaseStats);
    const tagged = computed.map(h => ({ ...h, to: currentPhase.id + 1 }));
    setPendingHeritages(tagged);
    setAllHeritages(prev => [...prev, ...tagged]);
    setGlobalStats(s => ({ combosUsed: s.combosUsed + phaseStats.combosUsed, lessons: s.lessons + (phaseStats.memoire || 0) }));
    const pivot = PIVOTS.find(p => p.after === currentPhase.id);
    if (pivot) { setUnlockedPivots(u => [...u, currentPhase.id]); setPendingPivot(pivot); }
    if (tagged.length > 0) setStage("heritage");
    else if (pivot) setStage("pivot");
    else advancePhase();
  }

  function handleHeritageAck() { if (pendingPivot) setStage("pivot"); else advancePhase(); }
  function handlePivotDone() { setPendingPivot(null); advancePhase(); }
  function advancePhase() { const next = phaseIdx + 1; if (next >= PHASES.length) setStage("final"); else startPhase(next); }
  function handleGameOver() { setStage("gameover"); }

  function handleRestart() {
    setStage("title"); setPhaseIdx(0); setAllHeritages([]); setPhaseHeritages([]);
    setPendingHeritages([]); setPendingPivot(null); setCurrentStrategy(null);
    setProfileVotes([]); setGlobalStats({ combosUsed: 0, lessons: 0 });
    setGlobalLessons([]); setUnlockedPivots([]);
  }

  const handleLessonCollected = useCallback((lessons) => {
    setGlobalLessons(lessons);
  }, []);

  if (stage === "title") return (
    <>
      <style>{GLOBAL_CSS}</style>
      <div style={{position:"fixed",inset:0,background:"#04060d",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne',sans-serif"}}>
        <div style={{textAlign:"center",maxWidth:500,padding:"0 24px"}}>
          <div style={{display:"flex",justifyContent:"center",gap:8,marginBottom:22,fontSize:28}}>
            {["🐻","🐝","🦉","🐺","🐿️","🦊","🦢"].map((e, i) => (
              <span key={i} style={{animation: `float ${3 + i * 0.3}s ease-in-out infinite, pulseLivret ${3 + i * 0.3}s ease-in-out infinite`,animationDelay:`${i*0.2}s`,display:"inline-block",opacity:0.7+(i*0.04)}}>{e}</span>
            ))}
          </div>
          <div style={{fontSize:11,letterSpacing:"0.45em",color:"rgba(255,255,255,0.40)",marginBottom:8,textTransform:"uppercase"}}>Apprentissage × Éducation Économique</div>
          <h1 style={{fontSize:36,fontWeight:800,color:"#fff",marginBottom:4,letterSpacing:"-0.02em"}}>PESACORE Family</h1>
          <div style={{fontSize:14,color:"transparent",marginBottom:5,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>L'Odyssée Économique</div>
          
          <div style={{display:"flex",gap:6,justifyContent:"center",flexWrap:"wrap",marginBottom:24}}>
            {[["⌛","Sept Epoques","#4ade80"],["🔗","Effet Domino","#4ade80"],["🌑","Monde Parallèle","#dc2626"],["⛓️","Cartes imposées","#9ca3af"],["⏸","Pause","#60a5fa"],["❓","Guide","#fbbf24"]].map(([icon,label,color]) => (
              <div key={label} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 9px",borderRadius:10,background:`${color}09`,border:`1px solid ${color}20`,fontSize:11,color:color,fontWeight:700}}>{icon} {label}</div>
            ))}
            {/* Nouveau bouton Livret */}
            <button
              onClick={() => setShowManual(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                borderRadius: 10,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(165, 180, 252, 0.25)",
                fontSize: 11,
                color: "#e0e4ff",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Syne',sans-serif",
                transition: "all 0.25s",
                boxShadow: "0 0 15px rgba(165,180,252,0.15)",
                //animation: "pulseLivret 2.8s ease-in-out infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(165,180,252,0.12)";
                e.currentTarget.style.borderColor = "#a5b4fc";
                e.currentTarget.style.boxShadow = "0 0 28px rgba(165,180,252,0.5)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                e.currentTarget.style.borderColor = "rgba(165, 180, 252, 0.25)";
                e.currentTarget.style.boxShadow = "0 0 15px rgba(165,180,252,0.15)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              📖 Livret
            </button>
          </div>
          <div style={{padding:"12px 16px",borderRadius:10,background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",marginBottom:22,fontSize:11,color:"rgba(255,255,255,0.35)",lineHeight:1.8,fontFamily:"'DM Serif Display',serif",fontStyle:"italic"}}>Ce que tu décides en Préhistoire se répercute jusqu'à la Mondialisation. Tes choix ne sont pas libres — mais tu peux comprendre pourquoi.</div>
          <button
            onClick={() => {
              audio.init();
              if (!tutorialDone) { setShowTutorial(true); setTutorialDone(true); }
              else startPhase(0);
            }}
            style={{padding:"12px 30px",borderRadius:9,background:"linear-gradient(135deg,#4ade80,#22d3ee)",border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Syne',sans-serif",boxShadow:"0 8px 28px rgba(74,222,128,0.2)",marginBottom:10}}
          >Commencer l'Odyssée </button>


          <div style={{fontSize:11,color:"rgba(255,255,255,0.1)"}}>Accessible dès 15 ans · 45–90 min · 7 phases</div>
        </div>
        {showTutorial && <TutorialOverlay onDone={() => { setShowTutorial(false); startPhase(0); }} />}
      </div>
      {showManual && <LivretExplication onClose={() => setShowManual(false)} />}
    </>
  );
  if (stage === "blocked" && !isSuperAdmin) {
    return (
      <>
        <style>{GLOBAL_CSS}</style>
        <div style={{
          position: "fixed",
          inset: 0,
          background: "#04060d",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Syne', sans-serif",
          color: "#fff"
        }}>
          <div style={{ textAlign: "center", maxWidth: 460, padding: "0 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            
            <h2 style={{ fontSize: 28, marginBottom: 12 }}>
              Accès restreint
            </h2>             
                <p style={{ color: "#aaa", marginBottom: 24, lineHeight: 1.6 }}>
                  {user 
                    ? `Nombre de pesas insuffisant (${userPesas} / 2200)` 
                    : "Tu dois être connecté pour lancer l'Odyssée."}
                </p>
                <button
                  onClick={() => {window.location.href = "/centre-ajustement"}}
                  style={{
                    padding: "14px 32px",
                    fontSize: 15,
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #4ade80, #22d3ee)",
                    border: "none",
                    borderRadius: 10,
                    color: "#fff",
                    cursor: "pointer"
                  }}
                >
                  {user ? "Obtenir des pesas" : "Se connecter"}
                </button>             
          </div>
        </div>
      </>
    );
  }
  if (stage === "cinematic") return (
    <><style>{GLOBAL_CSS}</style><Cinematic phase={currentPhase} heritages={phaseHeritages} onStart={handleCinematicDone} /></>
  );
  if (stage === "strategy") return (
    <><style>{GLOBAL_CSS}</style><StrategyScreen phase={currentPhase} heritages={phaseHeritages} onChoose={handleStrategyChosen} /></>
  );
  if (stage === "heritage") return (
    <><style>{GLOBAL_CSS}</style><HeritageReveal heritages={pendingHeritages} phaseName={currentPhase.era} onContinue={handleHeritageAck} /></>
  );
  if (stage === "pivot") return (
    <><style>{GLOBAL_CSS}</style><PivotScreen pivot={pendingPivot} onContinue={handlePivotDone} /></>
  );
  if (stage === "gameover") return (
    <><style>{GLOBAL_CSS}</style><GameOverScreen phase={currentPhase} onRestart={handleRestart} /></>
  );
  if (stage === "final") return (
    <><style>{GLOBAL_CSS}</style><FinalScreen allHeritages={allHeritages} profileVotes={profileVotes} stats={globalStats} onRestart={handleRestart} /></>
  );
  if (stage === "battle") return (
    <div style={{
      position:"relative",
      height:"100dvh",
      display:"flex",
      flexDirection:"column",
      overflow:"hidden"      // Cache l'overflow du conteneur principal
    }}>
      <style>{GLOBAL_CSS}</style>
      <GameArena
        phase={currentPhase}
        heritages={phaseHeritages}
        strategy={currentStrategy}
        onPhaseComplete={handlePhaseComplete}
        onGameOver={handleGameOver}
        onLessonCollected={handleLessonCollected}
        onRestart={handleRestart}
        onChars={() => setCharsOpen(true)}
        onCodex={() => setCodexOpen(true)}
        style={{flex:1, overflowY:"auto"}}
      />
      {codexOpen && <Codex unlockedPivots={unlockedPivots} lessons={globalLessons} onClose={() => setCodexOpen(false)} />}
      {charsOpen && <CharactersModal phaseIdx={phaseIdx} onClose={() => setCharsOpen(false)} />}
      {showTutorial && <TutorialOverlay onDone={() => setShowTutorial(false)} />}
    </div>
  );

  return null;
}